import fittingsData from '../fittingsData';
import {
  generateConnectionWithCaps,
  getShaibaKeyForFitting,
} from './connectionLogic';
import {
  generatePairedCircuitsFull,
  generateCircuits,
} from './circuitGenerationLogic';
import {
  facingStickFields,
  getFittingConnectionDiameter,
  getHoleDiameterForPipe,
  flangePipeInsertion,
  getInsertionDepth,
  nakShaibaPipeMeasure,
  visibleSpecialStubLength,
  endFittingAutoLength,
} from './collectorUtils';
import {
  generateCenterFittings,
  generateCenterFittingBalloons,
  generateCenterFittingDimensions,
} from './centerFittingsLogic';
import {
  generateCap,
  generateReducer,
  getCapLength,
} from './capsLogic';
import {
  generateDistributionCollectorDimensions,
} from './dimensionLinesLogic';
import {
  getCapRequirementForFitting,
  extractDnFromFlangeKey,
  extractSizeFromNakGaikaKey,
  extractSizeFromShaibaNarRezba,
  getPipeDiameterForDn,
  getPipeDiameterForNakGaika,
  getPipeDiameterForShaibaNarRezba,
  resolveNippelMuftaFitting,
  resolveSideConnectionOutletDimension,
} from './collectorUtils';
import { DEFAULT_SQUARE_PIPE_COMBOS } from '../pipeCatalog';
import {
  determineThickness,
  getMinThickness,
  determineCoating,
  getActualCoating,
} from './pipeThicknessUtils';

/**
 * Вычисляет реальную позицию подключения с учетом направления отсчета
 */
const getConnectionPosition = (offsetFromTop, offsetDirection, actualMainPipeHeight, topBortshaibaLength, topStraightCapLength, bottomBortshaibaLength, bottomStraightCapLength) => {
  if ((offsetDirection || 'top') === 'bottom') {
    // Отсчет снизу - пересчитываем в отсчет от верха
    // Общая высота = actualMainPipeHeight + верхние элементы + нижние элементы
    // Позиция от верха = общая высота - отступ от низа
    const totalHeight = actualMainPipeHeight + topBortshaibaLength + topStraightCapLength + bottomBortshaibaLength + bottomStraightCapLength;
    const result = totalHeight - offsetFromTop;
    return result;
  }
  // Отсчет сверху - как есть
  return offsetFromTop;
};

/**
 * Получает вылет фитинга с учетом дефолтных значений
 * @param {string} fittingId - ID фитинга
 * @param {number} customLength - Пользовательская длина
 * @param {string} shape - Форма коллектора ('round' или 'square')
 */
const getFittingLength = (fittingId, customLength, shape = 'round') => {
  if (customLength !== null && customLength !== undefined) {
    return customLength;
  }
  
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return 100;
  
  const isFlange = fittingId.startsWith('Flanec_');
  const isNakGaika = fittingId.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
  
  if (isFlange) {
    return 100;
  } else if (isNakGaika || isShaibaNarRezba) {
    // Для накидных гаек и шайб с резьбой: 58мм (квадратные) / 40мм (круглые)
    return shape === 'square' ? 58 : 40;
  }
  
  return fittingData.length;
};

/**
 * Создает объект pipeSpec с правильным покрытием
 * Если для указанного диаметра и толщины нет трубы с предпочитаемым покрытием,
 * автоматически использует доступное покрытие
 * @param {number} diameter - Диаметр трубы
 * @param {string} thickness - Толщина трубы (например, 'x2')
 * @param {string} preferredCoating - Предпочитаемое покрытие ('Матовый' или 'Зеркало')
 * @returns {object} - Объект pipeSpec с правильным покрытием
 */
const createPipeSpec = (diameter, thickness, preferredCoating) => {
  const actualCoating = getActualCoating(diameter, thickness, preferredCoating);
  return {
    shape: 'round',
    diameter: diameter,
    thickness: thickness,
    coating: actualCoating,
  };
};

/**
 * Генерация коллектора с гидравлическим разделителем
 * Комбинирует ПОЛНУЮ функциональность HydraulicSeparatorEditor и DistributionCollectorEditor
 */
export function generateCollectorWithSeparator(params, effectiveSupplyFirstCircuitOffset = null, effectiveReturnFirstCircuitOffset = null) {
  // При отражении системы БЕЗ предкотлового коллектора:
  // включаем предкотловой коллектор и копируем в него данные из обычного коллектора
  const shouldDrawCollectorAsPreBoiler = params.isSystemFlipped && !params.hasPreBoilerCollector;
  
  let effectiveParams = { ...params };
  
  if (shouldDrawCollectorAsPreBoiler) {
    // Включаем предкотловой коллектор
    effectiveParams.hasPreBoilerCollector = true;
    
    // Копируем параметры обычного коллектора в предкотловой
    effectiveParams.preBoilerSupplyPipeDiameter = params.supplyPipeDiameter;
    effectiveParams.preBoilerReturnPipeDiameter = params.returnPipeDiameter;
    effectiveParams.preBoilerPipeSpacing = params.pipeSpacing;
    effectiveParams.preBoilerPipeSpacingMode = params.pipeSpacingMode;
    effectiveParams.preBoilerSupplyPipeOffset = params.supplyPipeOffset;
    effectiveParams.preBoilerReturnPipeOffset = params.returnPipeOffset;
    effectiveParams.preBoilerSupplyFirstCircuitOffset = params.supplyFirstCircuitOffset;
    effectiveParams.preBoilerSupplyLastCircuitOffset = params.supplyLastCircuitOffset;
    effectiveParams.preBoilerReturnFirstCircuitOffset = params.returnFirstCircuitOffset;
    effectiveParams.preBoilerReturnLastCircuitOffset = params.returnLastCircuitOffset;
    effectiveParams.preBoilerSupplyCircuitSpacing = params.supplyCircuitSpacing;
    effectiveParams.preBoilerReturnCircuitSpacing = params.returnCircuitSpacing;
    effectiveParams.preBoilerSupplySpacingBetweenCircuits = params.supplySpacingBetweenCircuits;
    effectiveParams.preBoilerReturnSpacingBetweenCircuits = params.returnSpacingBetweenCircuits;
    effectiveParams.preBoilerSupplyCenterFittingCount = params.supplyCenterFittingCount;
    effectiveParams.preBoilerReturnCenterFittingCount = params.returnCenterFittingCount;
    effectiveParams.preBoilerSupplyAdditionalOutletCount = params.supplyAdditionalOutletCount;
    effectiveParams.preBoilerReturnAdditionalOutletCount = params.returnAdditionalOutletCount;
    effectiveParams.usePreBoilerManualLength = params.useManualLength;
    effectiveParams.manualPreBoilerSupplyLength = params.manualSupplyLength;
    effectiveParams.manualPreBoilerReturnLength = params.manualReturnLength;
    
    // Копируем массивы контуров и фитингов
    effectiveParams.preBoilerSupplyCircuits = params.supplyCircuits || [];
    effectiveParams.preBoilerReturnCircuits = params.returnCircuits || [];
    effectiveParams.preBoilerSupplyCenterFittings = params.supplyCenterFittings || [];
    effectiveParams.preBoilerReturnCenterFittings = params.returnCenterFittings || [];
    effectiveParams.preBoilerSupplyAdditionalOutlets = params.supplyAdditionalOutlets || [];
    effectiveParams.preBoilerReturnAdditionalOutlets = params.returnAdditionalOutlets || [];
    
    // При отражении: правые торцевые обычного коллектора становятся левыми предкотлового
    // (правый торец коллектора при отражении оказывается снаружи слева)
    effectiveParams.hasPreBoilerSupplyLeftConnection = params.hasSupplyRightConnection;
    effectiveParams.preBoilerSupplyLeftConnectionType = params.supplyRightConnectionType;
    effectiveParams.preBoilerSupplyLeftConnectionFitting = params.supplyRightConnectionFitting;
    effectiveParams.preBoilerSupplyLeftConnectionFittingLength = params.supplyRightConnectionFittingLength;
    effectiveParams.preBoilerSupplyLeftConnectionLength = params.supplyRightConnectionLength;
    effectiveParams.preBoilerSupplyLeftConnectionPipeDiameter = params.supplyRightConnectionPipeDiameter;
    effectiveParams.preBoilerSupplyLeftConnectionPipeLength = params.supplyRightConnectionPipeLength;

    effectiveParams.hasPreBoilerReturnLeftConnection = params.hasReturnRightConnection;
    effectiveParams.preBoilerReturnLeftConnectionType = params.returnRightConnectionType;
    effectiveParams.preBoilerReturnLeftConnectionFitting = params.returnRightConnectionFitting;
    effectiveParams.preBoilerReturnLeftConnectionFittingLength = params.returnRightConnectionFittingLength;
    effectiveParams.preBoilerReturnLeftConnectionLength = params.returnRightConnectionLength;
    effectiveParams.preBoilerReturnLeftConnectionPipeDiameter = params.returnRightConnectionPipeDiameter;
    effectiveParams.preBoilerReturnLeftConnectionPipeLength = params.returnRightConnectionPipeLength;

    // Заглушки/переходы для торцевых подключений при отражении
    effectiveParams.hasPreBoilerSupplyLeftCap = params.hasSupplyRightCap;
    effectiveParams.preBoilerSupplyLeftCapType = params.supplyRightCapType;
    effectiveParams.preBoilerSupplyLeftCapElementType = params.supplyRightCapElementType;
    effectiveParams.preBoilerSupplyLeftReducer = params.supplyRightReducer;

    effectiveParams.hasPreBoilerReturnLeftCap = params.hasReturnRightCap;
    effectiveParams.preBoilerReturnLeftCapType = params.returnRightCapType;
    effectiveParams.preBoilerReturnLeftCapElementType = params.returnRightCapElementType;
    effectiveParams.preBoilerReturnLeftReducer = params.returnRightReducer;
    
    effectiveParams.preBoilerEqualizeLengths = params.equalizeLengths;
  }

  // При отражении с явным предкотловым: меняем местами торцевые подключения
  // Правые торцевые обычного коллектора ↔ Левые торцевые предкотлового
  if (params.isSystemFlipped && params.hasPreBoilerCollector) {
    // Левые предкотлового становятся правыми торцевыми обычного (и наоборот)
    // Подающий:
    effectiveParams.hasPreBoilerSupplyLeftConnection = params.hasSupplyRightConnection;
    effectiveParams.preBoilerSupplyLeftConnectionType = params.supplyRightConnectionType;
    effectiveParams.preBoilerSupplyLeftConnectionFitting = params.supplyRightConnectionFitting;
    effectiveParams.preBoilerSupplyLeftConnectionFittingLength = params.supplyRightConnectionFittingLength;
    effectiveParams.preBoilerSupplyLeftConnectionLength = params.supplyRightConnectionLength;
    effectiveParams.preBoilerSupplyLeftConnectionPipeDiameter = params.supplyRightConnectionPipeDiameter;
    effectiveParams.preBoilerSupplyLeftConnectionPipeLength = params.supplyRightConnectionPipeLength;
    effectiveParams.hasPreBoilerSupplyLeftCap = params.hasSupplyRightCap;
    effectiveParams.preBoilerSupplyLeftCapType = params.supplyRightCapType;
    effectiveParams.preBoilerSupplyLeftCapElementType = params.supplyRightCapElementType;
    effectiveParams.preBoilerSupplyLeftReducer = params.supplyRightReducer;

    effectiveParams.hasSupplyRightConnection = params.hasPreBoilerSupplyLeftConnection;
    effectiveParams.supplyRightConnectionType = params.preBoilerSupplyLeftConnectionType;
    effectiveParams.supplyRightConnectionFitting = params.preBoilerSupplyLeftConnectionFitting;
    effectiveParams.supplyRightConnectionFittingLength = params.preBoilerSupplyLeftConnectionFittingLength;
    effectiveParams.supplyRightConnectionLength = params.preBoilerSupplyLeftConnectionLength;
    effectiveParams.supplyRightConnectionPipeDiameter = params.preBoilerSupplyLeftConnectionPipeDiameter;
    effectiveParams.supplyRightConnectionPipeLength = params.preBoilerSupplyLeftConnectionPipeLength;
    effectiveParams.hasSupplyRightCap = params.hasPreBoilerSupplyLeftCap;
    effectiveParams.supplyRightCapType = params.preBoilerSupplyLeftCapType;
    effectiveParams.supplyRightCapElementType = params.preBoilerSupplyLeftCapElementType;
    effectiveParams.supplyRightReducer = params.preBoilerSupplyLeftReducer;

    // Обратный:
    effectiveParams.hasPreBoilerReturnLeftConnection = params.hasReturnRightConnection;
    effectiveParams.preBoilerReturnLeftConnectionType = params.returnRightConnectionType;
    effectiveParams.preBoilerReturnLeftConnectionFitting = params.returnRightConnectionFitting;
    effectiveParams.preBoilerReturnLeftConnectionFittingLength = params.returnRightConnectionFittingLength;
    effectiveParams.preBoilerReturnLeftConnectionLength = params.returnRightConnectionLength;
    effectiveParams.preBoilerReturnLeftConnectionPipeDiameter = params.returnRightConnectionPipeDiameter;
    effectiveParams.preBoilerReturnLeftConnectionPipeLength = params.returnRightConnectionPipeLength;
    effectiveParams.hasPreBoilerReturnLeftCap = params.hasReturnRightCap;
    effectiveParams.preBoilerReturnLeftCapType = params.returnRightCapType;
    effectiveParams.preBoilerReturnLeftCapElementType = params.returnRightCapElementType;
    effectiveParams.preBoilerReturnLeftReducer = params.returnRightReducer;

    effectiveParams.hasReturnRightConnection = params.hasPreBoilerReturnLeftConnection;
    effectiveParams.returnRightConnectionType = params.preBoilerReturnLeftConnectionType;
    effectiveParams.returnRightConnectionFitting = params.preBoilerReturnLeftConnectionFitting;
    effectiveParams.returnRightConnectionFittingLength = params.preBoilerReturnLeftConnectionFittingLength;
    effectiveParams.returnRightConnectionLength = params.preBoilerReturnLeftConnectionLength;
    effectiveParams.returnRightConnectionPipeDiameter = params.preBoilerReturnLeftConnectionPipeDiameter;
    effectiveParams.returnRightConnectionPipeLength = params.preBoilerReturnLeftConnectionPipeLength;
    effectiveParams.hasReturnRightCap = params.hasPreBoilerReturnLeftCap;
    effectiveParams.returnRightCapType = params.preBoilerReturnLeftCapType;
    effectiveParams.returnRightCapElementType = params.preBoilerReturnLeftCapElementType;
    effectiveParams.returnRightReducer = params.preBoilerReturnLeftReducer;
  }

  // Используем effectiveParams для деструктуризации
  const {
    // === Параметры гидроразделителя ===
    shape = 'round',
    mainPipeDiameter,
    separatorHeight,
    hasTopCap,
    hasBottomCap,
    topCapElementType,
    bottomCapElementType,
    topCapType,
    bottomCapType,
    topReducer,
    bottomReducer,
    hasTopConnection,
    topCapFitting,
    topCapFittingLength,
    hasBottomConnection,
    bottomCapFitting,
    bottomCapFittingLength,
    leftConnections = [],
    additionalOutlets = [],
    
    // === Параметры отражения системы ===
    isSystemFlipped = false,
    
    // === Параметры предкотлового коллектора ===
    hasPreBoilerCollector = false,
    preBoilerSupplyPipeDiameter = 76.1,
    preBoilerReturnPipeDiameter = 76.1,
    preBoilerSupplyPipeProfile, // профиль для квадратных (подающий предкотловой)
    preBoilerReturnPipeProfile, // профиль для квадратных (обратный предкотловой)
    preBoilerPipeSpacing = 200,
    preBoilerPipeSpacingMode = 'interaxis', // 'interaxis' или 'offset'
    preBoilerSupplyPipeOffset = 50,
    preBoilerReturnPipeOffset = 50,
    preBoilerSupplyFirstCircuitOffset = 60,
    preBoilerSupplyLastCircuitOffset = 60,
    preBoilerReturnFirstCircuitOffset = 60,
    preBoilerReturnLastCircuitOffset = 60,
    preBoilerSupplyCircuits = [],
    preBoilerReturnCircuits = [],
    preBoilerSupplyCenterFittings = [],
    preBoilerReturnCenterFittings = [],
    preBoilerSupplyAdditionalOutlets = [],
    preBoilerReturnAdditionalOutlets = [],
    // Левые торцевые подключения предкотловых коллекторов
    hasPreBoilerSupplyLeftConnection,
    preBoilerSupplyLeftConnectionType,
    preBoilerSupplyLeftConnectionFitting,
    preBoilerSupplyLeftConnectionFittingLength,
    preBoilerSupplyLeftConnectionLength,
    hasPreBoilerReturnLeftConnection,
    preBoilerReturnLeftConnectionType,
    preBoilerReturnLeftConnectionFitting,
    preBoilerReturnLeftConnectionFittingLength,
    preBoilerReturnLeftConnectionLength,
    // Левые заглушки предкотловых коллекторов
    hasPreBoilerSupplyLeftCap,
    preBoilerSupplyLeftCapType,
    preBoilerSupplyLeftCapElementType,
    preBoilerSupplyLeftReducer,
    hasPreBoilerReturnLeftCap,
    preBoilerReturnLeftCapType,
    preBoilerReturnLeftCapElementType,
    preBoilerReturnLeftReducer,
    
    // === Параметры распределительного коллектора ===
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeProfile, // профиль для квадратных (подающий)
    returnPipeProfile, // профиль для квадратных (обратный)
    pipeSpacing,
    pipeSpacingMode = 'interaxis', // 'interaxis' или 'offset'
    supplyPipeOffset = 50,
    returnPipeOffset = 50,
    supplyFirstCircuitOffset,
    supplyLastCircuitOffset,
    returnFirstCircuitOffset,
    returnLastCircuitOffset,
    supplyCircuits = [],
    returnCircuits = [],
    supplyCenterFittings = [],
    returnCenterFittings = [],
    supplyAdditionalOutlets = [],
    returnAdditionalOutlets = [],
    // Правые подключения распределительного коллектора
    hasSupplyRightConnection,
    supplyRightConnectionType,
    supplyRightConnectionFitting,
    supplyRightConnectionFittingLength,
    supplyRightConnectionLength,
    hasReturnRightConnection,
    returnRightConnectionType,
    returnRightConnectionFitting,
    returnRightConnectionFittingLength,
    returnRightConnectionLength,
    // Правые заглушки
    hasSupplyRightCap,
    supplyRightCapType,
    supplyRightCapElementType,
    supplyRightReducer,
    hasReturnRightCap,
    returnRightCapType,
    returnRightCapElementType,
    returnRightReducer,
    equalizeLengths,
    preBoilerEqualizeLengths,
  } = effectiveParams;

  // Для квадратных профилей принудительно устанавливаем прямые заглушки
  let effectiveTopCapType = topCapType;
  let effectiveBottomCapType = bottomCapType;
  let effectiveTopCapElementType = topCapElementType;
  let effectiveBottomCapElementType = bottomCapElementType;
  let effectivePreBoilerSupplyLeftCapType = preBoilerSupplyLeftCapType;
  let effectivePreBoilerReturnLeftCapType = preBoilerReturnLeftCapType;
  let effectivePreBoilerSupplyLeftCapElementType = preBoilerSupplyLeftCapElementType;
  let effectivePreBoilerReturnLeftCapElementType = preBoilerReturnLeftCapElementType;
  let effectiveSupplyRightCapType = supplyRightCapType;
  let effectiveReturnRightCapType = returnRightCapType;
  let effectiveSupplyRightCapElementType = supplyRightCapElementType;
  let effectiveReturnRightCapElementType = returnRightCapElementType;
  
  
  if (params.shape === 'square') {
    // Для квадратных профилей только прямые заглушки
    if (hasTopCap) {
      effectiveTopCapType = 'straight';
      effectiveTopCapElementType = 'cap';
    }
    if (hasBottomCap) {
      effectiveBottomCapType = 'straight';
      effectiveBottomCapElementType = 'cap';
    }
    // Для предкотлового коллектора
    if (hasPreBoilerSupplyLeftCap) {
      effectivePreBoilerSupplyLeftCapType = 'straight';
      effectivePreBoilerSupplyLeftCapElementType = 'cap';
    }
    if (hasPreBoilerReturnLeftCap) {
      effectivePreBoilerReturnLeftCapType = 'straight';
      effectivePreBoilerReturnLeftCapElementType = 'cap';
    }
    // Для основного коллектора
    if (hasSupplyRightCap) {
      effectiveSupplyRightCapType = 'straight';
      effectiveSupplyRightCapElementType = 'cap';
    }
    if (hasReturnRightCap) {
      effectiveReturnRightCapType = 'straight';
      effectiveReturnRightCapElementType = 'cap';
    }
    
  }

  // Определяем покрытие для всей системы
  // Собираем все уникальные диаметры труб в системе
  const allDiameters = [mainPipeDiameter, supplyPipeDiameter, returnPipeDiameter];
  
  if (hasPreBoilerCollector) {
    allDiameters.push(preBoilerSupplyPipeDiameter, preBoilerReturnPipeDiameter);
  }
  
  // Функция для получения диаметра промежуточной трубы для фитинга
  const getPipeDiameterForFitting = (fittingId) => {
    if (!fittingId) return null;
    
    if (fittingId.startsWith('Flanec_')) {
      const dn = extractDnFromFlangeKey(fittingId);
      return getPipeDiameterForDn(dn);
    }
    
    if (fittingId.startsWith('Nak_Gaika_')) {
      const size = extractSizeFromNakGaikaKey(fittingId);
      return getPipeDiameterForNakGaika(size);
    }
    
    if (fittingId.startsWith('Shaib_Nar_Rezba_')) {
      const size = extractSizeFromShaibaNarRezba(fittingId);
      return getPipeDiameterForShaibaNarRezba(size);
    }
    
    return null;
  };
  
  // Добавляем диаметры ТОЛЬКО промежуточных труб фланцев из контуров
  // (сами контуры - ниппели, отводы и т.д. - НЕ влияют на покрытие основной системы)
  supplyCircuits.forEach(circuit => {
    if (circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  returnCircuits.forEach(circuit => {
    if (circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  if (hasPreBoilerCollector) {
    preBoilerSupplyCircuits.forEach(circuit => {
      if (circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
        const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
        if (pipeDiameter) allDiameters.push(pipeDiameter);
      }
    });
    preBoilerReturnCircuits.forEach(circuit => {
      if (circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
        const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
        if (pipeDiameter) allDiameters.push(pipeDiameter);
      }
    });
  }
  
  // Добавляем диаметры ТОЛЬКО промежуточных труб фланцев из подключений гидрострелки
  leftConnections.forEach(conn => {
    if (conn.fittingId && conn.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(conn.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  // Добавляем диаметры из торцевых подключений гидрострелки
  if (hasTopConnection && topCapFitting) {
    const pipeDiameter = getPipeDiameterForFitting(topCapFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasBottomConnection && bottomCapFitting) {
    const pipeDiameter = getPipeDiameterForFitting(bottomCapFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  
  // Добавляем диаметры из торцевых подключений коллекторов
  if (hasPreBoilerCollector) {
    if (hasPreBoilerSupplyLeftConnection && preBoilerSupplyLeftConnectionFitting) {
      const pipeDiameter = getPipeDiameterForFitting(preBoilerSupplyLeftConnectionFitting);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
    if (hasPreBoilerReturnLeftConnection && preBoilerReturnLeftConnectionFitting) {
      const pipeDiameter = getPipeDiameterForFitting(preBoilerReturnLeftConnectionFitting);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  }
  if (hasSupplyRightConnection && supplyRightConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(supplyRightConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasReturnRightConnection && returnRightConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(returnRightConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  
  // Убираем дубликаты
  const uniqueDiameters = [...new Set(allDiameters)];
  
  // Проверяем наличие оцинкованных фланцев в системе
  const hasGalvanizedFlanges = () => {
    // Проверяем контуры
    const checkCircuits = (circuits) => circuits.some(circuit => 
      circuit.fittingId && circuit.fittingId.includes('_galv')
    );
    
    if (checkCircuits(supplyCircuits) || checkCircuits(returnCircuits)) return true;
    
    if (hasPreBoilerCollector) {
      if (checkCircuits(preBoilerSupplyCircuits) || checkCircuits(preBoilerReturnCircuits)) return true;
    }
    
    // Проверяем левые подключения гидрострелки
    if (leftConnections.some(conn => conn.fittingId && conn.fittingId.includes('_galv'))) return true;
    
    // Проверяем торцевые подключения гидрострелки
    if ((hasTopConnection && topCapFitting && topCapFitting.includes('_galv')) ||
        (hasBottomConnection && bottomCapFitting && bottomCapFitting.includes('_galv'))) return true;
    
    // Проверяем торцевые подключения основного коллектора (правые)
    if ((hasSupplyRightConnection && supplyRightConnectionFitting && supplyRightConnectionFitting.includes('_galv')) ||
        (hasReturnRightConnection && returnRightConnectionFitting && returnRightConnectionFitting.includes('_galv'))) return true;
    
    // Проверяем торцевые подключения предкотлового коллектора
    if (hasPreBoilerCollector) {
      if ((hasPreBoilerSupplyLeftConnection && preBoilerSupplyLeftConnectionFitting && preBoilerSupplyLeftConnectionFitting.includes('_galv')) ||
          (hasPreBoilerReturnLeftConnection && preBoilerReturnLeftConnectionFitting && preBoilerReturnLeftConnectionFitting.includes('_galv'))) return true;
    }
    
    return false;
  };
  
  // Определяем покрытие:
  // - Если квадратный профиль → всегда Матовый (профили только матовые)
  // - Если есть оцинкованные фланцы → всегда Матовый (для аннотации)
  // - Иначе: Зеркало если у всех труб есть, иначе Матовый
  const hasGalvFlanges = hasGalvanizedFlanges();
  const coating = (params.shape === 'square') ? 'Матовый' : (hasGalvFlanges ? 'Матовый' : determineCoating(uniqueDiameters));

  const cylinders = [];
  const fittings = [];
  const dimensionLines = [];
  const welds = [];
  const balloons = [];
  
  // Переменная для хранения Y-координаты общей линии длины коллектора
  // (используется для размещения размерной линии диаметра гидроразделителя)
  let totalLengthLineY;

  // Размеры холста (альбомная ориентация A3: 1191 x 842)
  const canvasWidth = 1191;
  // eslint-disable-next-line no-unused-vars
  const canvasHeight = 842;
  
  // ========================================
  // 1. ГИДРАВЛИЧЕСКИЙ РАЗДЕЛИТЕЛЬ (вертикально)
  // ========================================
  
  // Вычисляем длины заглушек
  // Для квадратных профилей используем ключ с профилем (кириллическая x), для круглых — с диаметром
  const getMainCapKey = (capType, isSquare, profile, diameter) => {
    if (isSquare) {
      // Для квадратных труб всегда прямые заглушки с профилем
      const profileKey = (profile || '80x80').replace('x', 'х'); // латинская x → кириллическая х
      return `Zaglushka_PR_${profileKey}`;
    }
    return capType === 'straight' ? `Zaglushka_PR_${diameter}` : `Zaglushka_EL_${diameter}`;
  };

  const isSquareShape = shape === 'square';
  const mainProfile = effectiveParams.mainPipeProfile || '80x80';
  
  const topCapKey = getMainCapKey(topCapType, isSquareShape, mainProfile, mainPipeDiameter);
  const bottomCapKey = getMainCapKey(bottomCapType, isSquareShape, mainProfile, mainPipeDiameter);
  const topCapData = fittingsData[topCapKey];
  const bottomCapData = fittingsData[bottomCapKey];
  
  let topCapLength = topCapData?.length || 0;
  let bottomCapLength = bottomCapData?.length || 0;
  
  let topEllipticalCapLength = 0;
  let bottomEllipticalCapLength = 0;
  
  if (hasTopCap && effectiveTopCapType === 'elliptical' && effectiveTopCapElementType === 'cap') {
    topEllipticalCapLength = topCapLength;
  }
  
  if (hasBottomCap && effectiveBottomCapType === 'elliptical' && effectiveBottomCapElementType === 'cap') {
    bottomEllipticalCapLength = bottomCapLength;
  }
  
  const topStraightCapLength = (effectiveTopCapType === 'straight' && hasTopCap && effectiveTopCapElementType === 'cap') ? topCapLength : 0;
  const bottomStraightCapLength = (effectiveBottomCapType === 'straight' && hasBottomCap && effectiveBottomCapElementType === 'cap') ? bottomCapLength : 0;
  
  // Бортшайбы для торцевых подключений
  let topBortshaibaLengthForHeight = 0;
  let bottomBortshaibaLengthForHeight = 0;
  
  if (hasTopConnection && topCapFitting) {
    const isFlange = topCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !topCapFitting.endsWith('_welded');
    const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
    
    const topCapReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    const fitsFlush = !topCapReq.needsCap;
    
    if (fitsFlush) {
      if (isFreeFlange || isNakGaika) {
        const shaibaKey = getShaibaKeyForFitting(topCapFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            topBortshaibaLengthForHeight = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba) {
        const shaibaInfo = fittingsData[topCapFitting];
        if (shaibaInfo) {
          topBortshaibaLengthForHeight = shaibaInfo.length;
        }
      }
    }
  }
  
  if (hasBottomConnection && bottomCapFitting) {
    const isFlange = bottomCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !bottomCapFitting.endsWith('_welded');
    const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
    
    const bottomCapReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const fitsFlush = !bottomCapReq.needsCap;
    
    if (fitsFlush) {
      if (isFreeFlange || isNakGaika) {
        const shaibaKey = getShaibaKeyForFitting(bottomCapFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            bottomBortshaibaLengthForHeight = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba) {
        const shaibaInfo = fittingsData[bottomCapFitting];
        if (shaibaInfo) {
          bottomBortshaibaLengthForHeight = shaibaInfo.length;
        }
      }
    }
  }
  
  const actualMainPipeHeight = separatorHeight - topStraightCapLength - bottomStraightCapLength - topBortshaibaLengthForHeight - bottomBortshaibaLengthForHeight;
  
  const topFittingData = topCapFitting ? fittingsData[topCapFitting] : null;
  const bottomFittingData = bottomCapFitting ? fittingsData[bottomCapFitting] : null;
  const topFittingLength = topFittingData
    ? endFittingAutoLength(topCapFitting, topCapFittingLength, getFittingLength(topCapFitting, null))
    : 0;
  // eslint-disable-next-line no-unused-vars
  const bottomFittingLength = bottomFittingData
    ? endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, getFittingLength(bottomCapFitting, null))
    : 0;
  
  // Вычисляем максимальную длину левых подключений
  let maxLeftFittingLength = 0;
  leftConnections.forEach(conn => {
    const connFittingData = fittingsData[conn.fittingId];
    if (connFittingData) {
      const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
      maxLeftFittingLength = Math.max(maxLeftFittingLength, fittingLength);
    }
  });
  
  // Используем эффективные отступы, если они переданы
  const actualSupplyFirstCircuitOffset = effectiveSupplyFirstCircuitOffset !== null 
    ? effectiveSupplyFirstCircuitOffset 
    : supplyFirstCircuitOffset;
  const actualReturnFirstCircuitOffset = effectiveReturnFirstCircuitOffset !== null 
    ? effectiveReturnFirstCircuitOffset 
    : returnFirstCircuitOffset;
  
  // Вычисляем длины коллекторов
  // Подача
  let supplyCollectorLength = actualSupplyFirstCircuitOffset + supplyLastCircuitOffset;
  supplyCircuits.forEach((circuit, index) => {
    const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
    supplyCollectorLength += circuitSpacing;
    
    if (index < supplyCircuits.length - 1) {
      const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
      supplyCollectorLength += spacingToNext;
    }
  });
  supplyCollectorLength = Math.max(supplyCollectorLength, 150);
  
  // Обратка
  let returnCollectorLength = actualReturnFirstCircuitOffset + returnLastCircuitOffset;
  returnCircuits.forEach((circuit, index) => {
    const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
    returnCollectorLength += circuitSpacing;
    
    if (index < returnCircuits.length - 1) {
      const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
      returnCollectorLength += spacingToNext;
    }
  });
  returnCollectorLength = Math.max(returnCollectorLength, 150);
  
  // Используем максимальную длину для обоих коллекторов, если не указано иное
  // Если equalizeLengths === false, используем разные длины
  const shouldEqualize = equalizeLengths !== false;
  const collectorLength = shouldEqualize 
    ? Math.max(supplyCollectorLength, returnCollectorLength)
    : Math.max(supplyCollectorLength, returnCollectorLength); // Для позиционирования всё равно нужна максимальная
  
  // Фактические длины труб (могут отличаться если не приравниваем)
  const actualSupplyCollectorLength = shouldEqualize ? collectorLength : supplyCollectorLength;
  const actualReturnCollectorLength = shouldEqualize ? collectorLength : returnCollectorLength;
  
  // Позиционирование (центрируем всю конструкцию)
  const totalWidth = maxLeftFittingLength + 50 + mainPipeDiameter + collectorLength + 100;
  const startX = (canvasWidth - totalWidth) / 2 + maxLeftFittingLength + 50 + 600; // Смещение вправо на 150мм
  const startY = 500; // Смещение вниз
  
  // ============================================
  // Вспомогательная функция для получения метки фитинга
  // ============================================
  const getFittingLabel = (fittingKey) => {
    if (!fittingKey) return '';

    const sizeMap = {
      '1.4': '1/4"',
      '3.8': '3/8"',
      '1.2': '1/2"',
      '3.4': '3/4"',
      '1': '1"',
      '1_1.4': '1.1/4"',
      '1_1.2': '1.1/2"',
      '2': '2"',
      '2_1/2': '2.1/2"',
      '3': '3"',
      '4': '4"',
    };

    if (fittingKey.startsWith('Nippel_')) {
      const size = fittingKey.replace('Nippel_', '');
      return (sizeMap[size] || size) + 'НР';
    }
    if (fittingKey.startsWith('Mufta_')) {
      const size = fittingKey.replace('Mufta_', '');
      return (sizeMap[size] || size) + 'ВР';
    }
    if (fittingKey.startsWith('Perehod_Konc_')) {
      const dims = fittingKey.replace('Perehod_Konc_', '');
      return 'Переход\n' + dims;
    }
    if (fittingKey.startsWith('Flanec_')) {
      const fittingDataInfo = fittingsData[fittingKey];
      if (fittingDataInfo) {
        const displayName = fittingDataInfo.displayName;
        const match = displayName.match(/Фланец (Ду\d+)\s*\((.+)\)/);
        if (match) return 'Фланец ' + match[1] + '\n(' + match[2] + ')';
        return displayName;
      }
    }
    if (fittingKey.startsWith('Nak_Gaika_')) {
      const size = fittingKey.replace('Nak_Gaika_', '');
      return (sizeMap[size] || size) + ' Н.Г';
    }
    if (fittingKey.startsWith('Shaib_Nar_Rezba_')) {
      const size = fittingKey.replace('Shaib_Nar_Rezba_', '');
      return (sizeMap[size] || size) + ' Ш.Н.Р';
    }
    return fittingKey;
  };
  
  const mainPipeTopY = startY + topFittingLength + topEllipticalCapLength + topBortshaibaLengthForHeight + topStraightCapLength;
  
  // Середина разделителя для смещения размерных линий к центру (используется в balloons)
  const midSeparatorY = mainPipeTopY + actualMainPipeHeight / 2;
  
  // Основная труба разделителя
  const mainPipeId = `cyl-main-${Date.now()}`;
  const mainPipeThickness = getMinThickness(mainPipeDiameter, coating);
  // Создаем pipeSpec с учетом формы
  let mainPipeSpec;
  let mainPipeSize = mainPipeDiameter; // Размер для позиционирования
  if (params.shape === 'square') {
    const profile = params.mainPipeProfile || '80x80';
    mainPipeSize = parseFloat(profile.split('x')[0]); // Берем первое число из профиля
    const comboKey = `SQUARE_PROFILE|${profile}|`;
    const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
    let thickness = '2';
    let squareCoating = 'Матовый';
    if (matchingKey) {
      const parts = matchingKey.split('|');
      thickness = parts[2] || '2';
      squareCoating = parts[3] || 'Матовый';
    }
    mainPipeSpec = {
      shape: 'square',
      profile: profile,
      thickness: thickness,
      coating: squareCoating,
      squareType: 'profile',
    };
  } else {
    mainPipeSpec = createPipeSpec(mainPipeDiameter, mainPipeThickness, coating);
  }
  
  cylinders.push({
    id: mainPipeId,
    x: startX - mainPipeSize / 2,
    y: mainPipeTopY,
    width: mainPipeSize,
    height: actualMainPipeHeight,
    pipeSpec: mainPipeSpec,
  });
  
  // Верхняя заглушка/переход
  let generateTopCap = hasTopCap;
  const topFittingData2 = topFittingData;
  
  // ID для прямых заглушек (для привязки размерных линий)
  let topStraightCapIdForDimLine = null;
  let bottomStraightCapIdForDimLine = null;
  let topEllipticalCapIdForDimLine = null;
  let bottomEllipticalCapIdForDimLine = null;
  
  if (hasTopConnection && topCapFitting && topFittingData2) {
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    if (capReq.fitsFlush) {
      generateTopCap = false;
    }
  }

  if (generateTopCap) {
    if (effectiveTopCapElementType === 'reducer' && topReducer && topReducer !== '' && fittingsData[topReducer]) {
      const reducer = generateReducer({
        reducerKey: topReducer,
        x: startX,
        y: mainPipeTopY,
        rotation: 0,
        side: 'top',
      });
      if (reducer) fittings.push(reducer);
    } else {
      const topCapId = `fitting-cap-top-${Date.now()}`;
      const cap = generateCap({
        capType: effectiveTopCapType,
        diameter: mainPipeDiameter,
        x: startX,
        y: mainPipeTopY,
        rotation: 0,
        side: 'top',
        shape: params.shape || 'round',
        profile: params.mainPipeProfile,
      });
      if (cap) {
        cap.id = topCapId;
        // Сохраняем ID для размерных линий
        if (effectiveTopCapType === 'straight') {
          topStraightCapIdForDimLine = topCapId;
        } else if (effectiveTopCapType === 'elliptical') {
          topEllipticalCapIdForDimLine = topCapId;
        }
        fittings.push(cap);
      }
    }
  }
  
  // Нижняя заглушка/переход
  let generateBottomCap = hasBottomCap;
  
  if (hasBottomConnection && bottomCapFitting && bottomFittingData) {
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    if (capReq.fitsFlush) {
      generateBottomCap = false;
    }
  }

  if (generateBottomCap) {
    if (effectiveBottomCapElementType === 'reducer' && bottomReducer && bottomReducer !== '' && fittingsData[bottomReducer]) {
      const reducer = generateReducer({
        reducerKey: bottomReducer,
        x: startX,
        y: mainPipeTopY + actualMainPipeHeight,
        rotation: 180,
        side: 'bottom',
      });
      if (reducer) fittings.push(reducer);
    } else {
      const bottomCapId = `fitting-cap-bottom-${Date.now()}`;
      const cap = generateCap({
        capType: effectiveBottomCapType,
        diameter: mainPipeDiameter,
        x: startX,
        y: mainPipeTopY + actualMainPipeHeight,
        rotation: 180,
        side: 'bottom',
        shape: params.shape || 'round',
        profile: params.mainPipeProfile,
      });
      if (cap) {
        cap.id = bottomCapId;
        // Сохраняем ID для размерных линий
        if (effectiveBottomCapType === 'straight') {
          bottomStraightCapIdForDimLine = bottomCapId;
        } else if (effectiveBottomCapType === 'elliptical') {
          bottomEllipticalCapIdForDimLine = bottomCapId;
        }
        fittings.push(cap);
      }
    }
  }
  
  // ========================================
  // 4. Торцевое подключение сверху (ПОЛНАЯ ЛОГИКА из HydraulicSeparatorEditor)
  // ========================================
  
  // Переменные для хранения ID объектов (для привязки размерных линий)
  // eslint-disable-next-line no-unused-vars
  let topReducerId = null;
  // eslint-disable-next-line no-unused-vars
  let topIntermediatePipeId = null;
  let topFittingEndObjectId = null; // ID конечного объекта (фитинг или шайба)
  let topFittingEndObjectType = null; // 'fitting' или 'cylinder'
  let topFittingId = null; // ID основного фитинга (для balloons)
  // eslint-disable-next-line no-unused-vars
  let topBortshaibaIdForDimLine = null; // ID бортшайбы для размерной линии общей высоты
  
  if (hasTopConnection && topCapFitting && topFittingData2) {
    topFittingId = `fitting-top-${Date.now()}`;
    
    // Определяем тип фитинга
    const isFlange = topCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !topCapFitting.endsWith('_welded');
    const isWeldedFlange = isFlange && !isFreeFlange;
    const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
    const isNippel = topCapFitting.startsWith('Nippel_');
    const isMufta = topCapFitting.startsWith('Mufta_');
    const needsWasher = isFreeFlange || isNakGaika;
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = topFittingData2.diameter;
    if (isFlange) {
      const dn = extractDnFromFlangeKey(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForDn(dn) || topFittingData2.diameter;
    } else if (isNakGaika) {
      const size = extractSizeFromNakGaikaKey(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForNakGaika(size) || topFittingData2.diameter;
    } else if (isShaibaNarRezba) {
      const size = extractSizeFromShaibaNarRezba(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size) || topFittingData2.diameter;
    }
    
    // Проверяем нужен ли переход/заглушка
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    const needsReducer = capReq.needsCap && capReq.capType === 'reducer';
    const needsEllipticalCap = capReq.needsCap && capReq.capType === 'elliptical';
    const fitsFlush = capReq.fitsFlush; // Диаметры совпадают - впритык
    
    // Начальная точка - верх основной трубы
    const capTopY = mainPipeTopY;
    
    // Получаем ключ шайбы/бортшайбы
    const shaibaKey = getShaibaKeyForFitting(topCapFitting);
    let bortshaibaLength = 0;
    if (needsWasher && shaibaKey) {
      const shaibaInfo = fittingsData[shaibaKey];
      bortshaibaLength = shaibaInfo?.length || 10;
    }
    if (isShaibaNarRezba) {
      bortshaibaLength = topFittingData2.length || 10;
    }
    
    // === ЛОГИКА 1: Диаметры совпадают (впритык) БЕЗ заглушки ===
    if (fitsFlush && !hasTopCap) {
      if (isNippel || isMufta) {
        // Ниппель/муфта впритык к основной трубе
        let fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
        
        
        // Логика для "Измененное значение"
        let dimensionOverride = {};
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else if (isFreeFlange || isNakGaika) {
        // Свободный фланец или накидная гайка впритык: шайба/бортшайба входит в размер гребенки
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
            topBortshaibaIdForDimLine = bortshaibaId; // Сохраняем для размерной линии
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0,
              y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: startX,
              groupY: mainPipeTopY, // От верха гребенки (шайба входит в размер)
              rotation: 0,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
          }
        }
        // Фланец/гайка надет на шайбу (шайба на конце трубы, фланец перед ней)
        const fLength = topFittingData2.length;
        
        // Логика для "Измененное значения" - накидные гайки (как для контуров)
        let dimensionOverride = {};
        if (isNakGaika && (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 54 || topCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaIdForDimLine,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY + fLength, // Фланец надет на трубу, его верх выше mainPipeTopY
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isShaibaNarRezba) {
        // Шайба с наружной резьбой впритык: сама шайба входит в размер гребенки
        // eslint-disable-next-line no-unused-vars
        topBortshaibaIdForDimLine = topFittingId; // Сохраняем для размерной линии (сама шайба)
        
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        if (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: topFittingData2.length,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: topFittingData2.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isWeldedFlange) {
        // Приварной фланец впритык
        const fLength = topFittingData2.length;
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY + fLength,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      }
    }
    // === ЛОГИКА 1.5: Ниппели/муфты С заглушкой (прямой или эллиптической), БЕЗ перехода ===
    else if ((isNippel || isMufta) && hasTopCap && topCapElementType !== 'reducer') {
      // Если есть заглушка (не переход) - ниппель/муфта размещается от конца заглушки
      let fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
      
      
      // Для прямой заглушки: она входит в размер, ниппель от (mainPipeTopY - topStraightCapLength)
      // Для эллиптической заглушки: она выступает, ниппель от (mainPipeTopY - topEllipticalCapLength)
      let connectionStartY = mainPipeTopY;
      if (topCapType === 'elliptical') {
        connectionStartY = mainPipeTopY - topEllipticalCapLength;
      } else if (topCapType === 'straight') {
        connectionStartY = mainPipeTopY - topStraightCapLength;
      }
      
      // Логика для "Измененное значение"
      let dimensionOverride = {};
      
      fittings.push({
        id: topFittingId,
        key: topCapFitting,
        itemKey: topCapFitting,
        x: 0,
        y: 0,
        width: topFittingData2.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 0,
        offsetX: topFittingData2.diameter / 2,
        offsetY: fLength,
        ...dimensionOverride,
        // IGES: торцевые подключения НЕ создают отверстия
        igesCreateHole: false,
      });
      topFittingEndObjectId = topFittingId;
      topFittingEndObjectType = 'fitting';
    }
    // === ЛОГИКА 1.6: Фланцы/Накидные гайки/шайбы с резьбой С ПРЯМОЙ заглушкой ===
    else if ((isFlange || isNakGaika || isShaibaNarRezba) && hasTopCap && topCapType === 'straight') {
      // Прямая заглушка - фитинги идут от её конца
      const capEndY = mainPipeTopY - topStraightCapLength;
      
      // Промежуточная труба для фланцев/гаек
      const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(topCapFitting, null));
      const totalConnectionLength = topCapFittingLength !== null && topCapFittingLength !== undefined ? topCapFittingLength : defaultLength;
      // Для прямой заглушки: вылет отсчитывается ОТ заглушки, НЕ вычитаем длину заглушки
      const intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
      
      let pipeEndY = capEndY;
      if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
        const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
        topIntermediatePipeId = intermediatePipeId;
        const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
        cylinders.push({
          id: intermediatePipeId,
          x: startX - pipeDiameterForFitting / 2,
          y: capEndY - intermediatePipeLength,
          width: pipeDiameterForFitting,
          height: intermediatePipeLength,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
        });
        pipeEndY = capEndY - intermediatePipeLength;
      }
      
      // Бортшайба/шайба
      let topBortshaibaId = null;
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId;
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        }
      }
      
      // Шайба с наружной резьбой
      if (isShaibaNarRezba) {
        let dimensionOverride = {};
        if (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: topFittingData2.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: topFittingData2.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData2.length;
        const fittingY = pipeEndY + fLength;
        
        let dimensionOverride = {};
        if (isNakGaika && (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 54 || topCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaId,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 2: С переходом концентрическим ===
    else if (needsReducer && capReq.reducer) {
      const reducerKey = capReq.reducer.key;
      const reducerLength = capReq.reducer.length;
      const reducerDiameter = capReq.reducer.diameter;

      // Переход от заглушки вверх
      const reducerId = `fitting-top-reducer-${Date.now()}`;
      topReducerId = reducerId; // Сохраняем ID для размерных линий

      fittings.push({
        id: reducerId,
        key: reducerKey,
        itemKey: reducerKey,
        x: 0,
        y: 0,
        width: reducerDiameter,
        height: reducerLength,
        groupX: startX,
        groupY: capTopY,
        rotation: 0,
        offsetX: reducerDiameter / 2,
        offsetY: reducerLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      
      // Точка подключения после перехода
      const reducerEndY = capTopY - reducerLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от перехода)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = reducerEndY;
      if (!isNippel && !isMufta) {
        // Длина вылета (общая длина - переход - бортшайба)
        const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
        const totalConnectionLength = topCapFittingLength ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(topCapFitting, null)));
        const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
        
        // Промежуточная труба
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
          topIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: reducerEndY - intermediatePipeLength,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
          });
          pipeEndY = reducerEndY - intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба (для свободных фланцев и накидных гаек)
      let topBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Фитинг
      if (isNippel || isMufta) {
        // Ниппель/муфта с переходом - размещаем сразу от перехода
        let fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
        
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData2.length;
        const fittingY = pipeEndY + fLength;
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 3: С эллиптической заглушкой (разные диаметры, нет перехода) ===
    else if (needsEllipticalCap) {
      // Эллиптическая заглушка уже есть (topCapType = 'elliptical')
      // ВАЖНО: Эллиптическая заглушка выступает наружу
      
      // Начальная точка - от конца эллиптической заглушки
      const capEndY = mainPipeTopY - topEllipticalCapLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от заглушки)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = capEndY;
      if (!isNippel && !isMufta) {
        // Промежуточная труба для фланцев/гаек
        const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
        const totalConnectionLength = topCapFittingLength ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(topCapFitting, null)));
        const capLength = topEllipticalCapLength; // Длина эллиптической заглушки
        // Для накидных гаек и шайб с резьбой: вылет от заглушки, не вычитаем capLength
        const intermediatePipeLength = (isNakGaika || isShaibaNarRezba) 
          ? Math.max(0, totalConnectionLength - bortshaibaLength)
          : Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
        
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
          // eslint-disable-next-line no-unused-vars
          topIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: capEndY - intermediatePipeLength,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
          });
          pipeEndY = capEndY - intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба
      let topBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой (сама шайба = фитинг)
      if (isShaibaNarRezba) {
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        if (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: topFittingData2.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: topFittingData2.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else if (isNippel || isMufta) {
        // Ниппель/муфта с эллиптической заглушкой - размещаем фитинг сразу от заглушки (без промежуточной трубы)
        let fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
        
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData2.length;
        const fittingY = pipeEndY + fLength;
        
        // Логика для "Измененное значение" - накидные гайки (как для контуров)
        let dimensionOverride = {};
        if (isNakGaika && (topCapFittingLength === null || topCapFittingLength === undefined || topCapFittingLength === 40 || topCapFittingLength === 54 || topCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaId,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData2.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData2.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 4: Обычный фитинг с заглушкой (ниппель/муфта) ===
    // Аналогично распределительному коллектору: фитинг размещается от края заглушки
    else {
      const fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
      
      // Начальная точка подключения - от края заглушки (если есть)
      // Для эллиптической заглушки: она создана в секции 2 и размещена от mainPipeTopY вверх на topEllipticalCapLength
      // Поэтому фитинг должен начинаться от (mainPipeTopY - topEllipticalCapLength)
      let connectionStartY = mainPipeTopY;
      if (needsEllipticalCap) {
        // Эллиптическая заглушка выступает наружу - фитинг от её конца
        connectionStartY = mainPipeTopY - topEllipticalCapLength;
      }
      // Прямая заглушка входит в размер гребёнки - фитинг от mainPipeTopY
      
      
      fittings.push({
        id: topFittingId,
        key: topCapFitting,
        itemKey: topCapFitting,
        x: 0,
        y: 0,
        width: topFittingData2.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 0,
        offsetX: topFittingData2.diameter / 2,
        offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
    }
  }
  
  // ========================================
  // 5. Торцевое подключение снизу (ПОЛНАЯ ЛОГИКА из HydraulicSeparatorEditor)
  // ========================================
  
  // Переменные для хранения ID объектов (для привязки размерных линий)
  // eslint-disable-next-line no-unused-vars
  let bottomReducerId = null;
  // eslint-disable-next-line no-unused-vars
  let bottomIntermediatePipeId = null;
  let bottomFittingEndObjectId = null;
  let bottomFittingEndObjectType = null;
  let bottomFittingId = null; // ID основного фитинга (для balloons)
  // eslint-disable-next-line no-unused-vars
  let bottomBortshaibaIdForDimLine = null; // ID бортшайбы для размерной линии общей высоты

  if (hasBottomConnection && bottomCapFitting && bottomFittingData) {
    bottomFittingId = `fitting-bottom-${Date.now()}`;
    
    // Определяем тип фитинга
    const isFlange = bottomCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !bottomCapFitting.endsWith('_welded');
    const isWeldedFlange = isFlange && !isFreeFlange;
    const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
    const isNippel = bottomCapFitting.startsWith('Nippel_');
    const isMufta = bottomCapFitting.startsWith('Mufta_');
    const needsWasher = isFreeFlange || isNakGaika;
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = bottomFittingData.diameter;
    if (isFlange) {
      const dn = extractDnFromFlangeKey(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForDn(dn) || bottomFittingData.diameter;
    } else if (isNakGaika) {
      const size = extractSizeFromNakGaikaKey(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForNakGaika(size) || bottomFittingData.diameter;
    } else if (isShaibaNarRezba) {
      const size = extractSizeFromShaibaNarRezba(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size) || bottomFittingData.diameter;
    }
    
    // Проверяем нужен ли переход/заглушка
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const needsReducer = capReq.needsCap && capReq.capType === 'reducer';
    const needsEllipticalCap = capReq.needsCap && capReq.capType === 'elliptical';
    const fitsFlush = capReq.fitsFlush; // Диаметры совпадают - впритык
    
    // Начальная точка - низ основной трубы
    const bottomEdgeY = mainPipeTopY + actualMainPipeHeight;
    const capBottomY = bottomEdgeY;
    
    // Получаем ключ шайбы/бортшайбы
    const shaibaKey = getShaibaKeyForFitting(bottomCapFitting);
    let bortshaibaLength = 0;
    if (needsWasher && shaibaKey) {
      const shaibaInfo = fittingsData[shaibaKey];
      bortshaibaLength = shaibaInfo?.length || 10;
    }
    if (isShaibaNarRezba) {
      bortshaibaLength = bottomFittingData.length || 10;
    }
    
    // === ЛОГИКА 1: Диаметры совпадают (впритык) БЕЗ заглушки ===
    if (fitsFlush && !hasBottomCap) {
      if (isNippel || isMufta) {
        // Ниппель/муфта впритык к основной трубе
        let fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
        
        
        // Логика для "Измененное значение"
        let dimensionOverride = {};
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else if (isFreeFlange || isNakGaika) {
        // Свободный фланец или накидная гайка впритык: шайба/бортшайба входит в размер гребенки
        let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
            bottomBortshaibaId = bortshaibaId; // Сохраняем ID
            bottomBortshaibaIdForDimLine = bortshaibaId; // Сохраняем для размерной линии
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0,
              y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: startX,
              groupY: bottomEdgeY, // От низа гребенки (шайба входит в размер)
              rotation: 180,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
          }
        }
        // Фланец/гайка надет на шайбу
        const fLength = bottomFittingData.length;
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        if (isNakGaika && (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 54 || bottomCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY - fLength,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть)
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        }
      } else if (isShaibaNarRezba) {
        // Шайба с наружной резьбой впритык: сама шайба входит в размер гребенки
        // eslint-disable-next-line no-unused-vars
        bottomBortshaibaIdForDimLine = bottomFittingId; // Сохраняем для размерной линии (сама шайба)
        
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        if (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: bottomEdgeY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isWeldedFlange) {
        // Приварной фланец впритык
        const fLength = bottomFittingData.length;
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY - fLength,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      }
    }
    // === ЛОГИКА 1.5: Ниппели/муфты С заглушкой (прямой или эллиптической), БЕЗ перехода ===
    else if ((isNippel || isMufta) && hasBottomCap && bottomCapElementType !== 'reducer') {
      // Если есть заглушка (не переход) - ниппель/муфта размещается от конца заглушки
      let fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
      
      
      // Для прямой заглушки: она входит в размер, ниппель от (bottomEdgeY + bottomStraightCapLength)
      // Для эллиптической заглушки: она выступает, ниппель от (bottomEdgeY + bottomEllipticalCapLength)
      let connectionStartY = bottomEdgeY;
      if (bottomCapType === 'elliptical') {
        connectionStartY = bottomEdgeY + bottomEllipticalCapLength;
      } else if (bottomCapType === 'straight') {
        connectionStartY = bottomEdgeY + bottomStraightCapLength;
      }
      
      // Логика для "Измененное значение"
      let dimensionOverride = {};
      
      fittings.push({
        id: bottomFittingId,
        key: bottomCapFitting,
        itemKey: bottomCapFitting,
        x: 0,
        y: 0,
        width: bottomFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 180,
        offsetX: bottomFittingData.diameter / 2,
        offsetY: fLength,
        ...dimensionOverride,
        // IGES: торцевые подключения НЕ создают отверстия
        igesCreateHole: false,
      });
      bottomFittingEndObjectId = bottomFittingId;
      bottomFittingEndObjectType = 'fitting';
    }
    // === ЛОГИКА 1.6: Фланцы/Накидные гайки/шайбы с резьбой С ПРЯМОЙ заглушкой ===
    else if ((isFlange || isNakGaika || isShaibaNarRezba) && hasBottomCap && bottomCapType === 'straight') {
      // Прямая заглушка - фитинги идут от её конца
      const capEndY = bottomEdgeY + bottomStraightCapLength;
      
      // Промежуточная труба для фланцев/гаек
      const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(bottomCapFitting, null));
      const totalConnectionLength = bottomCapFittingLength !== null && bottomCapFittingLength !== undefined ? bottomCapFittingLength : defaultLength;
      // Для прямой заглушки: вылет отсчитывается ОТ заглушки, НЕ вычитаем длину заглушки
      const intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
      
      let pipeEndY = capEndY;
      if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
        const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
        bottomIntermediatePipeId = intermediatePipeId;
        const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
        cylinders.push({
          id: intermediatePipeId,
          x: startX - pipeDiameterForFitting / 2,
          y: capEndY,
          width: pipeDiameterForFitting,
          height: intermediatePipeLength,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
        });
        pipeEndY = capEndY + intermediatePipeLength;
      }
      
      // Бортшайба/шайба
      let bottomBortshaibaId = null;
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId;
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        }
      }
      
      // Шайба с наружной резьбой
      if (isShaibaNarRezba) {
        let dimensionOverride = {};
        if (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        let dimensionOverride = {};
        if (isNakGaika && (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 54 || bottomCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 2: С переходом концентрическим ===
    else if (needsReducer && capReq.reducer) {
      const reducerKey = capReq.reducer.key;
      const reducerLength = capReq.reducer.length;
      const reducerDiameter = capReq.reducer.diameter;
      
      // Переход от заглушки вниз
      const reducerId = `fitting-bottom-reducer-${Date.now()}`;
      bottomReducerId = reducerId; // Сохраняем ID для размерных линий
      fittings.push({
        id: reducerId,
        key: reducerKey,
        itemKey: reducerKey,
        x: 0,
        y: 0,
        width: reducerDiameter,
        height: reducerLength,
        groupX: startX,
        groupY: capBottomY,
        rotation: 180,
        offsetX: reducerDiameter / 2,
        offsetY: reducerLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      
      // Точка подключения после перехода
      const reducerEndY = capBottomY + reducerLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от перехода)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = reducerEndY;
      if (!isNippel && !isMufta) {
        // Длина вылета (общая длина - переход - бортшайба)
        const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
        const totalConnectionLength = bottomCapFittingLength ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(bottomCapFitting, null)));
        const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
        
        // Промежуточная труба
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
          bottomIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: reducerEndY,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
          });
          pipeEndY = reducerEndY + intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба (для свободных фланцев и накидных гаек)
      let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Фитинг
      if (isNippel || isMufta) {
        // Ниппель/муфта с переходом - размещаем сразу от перехода
        let fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
        
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 3: С эллиптической заглушкой (разные диаметры, нет перехода) ===
    else if (needsEllipticalCap) {
      // Эллиптическая заглушка уже есть (bottomCapType = 'elliptical')
      // ВАЖНО: Эллиптическая заглушка выступает наружу
      
      // Начальная точка - от конца эллиптической заглушки
      const capEndY = bottomEdgeY + bottomEllipticalCapLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от заглушки)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = capEndY;
      if (!isNippel && !isMufta) {
        // Промежуточная труба для фланцев/гаек
        const defaultNakGaikaShaibLength = params.shape === 'square' ? 58 : 40;
        const totalConnectionLength = bottomCapFittingLength ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaibLength : (isFlange ? 100 : getFittingLength(bottomCapFitting, null)));
        const capLength = bottomEllipticalCapLength; // Длина эллиптической заглушки
        // Для накидных гаек и шайб с резьбой: вылет от заглушки, не вычитаем capLength
        const intermediatePipeLength = (isNakGaika || isShaibaNarRezba) 
          ? Math.max(0, totalConnectionLength - bortshaibaLength)
          : Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
        
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
          // eslint-disable-next-line no-unused-vars
          bottomIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          const intermediateThickness = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: capEndY,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intermediateThickness, coating),
          });
          pipeEndY = capEndY + intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба
      let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой (сама шайба = фитинг)
      if (isShaibaNarRezba) {
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        if (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else if (isNippel || isMufta) {
        // Ниппель/муфта с эллиптической заглушкой - просто размещаем фитинг на конце промежуточной трубы
        let fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
        
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        if (isNakGaika && (bottomCapFittingLength === null || bottomCapFittingLength === undefined || bottomCapFittingLength === 40 || bottomCapFittingLength === 54 || bottomCapFittingLength === 58)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 4: Обычный фитинг с заглушкой (ниппель/муфта) ===
    // Аналогично распределительному коллектору: фитинг размещается от края заглушки
    else {
      const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
      
      // Начальная точка подключения - от края заглушки (если есть)
      // Для эллиптической заглушки: она создана в секции 3 и размещена от (mainPipeTopY + actualMainPipeHeight) вниз на bottomEllipticalCapLength
      // Поэтому фитинг должен начинаться от (capBottomY + bottomEllipticalCapLength)
      let connectionStartY = capBottomY;
      if (needsEllipticalCap) {
        // Эллиптическая заглушка выступает наружу - фитинг от её конца
        connectionStartY = capBottomY + bottomEllipticalCapLength;
      }
      // Прямая заглушка входит в размер гребёнки - фитинг от capBottomY
      
      fittings.push({
        id: bottomFittingId,
        key: bottomCapFitting,
        itemKey: bottomCapFitting,
        x: 0,
        y: 0,
        width: bottomFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 180,
        offsetX: bottomFittingData.diameter / 2,
        offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
    }
  }
  
  // Левые подключения (источник) - отключаются если есть предкотловой коллектор
  // При отражении системы (isSystemFlipped) левые подключения становятся правыми
  const leftFittingIds = []; // Массив для сохранения ID фитингов
  
  // Подключения генерируются если НЕТ реального предкотлового коллектора
  // (при отражении без предкотлового они тоже генерируются, но справа)
  if (!params.hasPreBoilerCollector) {
    leftConnections.forEach((conn, index) => {
      const fittingData = fittingsData[conn.fittingId];
      if (!fittingData) return;

      // Вычисляем реальную позицию
      let actualOffset;
      if (params.leftConnectionsPlacementMode === 'spacing') {
        // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
        actualOffset = params.leftConnectionsFirstOffset || 50;
        for (let i = 0; i < index; i++) {
          const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
          actualOffset += spacing;
        }
      } else {
        // Режим отступов: используем offsetFromTop и offsetDirection
        actualOffset = getConnectionPosition(
          conn.offsetFromTop, 
          conn.offsetDirection, 
          actualMainPipeHeight,
          topBortshaibaLengthForHeight,
          topStraightCapLength,
          bottomBortshaibaLengthForHeight,
          bottomStraightCapLength
        );
      }

      // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
      const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
      // При отражении системы подключения рисуются справа, иначе слева
      const connectionX = isSystemFlipped 
        ? startX + mainPipeSize / 2  // Правый край основной трубы
        : startX - mainPipeSize / 2; // Левый край основной трубы
      
      // Направление рисования: 1 для правых подключений (вправо), -1 для левых (влево)
      const direction = isSystemFlipped ? 1 : -1;
      
      // Защита от NaN значений
      if (isNaN(connectionY) || isNaN(connectionX)) return;
      
      // Определяем тип фитинга
      const isFlange = conn.fittingId.startsWith('Flanec_');
      const isFreeFlange = isFlange && !conn.fittingId.endsWith('_welded');
      const isNakGaika = conn.fittingId.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = conn.fittingId.startsWith('Shaib_Nar_Rezba_');
      
      // Получаем диаметр трубы под фитинг
      let pipeDiameterForFitting = fittingData.diameter;
      if (isFlange) {
        const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
        if (dnMatch) {
          const dn = parseInt(dnMatch[1]);
          // Используем функцию getPipeDiameterForDn для получения правильного диаметра трубы
          pipeDiameterForFitting = getPipeDiameterForDn(dn) || fittingData.diameter;
        }
      } else if (isNakGaika) {
        const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
        if (sizeMatch) {
          const NAK_GAIKA_TO_PIPE_DIAMETER = {
            '1': 21.3, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
          };
          pipeDiameterForFitting = NAK_GAIKA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
        }
      } else if (isShaibaNarRezba) {
        const sizeMatch = conn.fittingId.match(/Shaib_Nar_Rezba_(.+)/);
        if (sizeMatch) {
          const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
            '1': 26.9, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
          };
          pipeDiameterForFitting = SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
        }
      }
      
      // Используем обновленную функцию getFittingLength с поддержкой квадратных профилей
      let fittingLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
      
      if (isFlange) {
        // === ФЛАНЕЦ НА ПОДКЛЮЧЕНИИ ===
        const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
        const dn = dnMatch ? parseInt(dnMatch[1]) : 0;
        const flangeLength = fittingData.length;
        const flangeDiameter = fittingData.diameter;
        
        // Бортшайба для свободных фланцев
        let bortshaibaLength = 0;
        if (isFreeFlange) {
          const bortshaibaKey = `Bortshaiba_${dn}`;
          const bortshaibaInfo = fittingsData[bortshaibaKey];
          if (bortshaibaInfo) {
            bortshaibaLength = bortshaibaInfo.length || 10;
          }
        }
        
        const actualPipeLength = isFreeFlange
          ? Math.max(0, fittingLength - bortshaibaLength)
          : fittingLength;
        
        let currentX = connectionX;
        
        // 1. Добавляем трубку
        const circuitPipeId = `cyl-left-flange-${index}-${Date.now()}`;
        if (actualPipeLength > 0) {
          const pipeThickness = determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          const pipeX = direction > 0 ? connectionX : connectionX - actualPipeLength;
          cylinders.push({
            id: circuitPipeId,
            x: pipeX,
            y: connectionY - pipeDiameterForFitting / 2,
            width: actualPipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, pipeThickness, coating),
            insertionDepth: flangePipeInsertion(mainPipeDiameter, pipeDiameterForFitting, params.shape),
          });
          currentX = direction > 0 ? connectionX + actualPipeLength : connectionX - actualPipeLength;
        }
        
        // 2. Добавляем бортшайбу
        let bortshaibaId = null;
        if (isFreeFlange) {
          const bortshaibaKey = `Bortshaiba_${dn}`;
          const bortshaibaInfo = fittingsData[bortshaibaKey];
          if (bortshaibaInfo) {
            bortshaibaId = `fitting-left-bortshaiba-${index}-${Date.now()}`;
            fittings.push({
              id: bortshaibaId,
              key: bortshaibaKey,
              itemKey: bortshaibaKey,
              x: 0,
              y: 0,
              width: bortshaibaInfo.diameter,
              height: bortshaibaLength,
              snapPoints: bortshaibaInfo.snapPoints,
              correctionOffset: 3,
              useOriginalSize: true,
              groupX: currentX,
              groupY: connectionY,
              rotation: direction > 0 ? 90 : -90,
              offsetX: bortshaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
            currentX = direction > 0 ? currentX + bortshaibaLength : currentX - bortshaibaLength;
          }
        }
        
        // 3. Добавляем фланец
        const leftFittingId = `fitting-left-${index}-${Date.now()}`;
        const outerElementId = isFreeFlange && bortshaibaId ? bortshaibaId : circuitPipeId;
        const outerElementType = isFreeFlange && bortshaibaId ? 'fitting' : 'cylinder';
        leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
        
        const flangeX = direction > 0 
          ? connectionX + actualPipeLength - flangeLength
          : connectionX - actualPipeLength + flangeLength;
        
        fittings.push({
          id: leftFittingId,
          key: conn.fittingId,
          itemKey: conn.fittingId,
          x: 0,
          y: 0,
          width: flangeDiameter,
          height: flangeLength,
          groupX: flangeX,
          groupY: connectionY,
          rotation: -90,
          offsetX: flangeDiameter / 2,
          offsetY: flangeLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: isSystemFlipped ? 'right' : 'left', // IGES: направление
        });
      } else if (isNakGaika) {
        // === НАКИДНАЯ ГАЙКА НА ПОДКЛЮЧЕНИИ ===
        const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
        const size = sizeMatch ? sizeMatch[1] : '';
        const nakGaikaLength = fittingData.length;
        const nakGaikaDiameter = fittingData.diameter;
        
        const shaibaKey = `Shaib_Gaika_${size}`;
        const shaibaInfo = fittingsData[shaibaKey];
        const shaibaLength = shaibaInfo?.length || 10;
        
        const nakMeasure = nakShaibaPipeMeasure(fittingLength, shaibaLength, params.shape, false);
        const actualPipeLength = nakMeasure.outside;
        
        let currentX = connectionX;
        
        // 1. Добавляем трубку
        const circuitPipeId = `cyl-left-nakgaika-${index}-${Date.now()}`;
        if (actualPipeLength > 0) {
          const pipeThickness = 'x3';
          const pipeX = direction > 0 ? connectionX : connectionX - actualPipeLength;
          cylinders.push({
            id: circuitPipeId,
            x: pipeX,
            y: connectionY - pipeDiameterForFitting / 2,
            width: actualPipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, pipeThickness, coating),
            insertionDepth: nakMeasure.insertion,
          });
          currentX = direction > 0 ? connectionX + actualPipeLength : connectionX - actualPipeLength;
        }
        
        // 2. Добавляем шайбу накидной гайки
        let shaibaId = null;
        if (shaibaInfo) {
          shaibaId = `fitting-left-shaiba-${index}-${Date.now()}`;
          fittings.push({
            id: shaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: shaibaLength,
            groupX: currentX,
            groupY: connectionY,
            rotation: direction > 0 ? 90 : -90,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: shaibaLength,
          });
          currentX = direction > 0 ? currentX + shaibaLength : currentX - shaibaLength;
        }
        
        // 3. Добавляем накидную гайку
        const leftFittingId = `fitting-left-${index}-${Date.now()}`;
        const outerElementId = shaibaId || circuitPipeId;
        const outerElementType = shaibaId ? 'fitting' : 'cylinder';
        leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
        
        const nakGaikaX = direction > 0 
          ? connectionX + actualPipeLength - nakGaikaLength
          : connectionX - actualPipeLength + nakGaikaLength;
        
        // Логика для "Измененное значение" - накидные гайки (как для контуров)
        let dimensionOverride = {};
        if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === 40 || conn.fittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: true,
            overrideText: params.shape === 'square' ? '54' : '32',
            bortshaibaId: shaibaId,
          };
        }
        
        fittings.push({
          id: leftFittingId,
          key: conn.fittingId,
          itemKey: conn.fittingId,
          x: 0,
          y: 0,
          width: nakGaikaDiameter,
          height: nakGaikaLength,
          groupX: nakGaikaX,
          groupY: connectionY,
          rotation: direction > 0 ? 90 : -90,
          offsetX: nakGaikaDiameter / 2,
          offsetY: nakGaikaLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: isSystemFlipped ? 'right' : 'left', // IGES: направление
          ...dimensionOverride,
        });
      } else if (isShaibaNarRezba) {
        // === ШАЙБА С НАРУЖНОЙ РЕЗЬБОЙ НА ПОДКЛЮЧЕНИИ ===
        const shaibaLength = fittingData.length;
        const shaibaDiameter = fittingData.diameter;
        
        const shaibaMeasure = nakShaibaPipeMeasure(fittingLength, shaibaLength, params.shape, true);
        const actualPipeLength = shaibaMeasure.outside;
        
        let currentX = connectionX;
        
        // 1. Добавляем трубку
        const circuitPipeId = `cyl-left-shaiba-${index}-${Date.now()}`;
        if (actualPipeLength > 0) {
          const pipeThickness = 'x3';
          const pipeX = direction > 0 ? connectionX : connectionX - actualPipeLength;
          cylinders.push({
            id: circuitPipeId,
            x: pipeX,
            y: connectionY - pipeDiameterForFitting / 2,
            width: actualPipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, pipeThickness, coating),
            insertionDepth: shaibaMeasure.insertion,
          });
          currentX = direction > 0 ? connectionX + actualPipeLength : connectionX - actualPipeLength;
        }
        
        // 2. Добавляем шайбу с наружной резьбой
        const leftFittingId = `fitting-left-${index}-${Date.now()}`;
        leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId: leftFittingId, outerElementType: 'fitting' });
        
        // Логика для "Измененное значение" - шайбы с резьбой (как для контуров)
        let dimensionOverride = {};
        if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === 40 || conn.fittingLength === 58) {
          dimensionOverride = {
            overrideEnabled: true,
            overrideText: params.shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: leftFittingId,
          key: conn.fittingId,
          itemKey: conn.fittingId,
          x: 0,
          y: 0,
          width: shaibaDiameter,
          height: shaibaLength,
          groupX: currentX,
          groupY: connectionY,
          rotation: direction > 0 ? 90 : -90,
          offsetX: shaibaDiameter / 2,
          offsetY: shaibaLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: isSystemFlipped ? 'right' : 'left', // IGES: направление
          ...dimensionOverride,
        });
      } else {
        // Обычный фитинг (ниппель, муфта без промежуточной трубы)
        const leftFittingId = `fitting-left-${index}-${Date.now()}`;
        leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId: leftFittingId, outerElementType: 'fitting' });
        
        // Логика для "Измененное значение"
        const isNippel = conn.fittingId.startsWith('Nippel_');
        const isMufta = conn.fittingId.startsWith('Mufta_');
        let dimensionOverride = {};
        let actualFittingLength = fittingLength;

        if (isNippel || isMufta) {
          const resolved = resolveNippelMuftaFitting({
            fittingId: conn.fittingId,
            fittingLength: conn.fittingLength ?? null,
            pipeDiameter: mainPipeDiameter,
            catalogLength: fittingData.length,
            shape: params.shape,
          });
          actualFittingLength = resolved.length;
          dimensionOverride = resolved.dimensionOverride;
        }
        
        fittings.push({
          id: leftFittingId,
          key: conn.fittingId,
          itemKey: conn.fittingId,
          x: 0,
          y: 0,
          width: fittingData.diameter,
          height: actualFittingLength,
          groupX: connectionX,
          groupY: connectionY,
          rotation: direction > 0 ? 90 : -90,
          offsetX: fittingData.diameter / 2,
          offsetY: actualFittingLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(fittingData.diameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: isSystemFlipped ? 'right' : 'left', // IGES: направление отверстия
          ...dimensionOverride,
        });
      }
    });
  } // Конец условия !hasPreBoilerCollector
  
  // Проверка всех фитингов на NaN координаты
  fittings.forEach((fitting, index) => {
    if (isNaN(fitting.groupY) || isNaN(fitting.groupX) || isNaN(fitting.y) || isNaN(fitting.x)) {
      console.error(`Fitting ${index} has NaN coordinates:`, {
        id: fitting.id,
        key: fitting.key,
        x: fitting.x,
        y: fitting.y,
        groupX: fitting.groupX,
        groupY: fitting.groupY,
        width: fitting.width,
        height: fitting.height,
        rotation: fitting.rotation,
      });
    }
  });
  
  // Дополнительные выходы гидроразделителя
  additionalOutlets.forEach((outlet, index) => {
    const fittingData = fittingsData[outlet.fittingId];
    if (!fittingData) return;

    const side = outlet.side || 'both';
    
    // Для "left" и "right" используем горизонтальную ориентацию (как торцевые подключения)
    if (side === 'left' || side === 'right') {
      // === ГОРИЗОНТАЛЬНЫЕ ВЫХОДЫ (СЛЕВА/СПРАВА) ===
      
      // Вычисляем позицию Y (на расстоянии от внешнего края с учетом заглушек)
      let outletY;
      if (outlet.positionType === 'center') {
        // По середине гидроразделителя (по высоте)
        outletY = mainPipeTopY + actualMainPipeHeight / 2;
      } else {
        const isFromTop = outlet.distanceDirection === 'left';
        const distance = outlet.distanceFromEdge || 50;
        
        // Для расстояния всегда используем actualMainPipeHeight (высота гидроразделителя)
        const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        
        if (isFromTop) {
          outletY = topEdge + distance;
        } else {
          outletY = bottomEdge - distance;
        }
      }
      
      // Для квадратных труб используем mainPipeSize, для круглых - mainPipeDiameter
      const effectivePipeSize = params.shape === 'square' ? mainPipeSize : mainPipeDiameter;
      const connectionX = side === 'left' 
        ? startX - effectivePipeSize / 2
        : startX + effectivePipeSize / 2;
      
      let fittingLength = outlet.fittingLength !== null && outlet.fittingLength !== undefined 
        ? outlet.fittingLength 
        : fittingData.length;
      const pipeDiameter = fittingData.diameter;
      const connectionDiameter = getFittingConnectionDiameter(outlet.fittingId) || pipeDiameter;

      let dimensionOverride = {};
      const nippelMuftaResolved = resolveNippelMuftaFitting({
        fittingId: outlet.fittingId,
        fittingLength: outlet.fittingLength ?? null,
        pipeDiameter: mainPipeDiameter,
        catalogLength: fittingData.length,
        shape: params.shape,
      });
      if (nippelMuftaResolved) {
        fittingLength = nippelMuftaResolved.length;
        dimensionOverride = nippelMuftaResolved.dimensionOverride;
      }
      
      const fittingId = `fitting-outlet-${side}-${index}-${Date.now()}`;
      
      if (side === 'left') {
        fittings.push({
          id: fittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: connectionX,
          groupY: outletY,
          rotation: -90,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square', isThroughHole: side === 'both' }), // IGES: диаметр отверстия (особая логика для муфты 1/2": сквозные=24.5, обычные на квадратных=21)
          igesHoleDirection: 'left', // IGES: левый дополнительный выход
          isHydraulicSeparatorOutlet: true, // ФЛАГ: дополнительный выход гидроразделителя
          ...dimensionOverride,
        });
      } else {
        fittings.push({
          id: fittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: connectionX,
          groupY: outletY,
          rotation: 90,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square', isThroughHole: side === 'both' }), // IGES: диаметр отверстия (особая логика для муфты 1/2": сквозные=24.5, обычные на квадратных=21)
          igesHoleDirection: 'right', // IGES: правый дополнительный выход
          isHydraulicSeparatorOutlet: true, // ФЛАГ: дополнительный выход гидроразделителя
          ...dimensionOverride,
        });
      }
      
      const label = getFittingLabel(outlet.fittingId);
      const balloonX = side === 'left' 
        ? connectionX - fittingLength - 40 
        : connectionX + fittingLength + 40;
      
      // Смещаем Y от середины разделителя
      const balloonY = outletY < midSeparatorY 
        ? outletY - 60  // Если выше середины - сдвигаем вверх
        : outletY + 60; // Если ниже середины - сдвигаем вниз
      
      balloons.push({
        id: `balloon-outlet-${index}-${Date.now()}`,
        x: balloonX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ type: 'fitting', id: fittingId }],
      });
      
      // Размерная линия для длины фитинга (вылета)
      const dimLineY = outletY;
      const dimLineStartX = connectionX;
      const dimLineEndX = side === 'left' 
        ? connectionX - fittingLength 
        : connectionX + fittingLength;
      
      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: dimLineStartX,
        y1: dimLineY,
        x2: dimLineEndX,
        y2: dimLineY,
        position: { 
          x: (dimLineStartX + dimLineEndX) / 2, 
          y: dimLineY - 30 
        },
        value: String(Math.round(fittingLength)),
        rawValue: fittingLength,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: mainPipeId, objectType: 'cylinder', x: dimLineStartX, y: dimLineY, type: side === 'left' ? 'left' : 'right' },
          { objectId: fittingId, objectType: 'fitting', x: dimLineEndX, y: dimLineY, type: 'top' },
        ],
        ...dimensionOverride,
      });
      
      // Размерная линия для positionType='distance'
      if (outlet.positionType === 'distance') {
        const topDimY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomDimY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        const isFromTop = outlet.distanceDirection === 'left';
        
        // Размерная линия по центру гидроразделителя (как у вертикальных выходов)
        const dimLineX = startX;
        
        const topElement = topStraightCapIdForDimLine
          ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : topBortshaibaIdForDimLine
          ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY, type: 'top' };
        
        const bottomElement = bottomStraightCapIdForDimLine
          ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : bottomBortshaibaIdForDimLine
          ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
        
        const fittingElement = { 
          objectId: fittingId,
          objectType: 'fitting', 
          x: dimLineX, 
          y: outletY, 
          type: 'top'
        };
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimLineX,
          y1: isFromTop ? topDimY : outletY,
          x2: dimLineX,
          y2: isFromTop ? outletY : bottomDimY,
          position: { 
            x: dimLineX + 15, 
            y: isFromTop ? (topDimY + outletY) / 2 : (outletY + bottomDimY) / 2 
          },
          value: String(Math.round(outlet.distanceFromEdge || 50)),
          rawValue: outlet.distanceFromEdge || 50,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: isFromTop 
            ? [topElement, fittingElement]
            : [fittingElement, bottomElement],
        });
      }
      
    } else {
      // === ВЕРТИКАЛЬНЫЕ ВЫХОДЫ (СПЕРЕДИ/СЗАДИ/С 2-Х СТОРОН) - "НА СЕБЯ" ===
      
      let outletY;
      if (outlet.positionType === 'center') {
        // По середине гидроразделителя (по высоте)
        outletY = mainPipeTopY + actualMainPipeHeight / 2;
      } else {
        const isFromLeft = outlet.distanceDirection === 'left';
        const distance = outlet.distanceFromEdge || 50;
        
        // Для расстояния всегда используем actualMainPipeHeight (высота гидроразделителя)
        const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        
        if (isFromLeft) {
          outletY = topEdge + distance;
        } else {
          outletY = bottomEdge - distance;
        }
      }

      const outletX = startX;
      
      const useSelfFacing = fittingData.selfFacing && fittingData.selfFacing.diameter;
      const fittingLength = useSelfFacing ? fittingData.selfFacing.length : fittingData.length;
      const pipeDiameter = useSelfFacing ? fittingData.selfFacing.diameter : fittingData.diameter;
      const connectionDiameter = getFittingConnectionDiameter(outlet.fittingId) || fittingData.diameter;
      
      const fittingIds = [];
      
      if (side === 'both' || side === 'front') {
        const frontFittingId = `fitting-outlet-front-${index}-${Date.now()}`;
        fittingIds.push(frontFittingId);
        
        fittings.push({
          id: frontFittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: outletX,
          groupY: outletY,
          rotation: 0,
          isSelfFacing: true,
          isCenterFitting: true,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength / 2,
          side: 'front', // IGES: спереди (отверстие вдоль +Z)
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'front', // IGES: направление отверстия
          ...facingStickFields(outlet),
        });
      }
      
      if (side === 'both' || side === 'back') {
        const backFittingId = `fitting-outlet-back-${index}-${Date.now()}`;
        fittingIds.push(backFittingId);
        
        fittings.push({
          id: backFittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: outletX,
          groupY: outletY,
          rotation: 0,
          isSelfFacing: true,
          isCenterFitting: true,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength / 2,
          side: 'back', // IGES: сзади (отверстие вдоль -Z)
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'back', // IGES: направление отверстия
          ...facingStickFields(outlet),
        });
      }
      
      let label = getFittingLabel(outlet.fittingId);
      
      if (side === 'both') {
        label = label + '\n(c 2-х ст.)';
      } else if (side === 'back') {
        label = label + '\n(сзади)';
      }
      
      const targets = fittingIds.map(id => ({ type: 'fitting', id: id }));
      
      const isFromBottom = outlet.distanceDirection === 'right';
      const balloonY = (outlet.positionType === 'center' || isFromBottom) 
        ? outletY - 40
        : outletY + 40;
      
      balloons.push({
        id: `balloon-outlet-${index}-${Date.now()}`,
        x: startX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: targets,
      });
      
      if (outlet.positionType === 'distance') {
        const dimLineX = startX;
        const topDimY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomDimY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        const isFromLeft = outlet.distanceDirection === 'left';
        
        const topElement = topStraightCapIdForDimLine
          ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : topBortshaibaIdForDimLine
          ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY, type: 'top' };
        
        const bottomElement = bottomStraightCapIdForDimLine
          ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : bottomBortshaibaIdForDimLine
          ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
        
        const fittingElement = { 
          objectId: fittingIds[0],
          objectType: 'fitting', 
          x: dimLineX, 
          y: outletY, 
          type: 'left'
        };
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimLineX,
          y1: isFromLeft ? topDimY : outletY,
          x2: dimLineX,
          y2: isFromLeft ? outletY : bottomDimY,
          position: { 
            x: dimLineX + 30, 
            y: isFromLeft ? (topDimY + outletY) / 2 : (outletY + bottomDimY) / 2 
          },
          value: String(Math.round(outlet.distanceFromEdge || 50)),
          rawValue: outlet.distanceFromEdge || 50,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: isFromLeft 
            ? [topElement, fittingElement]
            : [fittingElement, bottomElement],
        });
      }
    }
  });
  
  // ========================================
  // ВСПОМОГАТЕЛЬНЫЕ ПЕРЕМЕННЫЕ ДЛЯ РАЗМЕРНЫХ ЛИНИЙ
  // ========================================
  
  // Вычисляем координаты для общей высоты и элементы привязки
  // (используются в размерных линиях левых подключений и общей высоты)
  const totalHeightTopY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
  const totalHeightBottomY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;

  // Определяем объекты для привязки размерной линии
  // Приоритет: прямая заглушка > бортшайба > основная труба
  const topDimElement = topStraightCapIdForDimLine
    ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: startX - mainPipeDiameter / 2, y: totalHeightTopY, type: 'top' }
    : topBortshaibaIdForDimLine
    ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: startX - mainPipeDiameter / 2, y: totalHeightTopY, type: 'top' }
    : { objectId: mainPipeId, objectType: 'cylinder', x: startX - mainPipeDiameter / 2, y: mainPipeTopY, type: 'top' };

  const bottomDimElement = bottomStraightCapIdForDimLine
    ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: startX - mainPipeDiameter / 2, y: totalHeightBottomY, type: 'top' }
    : bottomBortshaibaIdForDimLine
    ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: startX - mainPipeDiameter / 2, y: totalHeightBottomY, type: 'top' }
    : { objectId: mainPipeId, objectType: 'cylinder', x: startX - mainPipeDiameter / 2, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
  
  // ========================================
  // ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ для вычисления X позиции выхода (используется для дополнительных выходов)
  // ========================================
  
  const calculateOutletX = (outlet, startX, mainPipeLength, circuits, circuitSpacing, firstCircuitOffset, alternateCircuits, alternateCircuitSpacing, alternateFirstCircuitOffset, collectorType = 'supply') => {
    const positionType = outlet.positionType || 'center';
    
    // Определяем, какие контуры использовать на основе referenceRow
    const referenceRow = outlet.referenceRow || 'supply';
    
    // Для подающего коллектора: circuits=подача, alternateCircuits=обратка
    // Для обратного коллектора: circuits=обратка, alternateCircuits=подача
    const useAlternate = (collectorType === 'supply' && referenceRow === 'return') || 
                         (collectorType === 'return' && referenceRow === 'supply');
    
    const selectedCircuits = (useAlternate && alternateCircuits) ? alternateCircuits : circuits;
    const selectedSpacing = (useAlternate && alternateCircuitSpacing !== undefined) ? alternateCircuitSpacing : circuitSpacing;
    const selectedFirstOffset = (useAlternate && alternateFirstCircuitOffset !== undefined) ? alternateFirstCircuitOffset : firstCircuitOffset;
    
    switch (positionType) {
      case 'center': {
        // По середине гребенки
        return startX + mainPipeLength / 2;
      }
      
      case 'betweenCircuits': {
        // Между контурами
        const betweenIndex = Number(outlet.betweenIndex) || 0;
        const circuit1Index = betweenIndex;
        const circuit2Index = betweenIndex + 1;
        
        if (circuit1Index < selectedCircuits.length && circuit2Index < selectedCircuits.length) {
          // Вычисляем позиции контуров (как в betweenFittings)
          const circuitPositions = [];
          let currentX = selectedFirstOffset;
          selectedCircuits.forEach((circuit, i) => {
            const internalSpacing = Number(circuit.circuitSpacing) || 50;
            circuitPositions.push({
              start: startX + currentX,
              spacing: internalSpacing
            });
            
            // Переход к следующему контуру
            if (i < selectedCircuits.length - 1) {
              const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
              currentX += internalSpacing + spacingToNext;
            }
          });
          
          // Между контурами = между концом первого контура и началом второго контура
          const pos1End = circuitPositions[circuit1Index].start + circuitPositions[circuit1Index].spacing;  // конец первого контура
          const pos2Start = circuitPositions[circuit2Index].start;  // начало второго контура
          
          return (pos1End + pos2Start) / 2;
        }
        
        return startX + mainPipeLength / 2;
      }
      
      case 'betweenFittings': {
        // Между патрубками
        // betweenIndex - это индекс (0, 1, 2...), который обозначает:
        // 0 = между патрубком 1 и 2, 1 = между 2 и 3 и т.д.
        const betweenIndex = Number(outlet.betweenIndex) ?? 0;
        
        // Строим массив ВСЕХ позиций патрубков (как в схеме превью)
        // Сначала вычисляем позиции первых патрубков каждого контура
        const circuitPositions = [];
        let currentX = selectedFirstOffset;
        selectedCircuits.forEach((circuit, i) => {
          const internalSpacing = Number(circuit.circuitSpacing) || 50;
          circuitPositions.push({
            start: startX + currentX,
            spacing: internalSpacing
          });
          
          // Переход к следующему контуру
          if (i < selectedCircuits.length - 1) {
            const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
            currentX += internalSpacing + spacingToNext;  // внутреннее межосевое + расстояние между контурами
          }
        });
        
        // Теперь строим массив всех патрубков
        const allFittingPositions = [];
        circuitPositions.forEach(pos => {
          allFittingPositions.push(pos.start);                  // первый патрубок контура
          allFittingPositions.push(pos.start + pos.spacing);    // второй патрубок контура
        });
        
        // Берем позиции двух патрубков по индексу
        if (betweenIndex < allFittingPositions.length - 1) {
          const x1 = allFittingPositions[betweenIndex];
          const x2 = allFittingPositions[betweenIndex + 1];
          return (x1 + x2) / 2;
        }
        
        return startX + mainPipeLength / 2;
      }
      
      case 'distance': {
        // На расстоянии от края
        const distance = Number(outlet.distanceFromEdge) || 60;
        const direction = outlet.distanceDirection || 'left';
        
        if (direction === 'right') {
          return startX + mainPipeLength - distance;
        } else {
          return startX + distance;
        }
      }
      
      default:
        return startX + mainPipeLength / 2;
    }
  };
  
  // ========================================
  // 1.5. ПРЕДКОТЛОВОЙ КОЛЛЕКТОР (слева от гидроразделителя, если включен)
  // ========================================
  
  // Объявляем переменные для предкотлового коллектора (для доступа в metadata)
  let preBoilerSupplyPipeId;
  let preBoilerReturnPipeId;
  // IGES: позиции контуров предкотлового коллектора
  let preBoilerSupplyCircuitPositions = [];
  let preBoilerReturnCircuitPositions = [];
  
  if (hasPreBoilerCollector) {
    // Получаем профили для квадратных коллекторов из params
    const preBoilerSupplyPipeProfileValue = preBoilerSupplyPipeProfile || (params.shape === 'square' ? '80x80' : null);
    const preBoilerReturnPipeProfileValue = preBoilerReturnPipeProfile || (params.shape === 'square' ? '80x80' : null);
    
    // Вычисляем длину предкотлового коллектора (берем максимальную из подачи и обратки)
    let preBoilerSupplyLength = preBoilerSupplyFirstCircuitOffset + preBoilerSupplyLastCircuitOffset;
    
    preBoilerSupplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
      preBoilerSupplyLength += circuitSpacing;
      
      if (index < preBoilerSupplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerSupplySpacingBetweenCircuits || 125;
        preBoilerSupplyLength += spacingToNext;
      }
    });
    
    let preBoilerReturnLength = preBoilerReturnFirstCircuitOffset + preBoilerReturnLastCircuitOffset;
    
    preBoilerReturnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
      preBoilerReturnLength += circuitSpacing;
      
      if (index < preBoilerReturnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerReturnSpacingBetweenCircuits || 125;
        preBoilerReturnLength += spacingToNext;
      }
    });
    
    // Используем максимальную длину, если не указано иное
    const shouldPreBoilerEqualize = preBoilerEqualizeLengths !== false;
    const preBoilerCollectorLength = Math.max(preBoilerSupplyLength, preBoilerReturnLength, 150);
    
    // Фактические длины предкотловых коллекторов (могут отличаться если не приравниваем)
    const actualPreBoilerSupplyCollectorLength = shouldPreBoilerEqualize ? preBoilerCollectorLength : Math.max(preBoilerSupplyLength, 150);
    const actualPreBoilerReturnCollectorLength = shouldPreBoilerEqualize ? preBoilerCollectorLength : Math.max(preBoilerReturnLength, 150);
    
    // Предкотловые коллекторы заканчиваются ВПРИТЫК к разделителю (слева)
    const preBoilerEndX = startX - mainPipeSize / 2;
    
    // ========================================
    // 1.5.0a. ВЫЧИСЛЕНИЕ ДЛИН БОРТШАЙБ/ПРЯМЫХ ЗАГЛУШЕК ДЛЯ ЛЕВЫХ ПОДКЛЮЧЕНИЙ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
    // ========================================
    
    // Прямые заглушки для левых подключений предкотлового коллектора (если есть)
    const preBoilerSupplyLeftCapInfo = getCapLength({
      hasCap: hasPreBoilerSupplyLeftCap,
      capElementType: effectivePreBoilerSupplyLeftCapElementType,
      capType: effectivePreBoilerSupplyLeftCapType,
      reducerKey: preBoilerSupplyLeftReducer,
      diameter: preBoilerSupplyPipeDiameter,
      shape: params.shape || 'round',
      profile: params.preBoilerSupplyPipeProfile,
    });
    
    const preBoilerReturnLeftCapInfo = getCapLength({
      hasCap: hasPreBoilerReturnLeftCap,
      capElementType: effectivePreBoilerReturnLeftCapElementType,
      capType: effectivePreBoilerReturnLeftCapType,
      reducerKey: preBoilerReturnLeftReducer,
      diameter: preBoilerReturnPipeDiameter,
      shape: params.shape || 'round',
      profile: params.preBoilerReturnPipeProfile,
    });
    
    // Бортшайбы для левых подключений предкотлового коллектора
    let preBoilerSupplyLeftBortshaibaLength = 0;
    let preBoilerReturnLeftBortshaibaLength = 0;
    
    // Подающий предкотловой коллектор - левая борт-шайба
    if (hasPreBoilerSupplyLeftConnection && preBoilerSupplyLeftConnectionType === 'fitting' && preBoilerSupplyLeftConnectionFitting) {
      const isFlange = preBoilerSupplyLeftConnectionFitting.startsWith('Flanec_');
      const isFreeFlange = isFlange && !preBoilerSupplyLeftConnectionFitting.endsWith('_welded');
      const isNakGaika = preBoilerSupplyLeftConnectionFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = preBoilerSupplyLeftConnectionFitting.startsWith('Shaib_Nar_Rezba_');
      
      if ((isFreeFlange || isNakGaika) && !hasPreBoilerSupplyLeftCap) {
        // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
        const shaibaKey = getShaibaKeyForFitting(preBoilerSupplyLeftConnectionFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            preBoilerSupplyLeftBortshaibaLength = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba && !hasPreBoilerSupplyLeftCap) {
        // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
        const shaibaInfo = fittingsData[preBoilerSupplyLeftConnectionFitting];
        if (shaibaInfo) {
          preBoilerSupplyLeftBortshaibaLength = shaibaInfo.length;
        }
      }
    }
    
    // Обратный предкотловой коллектор - левая борт-шайба
    if (hasPreBoilerReturnLeftConnection && preBoilerReturnLeftConnectionType === 'fitting' && preBoilerReturnLeftConnectionFitting) {
      const isFlange = preBoilerReturnLeftConnectionFitting.startsWith('Flanec_');
      const isFreeFlange = isFlange && !preBoilerReturnLeftConnectionFitting.endsWith('_welded');
      const isNakGaika = preBoilerReturnLeftConnectionFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = preBoilerReturnLeftConnectionFitting.startsWith('Shaib_Nar_Rezba_');
      
      if ((isFreeFlange || isNakGaika) && !hasPreBoilerReturnLeftCap) {
        // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
        const shaibaKey = getShaibaKeyForFitting(preBoilerReturnLeftConnectionFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            preBoilerReturnLeftBortshaibaLength = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba && !hasPreBoilerReturnLeftCap) {
        // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
        const shaibaInfo = fittingsData[preBoilerReturnLeftConnectionFitting];
        if (shaibaInfo) {
          preBoilerReturnLeftBortshaibaLength = shaibaInfo.length;
        }
      }
    }
    
    // Реальная длина предкотлового коллектора = длина коллектора - прямые заглушки - бортшайбы
    const actualPreBoilerSupplyPipeLength = actualPreBoilerSupplyCollectorLength - preBoilerSupplyLeftCapInfo.straightLength - preBoilerSupplyLeftBortshaibaLength;
    const actualPreBoilerReturnPipeLength = actualPreBoilerReturnCollectorLength - preBoilerReturnLeftCapInfo.straightLength - preBoilerReturnLeftBortshaibaLength;
    
    // ========================================
    // 1.5.0b. ПОЗИЦИИ ПРЕДКОТЛОВЫХ КОЛЛЕКТОРОВ ПО ВЕРТИКАЛИ
    // ========================================
    
    // Вычисляем размеры труб предкотловых коллекторов (для квадратных - размер профиля, для круглых - диаметр)
    let preBoilerSupplyPipeSize = preBoilerSupplyPipeDiameter;
    let preBoilerReturnPipeSize = preBoilerReturnPipeDiameter;
    if (params.shape === 'square') {
      const preBoilerSupplyProfile = params.preBoilerSupplyPipeProfile || '80x80';
      const preBoilerReturnProfile = params.preBoilerReturnPipeProfile || '80x80';
      preBoilerSupplyPipeSize = parseFloat(preBoilerSupplyProfile.split('x')[0]);
      preBoilerReturnPipeSize = parseFloat(preBoilerReturnProfile.split('x')[0]);
    }
    
    // Позиции предкотловых коллекторов по вертикали (после расчета бортшайб)
    const preBoilerCenterY = mainPipeTopY + actualMainPipeHeight / 2;
    let preBoilerSupplyY, preBoilerReturnY;
    let actualPreBoilerPipeSpacing; // Фактическое межосевое расстояние для контуров
    
    if (preBoilerPipeSpacingMode === 'offset') {
      // Режим отступов: отступ от верха (с учетом заглушек/бортшайб) до ЦЕНТРА трубы
      // Отступ считается от верхней бортшайбы/заглушки (если диаметры одинаковые)
      const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
      const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
      
      preBoilerSupplyY = topEdge + preBoilerSupplyPipeOffset - preBoilerSupplyPipeSize / 2;
      preBoilerReturnY = bottomEdge - preBoilerReturnPipeOffset - preBoilerReturnPipeSize / 2;
      // Вычисляем фактическое межосевое расстояние (от центра до центра)
      actualPreBoilerPipeSpacing = (preBoilerReturnY + preBoilerReturnPipeSize / 2) - (preBoilerSupplyY + preBoilerSupplyPipeSize / 2);
    } else {
      // Режим межосевого расстояния (по умолчанию)
      preBoilerSupplyY = preBoilerCenterY - preBoilerPipeSpacing / 2 - preBoilerSupplyPipeSize / 2;
      preBoilerReturnY = preBoilerCenterY + preBoilerPipeSpacing / 2 - preBoilerReturnPipeSize / 2;
      actualPreBoilerPipeSpacing = preBoilerPipeSpacing;
    }
    
    // Подающий предкотловой коллектор
    preBoilerSupplyPipeId = `cyl-preboiler-supply-main-${Date.now()}`;
    // Для левых подключений: начало вычисляется от правого края (гидрострелки) минус длина коллектора
    // Труба должна заканчиваться у гидрострелки (preBoilerEndX), а начинаться левее
    const preBoilerSupplyStartX = preBoilerEndX - actualPreBoilerSupplyCollectorLength;
    const preBoilerSupplyPipeStartX = preBoilerSupplyStartX + preBoilerSupplyLeftCapInfo.straightLength + preBoilerSupplyLeftBortshaibaLength;
    const preBoilerSupplyThickness = determineThickness(mainPipeDiameter, preBoilerSupplyPipeDiameter, coating);
    
    // Получаем глубину вставки для круглых труб (для IGES экспорта)
    const preBoilerSupplyInsertionDepth = params.shape === 'round' ? getInsertionDepth(mainPipeDiameter, preBoilerSupplyPipeDiameter) : 0;
    
    // Создаем pipeSpec для подающего предкотлового коллектора с учетом формы
    let preBoilerSupplyPipeSpec;
    if (params.shape === 'square') {
      const profile = params.preBoilerSupplyPipeProfile || '80x80';
      const comboKey = `SQUARE_PROFILE|${profile}|`;
      const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
      let thickness = '2';
      let squareCoating = 'Матовый';
      if (matchingKey) {
        const parts = matchingKey.split('|');
        thickness = parts[2] || '2';
        squareCoating = parts[3] || 'Матовый';
      }
      preBoilerSupplyPipeSpec = {
        shape: 'square',
        profile: profile,
        thickness: thickness,
        coating: squareCoating,
        squareType: 'profile',
      };
    } else {
      preBoilerSupplyPipeSpec = createPipeSpec(preBoilerSupplyPipeDiameter, preBoilerSupplyThickness, coating);
    }
    
    cylinders.push({
      id: preBoilerSupplyPipeId,
      x: preBoilerSupplyPipeStartX,
      y: preBoilerSupplyY,
      width: actualPreBoilerSupplyPipeLength,
      height: preBoilerSupplyPipeSize,
      pipeSpec: preBoilerSupplyPipeSpec,
      insertionDepth: preBoilerSupplyInsertionDepth, // Для IGES экспорта
    });
    
    // Обратный предкотловой коллектор
    preBoilerReturnPipeId = `cyl-preboiler-return-main-${Date.now()}`;
    // Для левых подключений: начало вычисляется от правого края (гидрострелки) минус длина коллектора
    const preBoilerReturnStartX = preBoilerEndX - actualPreBoilerReturnCollectorLength;
    const preBoilerReturnPipeStartX = preBoilerReturnStartX + preBoilerReturnLeftCapInfo.straightLength + preBoilerReturnLeftBortshaibaLength;
    const preBoilerReturnThickness = determineThickness(mainPipeDiameter, preBoilerReturnPipeDiameter, coating);
    
    // Получаем глубину вставки для круглых труб (для IGES экспорта)
    const preBoilerReturnInsertionDepth = params.shape === 'round' ? getInsertionDepth(mainPipeDiameter, preBoilerReturnPipeDiameter) : 0;
    
    // Создаем pipeSpec для обратного предкотлового коллектора с учетом формы
    let preBoilerReturnPipeSpec;
    if (params.shape === 'square') {
      const profile = params.preBoilerReturnPipeProfile || '80x80';
      const comboKey = `SQUARE_PROFILE|${profile}|`;
      const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
      let thickness = '2';
      let squareCoating = 'Матовый';
      if (matchingKey) {
        const parts = matchingKey.split('|');
        thickness = parts[2] || '2';
        squareCoating = parts[3] || 'Матовый';
      }
      preBoilerReturnPipeSpec = {
        shape: 'square',
        profile: profile,
        thickness: thickness,
        coating: squareCoating,
        squareType: 'profile',
      };
    } else {
      preBoilerReturnPipeSpec = createPipeSpec(preBoilerReturnPipeDiameter, preBoilerReturnThickness, coating);
    }
    
    cylinders.push({
      id: preBoilerReturnPipeId,
      x: preBoilerReturnPipeStartX,
      y: preBoilerReturnY,
      width: actualPreBoilerReturnPipeLength,
      height: preBoilerReturnPipeSize,
      pipeSpec: preBoilerReturnPipeSpec,
      insertionDepth: preBoilerReturnInsertionDepth, // Для IGES экспорта
    });
    
    // ========================================
    // IGES: ФИКТИВНЫЕ ФИТИНГИ ДЛЯ ОТВЕРСТИЙ В ГИДРОРАЗДЕЛИТЕЛЕ (предкотловой коллектор)
    // ========================================
    // Создаём невидимые фитинги для отверстий в гидроразделителе, куда привариваются трубы предкотлового коллектора
    
    // Фиктивный фитинг для подающей трубы предкотлового коллектора
    const preBoilerSupplyJunctionY = preBoilerSupplyY + preBoilerSupplyPipeSize / 2; // Центр подающей трубы
    const preBoilerSupplyHoleDiameter = getHoleDiameterForPipe(preBoilerSupplyPipeDiameter, { squareProfile: params.shape === 'square' ? params.preBoilerSupplyPipeProfile : null });
    
    fittings.push({
      id: `fitting-junction-preboiler-supply-${Date.now()}`,
      key: 'junction-preboiler-supply',
      itemKey: 'junction-preboiler-supply',
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      groupX: preBoilerEndX, // Конец предкотлового коллектора = край гидроразделителя слева
      groupY: preBoilerSupplyJunctionY,
      rotation: 0,
      offsetX: 0,
      offsetY: 0,
      isHidden: true, // Не отображается на чертеже
      igesParentCylinderId: mainPipeId, // IGES: отверстие в гидроразделителе
      igesCreateHole: true, // IGES: создаёт отверстие
      holeDiameter: preBoilerSupplyHoleDiameter, // IGES: диаметр отверстия (для квадратных -10мм, для круглых -уменьшен для сварки)
      igesHoleDirection: 'left', // IGES: направление отверстия (предкотловой слева)
    });
    
    // Фиктивный фитинг для обратной трубы предкотлового коллектора
    const preBoilerReturnJunctionY = preBoilerReturnY + preBoilerReturnPipeSize / 2; // Центр обратной трубы
    const preBoilerReturnHoleDiameter = getHoleDiameterForPipe(preBoilerReturnPipeDiameter, { squareProfile: params.shape === 'square' ? params.preBoilerReturnPipeProfile : null });
    
    fittings.push({
      id: `fitting-junction-preboiler-return-${Date.now()}`,
      key: 'junction-preboiler-return',
      itemKey: 'junction-preboiler-return',
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      groupX: preBoilerEndX, // Конец предкотлового коллектора = край гидроразделителя слева
      groupY: preBoilerReturnJunctionY,
      rotation: 0,
      offsetX: 0,
      offsetY: 0,
      isHidden: true, // Не отображается на чертеже
      igesParentCylinderId: mainPipeId, // IGES: отверстие в гидроразделителе
      igesCreateHole: true, // IGES: создаёт отверстие
      holeDiameter: preBoilerReturnHoleDiameter, // IGES: диаметр отверстия (для квадратных -10мм, для круглых -уменьшен для сварки)
      igesHoleDirection: 'left', // IGES: направление отверстия (предкотловой слева)
    });
    
    // ========================================
    // 1.5a. ЛЕВЫЕ ЗАГЛУШКИ И ПЕРЕХОДЫ предкотловых коллекторов
    // ========================================
    
    // ID для левых заглушек/переходов (для позиций)
    let preBoilerSupplyLeftCapId = null;
    let preBoilerReturnLeftCapId = null;
    
    // Подающий коллектор - левая заглушка/переход
    if (hasPreBoilerSupplyLeftCap) {
      // Заглушка на краю реальной трубы (с учетом бортшайбы и прямой заглушки)
      const capX = preBoilerSupplyPipeStartX;
      const capY = preBoilerSupplyY + preBoilerSupplyPipeSize / 2;
      
      if (effectivePreBoilerSupplyLeftCapElementType === 'reducer') {
        const reducer = generateReducer({
          reducerKey: preBoilerSupplyLeftReducer,
          x: capX,
          y: capY,
          rotation: -90,
          side: 'preboiler-supply-left',
        });
        if (reducer) {
          fittings.push(reducer);
          preBoilerSupplyLeftCapId = reducer.id; // Сохраняем ID
        }
      } else {
        const cap = generateCap({
          capType: effectivePreBoilerSupplyLeftCapType,
          diameter: preBoilerSupplyPipeDiameter,
          x: capX,
          y: capY,
          rotation: -90,
          side: 'preboiler-supply-left',
          shape: params.shape || 'round',
          profile: params.preBoilerSupplyPipeProfile,
        });
        if (cap) {
          fittings.push(cap);
          preBoilerSupplyLeftCapId = cap.id; // Сохраняем ID
        }
      }
    }
    
    // Обратный коллектор - левая заглушка/переход
    if (hasPreBoilerReturnLeftCap) {
      // Заглушка на краю реальной трубы (с учетом бортшайбы и прямой заглушки)
      const capX = preBoilerReturnPipeStartX;
      const capY = preBoilerReturnY + preBoilerReturnPipeSize / 2;
      
      if (effectivePreBoilerReturnLeftCapElementType === 'reducer') {
        const reducer = generateReducer({
          reducerKey: preBoilerReturnLeftReducer,
          x: capX,
          y: capY,
          rotation: -90,
          side: 'preboiler-return-left',
        });
        if (reducer) {
          fittings.push(reducer);
          preBoilerReturnLeftCapId = reducer.id; // Сохраняем ID
        }
      } else {
        const cap = generateCap({
          capType: effectivePreBoilerReturnLeftCapType,
          diameter: preBoilerReturnPipeDiameter,
          x: capX,
          y: capY,
          rotation: -90,
          side: 'preboiler-return-left',
          shape: params.shape || 'round',
          profile: params.preBoilerReturnPipeProfile,
        });
        if (cap) {
          fittings.push(cap);
          preBoilerReturnLeftCapId = cap.id; // Сохраняем ID
        }
      }
    }
    
    // ========================================
    // 1.5b. ЛЕВЫЕ ТОРЦЕВЫЕ ПОДКЛЮЧЕНИЯ предкотловых коллекторов
    // ========================================
    
    // Переменные для хранения информации о левых торцевых подключениях (для позиций)
    let preBoilerSupplyLeftConnectionInfo = null;
    let preBoilerReturnLeftConnectionInfo = null;
    
    // Левое подключение подающего предкотлового коллектора
    if (hasPreBoilerSupplyLeftConnection && preBoilerSupplyLeftConnectionFitting) {
      // Используем preBoilerSupplyPipeStartX (край реальной трубы с учётом заглушек/бортшайб)
      let connectionStartX = preBoilerSupplyPipeStartX;
      if (hasPreBoilerSupplyLeftCap) {
        if (preBoilerSupplyLeftCapElementType === 'reducer' || preBoilerSupplyLeftCapType === 'elliptical') {
          connectionStartX = preBoilerSupplyPipeStartX - preBoilerSupplyLeftCapInfo.actualLength;
        }
      }
      
      const connectionY = preBoilerSupplyY + preBoilerSupplyPipeSize / 2;
      
      // Определяем какую длину использовать: для фланцев/гаек/шайб с заглушками используем connectionLength
      const isPreBoilerSupplyFlange = preBoilerSupplyLeftConnectionFitting.startsWith('Flanec_');
      const isPreBoilerSupplyNakGaika = preBoilerSupplyLeftConnectionFitting.startsWith('Nak_Gaika_');
      const isPreBoilerSupplyShaibaNarRezba = preBoilerSupplyLeftConnectionFitting.startsWith('Shaib_Nar_Rezba_');
      const isPreBoilerSupplyNippel = preBoilerSupplyLeftConnectionFitting.startsWith('Nippel_');
      const usePreBoilerSupplyConnectionLength = (isPreBoilerSupplyFlange || isPreBoilerSupplyNakGaika || isPreBoilerSupplyShaibaNarRezba) && hasPreBoilerSupplyLeftCap;
      
      // Применяем дефолты для квадратных профилей (торцевые соединения)
      let defaultPreBoilerSupplyConnectionLength;
      if (isPreBoilerSupplyNakGaika || isPreBoilerSupplyShaibaNarRezba) {
        // Для накидных гаек и шайб с резьбой (торцевые): 58мм (квадратные) / 40мм (круглые)
        defaultPreBoilerSupplyConnectionLength = params.shape === 'square' ? 58 : 40;
      } else if (isPreBoilerSupplyNippel && params.shape === 'square') {
        defaultPreBoilerSupplyConnectionLength = null;
      } else {
        defaultPreBoilerSupplyConnectionLength = null;
      }
      
      let effectivePreBoilerSupplyLength;
      if (usePreBoilerSupplyConnectionLength) {
        effectivePreBoilerSupplyLength = preBoilerSupplyLeftConnectionLength || 
          (defaultPreBoilerSupplyConnectionLength !== null ? defaultPreBoilerSupplyConnectionLength : 100);
      } else {
        effectivePreBoilerSupplyLength = preBoilerSupplyLeftConnectionFittingLength || defaultPreBoilerSupplyConnectionLength;
      }
      
      const leftSupplyResult = generateConnectionWithCaps({
        connectionType: preBoilerSupplyLeftConnectionType,
        fittingId: preBoilerSupplyLeftConnectionFitting,
        fittingLength: effectivePreBoilerSupplyLength,
        startX: connectionStartX,
        y: connectionY,
        direction: 'left',
        mainPipeDiameter: preBoilerSupplyPipeDiameter,
        mainPipeId: preBoilerSupplyPipeId,
        prefix: 'preboiler-supply-left',
        balloonYOffset: 0,
        hasCap: hasPreBoilerSupplyLeftCap,
        capElementType: effectivePreBoilerSupplyLeftCapElementType,
        capType: effectivePreBoilerSupplyLeftCapType,
        reducerKey: preBoilerSupplyLeftReducer,
        capLength: preBoilerSupplyLeftCapInfo.actualLength,
        shape: params.shape || 'round',
        profile: params.preBoilerSupplyPipeProfile,
      });
      
      if (leftSupplyResult) {
        if (leftSupplyResult.cylinders) cylinders.push(...leftSupplyResult.cylinders);
        if (leftSupplyResult.fittings) fittings.push(...leftSupplyResult.fittings);
        // Не добавляем balloons из generateConnectionWithCaps - добавим вручную позже
        
        // Сохраняем информацию о подключении
        preBoilerSupplyLeftConnectionInfo = {
          id: leftSupplyResult.mainElementId,
          type: preBoilerSupplyLeftConnectionType === 'pipe' ? 'cylinder' : 'fitting',
          fittingKey: preBoilerSupplyLeftConnectionFitting,
          length: effectivePreBoilerSupplyLength || leftSupplyResult.totalLength,
          startX: preBoilerSupplyPipeStartX, // Край гребенки (левый край реальной трубы)
          intermediatePipeId: leftSupplyResult.intermediatePipeId,
          bortshaibaId: leftSupplyResult.bortshaibaId,
          capId: preBoilerSupplyLeftCapId,
          capType: preBoilerSupplyLeftCapType, // 'straight' или 'elliptical'
          capLength: preBoilerSupplyLeftCapInfo.straightLength, // Длина прямой заглушки
          needsCapOrReducer: hasPreBoilerSupplyLeftCap,
        };
      }
    }
    
    // Левое подключение обратного предкотлового коллектора
    if (hasPreBoilerReturnLeftConnection && preBoilerReturnLeftConnectionFitting) {
      // Используем preBoilerReturnPipeStartX (край реальной трубы с учётом заглушек/бортшайб)
      let connectionStartX = preBoilerReturnPipeStartX;
      if (hasPreBoilerReturnLeftCap) {
        if (preBoilerReturnLeftCapElementType === 'reducer' || preBoilerReturnLeftCapType === 'elliptical') {
          connectionStartX = preBoilerReturnPipeStartX - preBoilerReturnLeftCapInfo.actualLength;
        }
      }
      
      const connectionY = preBoilerReturnY + preBoilerReturnPipeSize / 2;
      
      // Определяем какую длину использовать: для фланцев/гаек/шайб с заглушками используем connectionLength
      const isPreBoilerReturnFlange = preBoilerReturnLeftConnectionFitting.startsWith('Flanec_');
      const isPreBoilerReturnNakGaika = preBoilerReturnLeftConnectionFitting.startsWith('Nak_Gaika_');
      const isPreBoilerReturnShaibaNarRezba = preBoilerReturnLeftConnectionFitting.startsWith('Shaib_Nar_Rezba_');
      const isPreBoilerReturnNippel = preBoilerReturnLeftConnectionFitting.startsWith('Nippel_');
      const usePreBoilerReturnConnectionLength = (isPreBoilerReturnFlange || isPreBoilerReturnNakGaika || isPreBoilerReturnShaibaNarRezba) && hasPreBoilerReturnLeftCap;
      
      // Применяем дефолты для квадратных профилей (торцевые соединения)
      let defaultPreBoilerReturnConnectionLength;
      if (isPreBoilerReturnNakGaika || isPreBoilerReturnShaibaNarRezba) {
        // Для накидных гаек и шайб с резьбой (торцевые): 58мм (квадратные) / 40мм (круглые)
        defaultPreBoilerReturnConnectionLength = params.shape === 'square' ? 58 : 40;
      } else if (isPreBoilerReturnNippel && params.shape === 'square') {
        defaultPreBoilerReturnConnectionLength = null;
      } else {
        defaultPreBoilerReturnConnectionLength = null;
      }
      
      let effectivePreBoilerReturnLength;
      if (usePreBoilerReturnConnectionLength) {
        effectivePreBoilerReturnLength = preBoilerReturnLeftConnectionLength || 
          (defaultPreBoilerReturnConnectionLength !== null ? defaultPreBoilerReturnConnectionLength : 100);
      } else {
        effectivePreBoilerReturnLength = preBoilerReturnLeftConnectionFittingLength || defaultPreBoilerReturnConnectionLength;
      }
      
      const leftReturnResult = generateConnectionWithCaps({
        connectionType: preBoilerReturnLeftConnectionType,
        fittingId: preBoilerReturnLeftConnectionFitting,
        fittingLength: effectivePreBoilerReturnLength,
        startX: connectionStartX,
        y: connectionY,
        direction: 'left',
        mainPipeDiameter: preBoilerReturnPipeDiameter,
        mainPipeId: preBoilerReturnPipeId,
        prefix: 'preboiler-return-left',
        balloonYOffset: 0,
        hasCap: hasPreBoilerReturnLeftCap,
        capElementType: effectivePreBoilerReturnLeftCapElementType,
        capType: effectivePreBoilerReturnLeftCapType,
        reducerKey: preBoilerReturnLeftReducer,
        capLength: preBoilerReturnLeftCapInfo.actualLength,
        shape: params.shape || 'round',
        profile: params.preBoilerReturnPipeProfile,
      });
      
      if (leftReturnResult) {
        if (leftReturnResult.cylinders) cylinders.push(...leftReturnResult.cylinders);
        if (leftReturnResult.fittings) fittings.push(...leftReturnResult.fittings);
        // Не добавляем balloons из generateConnectionWithCaps - добавим вручную позже
        
        // Сохраняем информацию о подключении
        preBoilerReturnLeftConnectionInfo = {
          id: leftReturnResult.mainElementId,
          type: preBoilerReturnLeftConnectionType === 'pipe' ? 'cylinder' : 'fitting',
          fittingKey: preBoilerReturnLeftConnectionFitting,
          length: effectivePreBoilerReturnLength || leftReturnResult.totalLength,
          startX: preBoilerReturnPipeStartX, // Край гребенки (левый край реальной трубы)
          intermediatePipeId: leftReturnResult.intermediatePipeId,
          bortshaibaId: leftReturnResult.bortshaibaId,
          capId: preBoilerReturnLeftCapId,
          capType: preBoilerReturnLeftCapType, // 'straight' или 'elliptical'
          capLength: preBoilerReturnLeftCapInfo.straightLength, // Длина прямой заглушки
          needsCapOrReducer: hasPreBoilerReturnLeftCap,
        };
      }
    }
    
    // ========================================
    // 1.5c. ПРАВЫЕ ПОДКЛЮЧЕНИЯ к гидроразделителю (предкотловые коллекторы)
    // ========================================
    
    // Соединяем правые концы предкотловых коллекторов с левыми подключениями гидроразделителя
    
    // Подающий предкотловой коллектор соединяется с левым верхним подключением гидроразделителя
    const preBoilerSupplyConnectionY = preBoilerSupplyY + preBoilerSupplyPipeSize / 2;
    
    // Горизонтальная труба от конца предкотлового подающего коллектора до левой стороны разделителя
    const preBoilerSupplyConnectorId = `cyl-preboiler-supply-connector-${Date.now()}`;
    const preBoilerSupplyConnectorThickness = determineThickness(mainPipeDiameter, preBoilerSupplyPipeDiameter, coating);
    
    // Создаем pipeSpec для коннектора с учетом формы
    let preBoilerSupplyConnectorPipeSpec;
    if (params.shape === 'square') {
      const profile = params.preBoilerSupplyPipeProfile || '80x80';
      const comboKey = `SQUARE_PROFILE|${profile}|`;
      const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
      let thickness = '2';
      let squareCoating = 'Матовый';
      if (matchingKey) {
        const parts = matchingKey.split('|');
        thickness = parts[2] || '2';
        squareCoating = parts[3] || 'Матовый';
      }
      preBoilerSupplyConnectorPipeSpec = {
        shape: 'square',
        profile: profile,
        thickness: thickness,
        coating: squareCoating,
        squareType: 'profile',
      };
    } else {
      preBoilerSupplyConnectorPipeSpec = createPipeSpec(preBoilerSupplyPipeDiameter, preBoilerSupplyConnectorThickness, coating);
    }
    
    cylinders.push({
      id: preBoilerSupplyConnectorId,
      x: preBoilerEndX,
      y: preBoilerSupplyConnectionY - preBoilerSupplyPipeSize / 2,
      width: startX - preBoilerEndX - mainPipeSize / 2,
      height: preBoilerSupplyPipeSize,
      pipeSpec: preBoilerSupplyConnectorPipeSpec,
      isHidden: true, // Не учитывается в стоимости (соединительная труба для IGES)
    });
    
    // Обратный предкотловой коллектор соединяется с левым нижним подключением гидроразделителя
    const preBoilerReturnConnectionY = preBoilerReturnY + preBoilerReturnPipeSize / 2;
    
    // Горизонтальная труба от конца предкотлового обратного коллектора до левой стороны разделителя
    const preBoilerReturnConnectorId = `cyl-preboiler-return-connector-${Date.now()}`;
    const preBoilerReturnConnectorThickness = determineThickness(mainPipeDiameter, preBoilerReturnPipeDiameter, coating);
    
    // Создаем pipeSpec для коннектора с учетом формы
    let preBoilerReturnConnectorPipeSpec;
    if (params.shape === 'square') {
      const profile = params.preBoilerReturnPipeProfile || '80x80';
      const comboKey = `SQUARE_PROFILE|${profile}|`;
      const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
      let thickness = '2';
      let squareCoating = 'Матовый';
      if (matchingKey) {
        const parts = matchingKey.split('|');
        thickness = parts[2] || '2';
        squareCoating = parts[3] || 'Матовый';
      }
      preBoilerReturnConnectorPipeSpec = {
        shape: 'square',
        profile: profile,
        thickness: thickness,
        coating: squareCoating,
        squareType: 'profile',
      };
    } else {
      preBoilerReturnConnectorPipeSpec = createPipeSpec(preBoilerReturnPipeDiameter, preBoilerReturnConnectorThickness, coating);
    }
    
    cylinders.push({
      id: preBoilerReturnConnectorId,
      x: preBoilerEndX,
      y: preBoilerReturnConnectionY - preBoilerReturnPipeSize / 2,
      width: startX - preBoilerEndX - mainPipeSize / 2,
      height: preBoilerReturnPipeSize,
      pipeSpec: preBoilerReturnConnectorPipeSpec,
      isHidden: true, // Не учитывается в стоимости (соединительная труба для IGES)
    });
    
    // ========================================
    // 1.5d. КОНТУРЫ предкотловых коллекторов
    // ========================================
    
    // Генерируем парные контуры предкотловых коллекторов
    const preBoilerCircuitsResult = generatePairedCircuitsFull({
      supplyCircuits: preBoilerSupplyCircuits,
      returnCircuits: preBoilerReturnCircuits,
      supplyStartX: preBoilerSupplyStartX,
      returnStartX: preBoilerReturnStartX,
      supplyY: preBoilerSupplyY,
      returnY: preBoilerReturnY,
      supplyPipeId: preBoilerSupplyPipeId,
      returnPipeId: preBoilerReturnPipeId,
      supplyPipeDiameter: preBoilerSupplyPipeSize,
      returnPipeDiameter: preBoilerReturnPipeSize,
      supplyFirstCircuitOffset: preBoilerSupplyFirstCircuitOffset,
      returnFirstCircuitOffset: preBoilerReturnFirstCircuitOffset,
      supplySpacingBetweenCircuits: params.preBoilerSupplySpacingBetweenCircuits || 125,
      returnSpacingBetweenCircuits: params.preBoilerReturnSpacingBetweenCircuits || 125,
      pipeSpacing: actualPreBoilerPipeSpacing,
      shape: params.shape || 'round',
    });
    
    if (preBoilerCircuitsResult) {
      if (preBoilerCircuitsResult.cylinders) cylinders.push(...preBoilerCircuitsResult.cylinders);
      if (preBoilerCircuitsResult.fittings) {
        // IGES: Настройка параметров для контурных фитингов предкотлового коллектора
        // Получаем информацию о длинных/коротких патрубках из circuitObjects
        const supplyCircuitPositions = (preBoilerCircuitsResult.supplyCircuitObjects || []).map(obj => ({
          x: obj.centerX || obj.x,
          direction: 'up',
          isLong: !obj.isShort,
        }));
        const returnCircuitPositions = (preBoilerCircuitsResult.returnCircuitObjects || []).map(obj => ({
          x: obj.centerX || obj.x,
          direction: 'down',
          isLong: !obj.isShort,
        }));
        
        // Сохраняем позиции контуров для метаданных IGES
        preBoilerSupplyCircuitPositions = supplyCircuitPositions;
        preBoilerReturnCircuitPositions = returnCircuitPositions;
        
        preBoilerCircuitsResult.fittings.forEach(fitting => {
          // Определяем, к какой трубе относится фитинг по его ID
          const isSupply = fitting.id && (
            fitting.id.includes('preboiler-supply') || 
            fitting.id.includes(`circuit-${preBoilerSupplyPipeId}`)
          );
          const isReturn = fitting.id && (
            fitting.id.includes('preboiler-return') || 
            fitting.id.includes(`circuit-${preBoilerReturnPipeId}`)
          );
          
          if (isSupply || isReturn) {
            const parentPipeId = isSupply ? preBoilerSupplyPipeId : preBoilerReturnPipeId;
            const pairedPipeId = isSupply ? preBoilerReturnPipeId : preBoilerSupplyPipeId;
            const direction = isSupply ? 'up' : 'down';
            const circuitPositions = isSupply ? supplyCircuitPositions : returnCircuitPositions;
            
            // Находим соответствующую позицию для определения isLong
            const fittingX = fitting.groupX || fitting.x;
            const matchingPos = circuitPositions.find(pos => Math.abs(pos.x - fittingX) < 10);
            const isLong = matchingPos ? matchingPos.isLong : false;
            
            // Устанавливаем IGES параметры
            fitting.igesParentCylinderId = parentPipeId;
            fitting.igesCreateHole = true;
            fitting.holeDiameter = fitting.diameter || preBoilerSupplyPipeDiameter; // диаметр фитинга
            fitting.igesHoleDirection = direction;
            
            // Для длинных патрубков добавляем информацию о парной трубе
            if (isLong) {
              fitting.isLongCircuit = true;
              fitting.pairedCylinderId = pairedPipeId;
            }
          }
        });
        
        fittings.push(...preBoilerCircuitsResult.fittings);
      }
      if (preBoilerCircuitsResult.welds) welds.push(...preBoilerCircuitsResult.welds);
    }
    
    // Сохраняем информацию о контурах предкотловых коллекторов для размерных линий
    // eslint-disable-next-line no-unused-vars
    const preBoilerSupplyCircuitObjects = preBoilerCircuitsResult?.supplyCircuitObjects || [];
    // eslint-disable-next-line no-unused-vars
    const preBoilerReturnCircuitObjects = preBoilerCircuitsResult?.returnCircuitObjects || [];
    
    // ========================================
    // 1.5e. ЦЕНТРАЛЬНЫЕ ФИТИНГИ предкотловых коллекторов
    // ========================================
    
    const preBoilerSupplyCenterY = preBoilerSupplyY + preBoilerSupplyPipeDiameter / 2;
    const preBoilerReturnCenterY = preBoilerReturnY + preBoilerReturnPipeDiameter / 2;
    
    // Центральные фитинги подающего предкотлового коллектора
    const preBoilerSupplyCenterFittingsResult = generateCenterFittings({
      centerFittings: preBoilerSupplyCenterFittings,
      mainPipeId: preBoilerSupplyPipeId,
      mainPipeDiameter: preBoilerSupplyPipeDiameter,
      mainCenterY: preBoilerSupplyCenterY,
      mainPipeStartX: preBoilerSupplyStartX,
      mainPipeLength: actualPreBoilerSupplyCollectorLength,
      startX: preBoilerSupplyStartX,
      circuits: preBoilerSupplyCircuits,  // Передаем исходные данные контуров подачи
      bottomCircuits: preBoilerReturnCircuits,  // Передаем контуры обратки для возможности переключения
      circuitSpacing: params.preBoilerSupplySpacingBetweenCircuits || 125,
      firstCircuitOffset: preBoilerSupplyFirstCircuitOffset,
      bottomFirstCircuitOffset: preBoilerReturnFirstCircuitOffset,
      bottomCircuitSpacing: params.preBoilerReturnSpacingBetweenCircuits || 125,
      collectorType: 'supply',  // Тип коллектора
      pipeType: 'preboiler-supply',
      shape: params.shape, // Передаем форму коллектора (round/square)
    });
    
    // Сохраняем объекты центральных фитингов для balloons
    const preBoilerSupplyCenterFittingObjects = [];
    
    if (preBoilerSupplyCenterFittingsResult) {
      if (preBoilerSupplyCenterFittingsResult.fittings) {
        fittings.push(...preBoilerSupplyCenterFittingsResult.fittings);
      }
      if (preBoilerSupplyCenterFittingsResult.centerFittingObjects) {
        preBoilerSupplyCenterFittingObjects.push(...preBoilerSupplyCenterFittingsResult.centerFittingObjects);
      }
      if (preBoilerSupplyCenterFittingsResult.welds) welds.push(...preBoilerSupplyCenterFittingsResult.welds);
    }
    
    // Центральные фитинги обратного предкотлового коллектора
    const preBoilerReturnCenterFittingsResult = generateCenterFittings({
      centerFittings: preBoilerReturnCenterFittings,
      mainPipeId: preBoilerReturnPipeId,
      mainPipeDiameter: preBoilerReturnPipeDiameter,
      mainCenterY: preBoilerReturnCenterY,
      mainPipeStartX: preBoilerReturnStartX,
      mainPipeLength: actualPreBoilerReturnCollectorLength,
      startX: preBoilerReturnStartX,
      circuits: preBoilerReturnCircuits,  // Передаем исходные данные контуров обратки
      bottomCircuits: preBoilerSupplyCircuits,  // Передаем контуры подачи для возможности переключения
      circuitSpacing: params.preBoilerReturnSpacingBetweenCircuits || 125,
      firstCircuitOffset: preBoilerReturnFirstCircuitOffset,
      bottomFirstCircuitOffset: preBoilerSupplyFirstCircuitOffset,
      bottomCircuitSpacing: params.preBoilerSupplySpacingBetweenCircuits || 125,
      collectorType: 'return',  // Тип коллектора
      pipeType: 'preboiler-return',
      shape: params.shape, // Передаем форму коллектора (round/square)
    });
    
    // Сохраняем объекты центральных фитингов для balloons
    const preBoilerReturnCenterFittingObjects = [];
    
    if (preBoilerReturnCenterFittingsResult) {
      if (preBoilerReturnCenterFittingsResult.fittings) {
        fittings.push(...preBoilerReturnCenterFittingsResult.fittings);
      }
      if (preBoilerReturnCenterFittingsResult.centerFittingObjects) {
        preBoilerReturnCenterFittingObjects.push(...preBoilerReturnCenterFittingsResult.centerFittingObjects);
      }
      if (preBoilerReturnCenterFittingsResult.welds) welds.push(...preBoilerReturnCenterFittingsResult.welds);
    }
    
    // ========================================
    // 1.5f. ДОПОЛНИТЕЛЬНЫЕ ВЫХОДЫ предкотловых коллекторов
    // ========================================
    
    // Генерируем дополнительные выходы для подающего предкотлового коллектора
    const preBoilerSupplyAdditionalOutletObjects = [];
    if (preBoilerSupplyAdditionalOutlets && preBoilerSupplyAdditionalOutlets.length > 0) {
      preBoilerSupplyAdditionalOutlets.forEach((outlet) => {
        const outletDirection = outlet.direction || 'up';
        const outletType = outlet.outletType || 'short'; // 'short' или 'through'
        
        // Вычисляем X позицию выхода на основе типа размещения
        const outletX = calculateOutletX(
          outlet, 
          preBoilerSupplyStartX, 
          actualPreBoilerSupplyCollectorLength, 
          preBoilerSupplyCircuits, 
          params.preBoilerSupplySpacingBetweenCircuits || 125, 
          preBoilerSupplyFirstCircuitOffset,
          preBoilerReturnCircuits,
          params.preBoilerReturnSpacingBetweenCircuits || 125,
          preBoilerReturnFirstCircuitOffset,
          'supply'
        );
        
        const result = generateCircuits({
          circuits: [outlet],
          startX: outletX,
          startY: preBoilerSupplyY,
          mainPipeId: preBoilerSupplyPipeId,
          mainPipeDiameter: preBoilerSupplyPipeDiameter,
          mainPipeProfile: preBoilerSupplyPipeProfileValue,
          pipeSpec: preBoilerSupplyPipeSpec,
          firstCircuitOffset: 0,
          circuitSpacing: 0,
          direction: outletDirection,
          shape,
          isAdditionalOutlet: true, // Флаг что это дополнительный выход
          outletType: outletType, // Тип выхода
          pipeSpacing: actualPreBoilerPipeSpacing, // Фактическое межосевое расстояние между коллекторами предкотлового
        });
        
        if (result) {
          if (result.cylinders) cylinders.push(...result.cylinders);
          if (result.fittings) fittings.push(...result.fittings);
          if (result.welds) welds.push(...result.welds);
          if (result.circuitObjects) preBoilerSupplyAdditionalOutletObjects.push(...result.circuitObjects);
        }
      });
    }
    
    // Генерируем дополнительные выходы для обратного предкотлового коллектора
    const preBoilerReturnAdditionalOutletObjects = [];
    if (preBoilerReturnAdditionalOutlets && preBoilerReturnAdditionalOutlets.length > 0) {
      preBoilerReturnAdditionalOutlets.forEach((outlet) => {
        const outletDirection = outlet.direction || 'down';
        const outletType = outlet.outletType || 'short'; // 'short' или 'through'
        
        // Вычисляем X позицию выхода на основе типа размещения
        const outletX = calculateOutletX(
          outlet, 
          preBoilerReturnStartX, 
          actualPreBoilerReturnCollectorLength, 
          preBoilerReturnCircuits, 
          params.preBoilerReturnSpacingBetweenCircuits || 125, 
          preBoilerReturnFirstCircuitOffset,
          preBoilerSupplyCircuits,
          params.preBoilerSupplySpacingBetweenCircuits || 125,
          preBoilerSupplyFirstCircuitOffset,
          'return'
        );
        
        const result = generateCircuits({
          circuits: [outlet],
          startX: outletX,
          startY: preBoilerReturnY,
          mainPipeId: preBoilerReturnPipeId,
          mainPipeDiameter: preBoilerReturnPipeDiameter,
          mainPipeProfile: preBoilerReturnPipeProfileValue,
          pipeSpec: preBoilerReturnPipeSpec,
          firstCircuitOffset: 0,
          circuitSpacing: 0,
          direction: outletDirection,
          shape,
          isAdditionalOutlet: true, // Флаг что это дополнительный выход
          outletType: outletType, // Тип выхода
          pipeSpacing: actualPreBoilerPipeSpacing, // Фактическое межосевое расстояние между коллекторами предкотлового
        });
        
        if (result) {
          if (result.cylinders) cylinders.push(...result.cylinders);
          if (result.fittings) fittings.push(...result.fittings);
          if (result.welds) welds.push(...result.welds);
          if (result.circuitObjects) preBoilerReturnAdditionalOutletObjects.push(...result.circuitObjects);
        }
      });
    }
    
    // ========================================
    // 1.5g. РАЗМЕРНЫЕ ЛИНИИ ДЛЯ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
    // ========================================
    
    // Генерируем размерные линии только если коллектор имеет валидную длину
    // Используем минимальный startX (самый левый край) для позиционирования
    const preBoilerMinStartX = Math.min(preBoilerSupplyStartX, preBoilerReturnStartX);
    
    if (preBoilerCollectorLength > 0 && preBoilerMinStartX !== undefined && !isNaN(preBoilerMinStartX)) {
      const dimParams = {
        startX: preBoilerMinStartX,
        supplyY: preBoilerSupplyY,
        returnY: preBoilerReturnY,
        supplyPipeId: preBoilerSupplyPipeId,
        returnPipeId: preBoilerReturnPipeId,
        supplyPipeDiameter: preBoilerSupplyPipeDiameter,
        returnPipeDiameter: preBoilerReturnPipeDiameter,
        supplyPipeLength: actualPreBoilerSupplyCollectorLength,
        returnPipeLength: actualPreBoilerReturnCollectorLength,
        supplyCircuitObjects: preBoilerSupplyCircuitObjects,
        returnCircuitObjects: preBoilerReturnCircuitObjects,
        supplyFirstCircuitOffset: preBoilerSupplyFirstCircuitOffset,
        supplyLastCircuitOffset: preBoilerSupplyLastCircuitOffset,
        returnFirstCircuitOffset: preBoilerReturnFirstCircuitOffset,
        returnLastCircuitOffset: preBoilerReturnLastCircuitOffset,
        supplyCircuits: preBoilerSupplyCircuits,
        returnCircuits: preBoilerReturnCircuits,
        dimOffset: 40,
        // ID заглушек/переходов (для размерных линий контуров)
        supplyLeftCapId: preBoilerSupplyLeftCapId,
        returnLeftCapId: preBoilerReturnLeftCapId,
        // Длины прямых заглушек
        supplyLeftStraightCapLength: preBoilerSupplyLeftCapInfo.straightLength,
        returnLeftStraightCapLength: preBoilerReturnLeftCapInfo.straightLength,
        // Длины бортшайб (для размерных линий и отступов)
        supplyLeftBortshaibaLength: preBoilerSupplyLeftBortshaibaLength,
        returnLeftBortshaibaLength: preBoilerReturnLeftBortshaibaLength,
        // Фактические позиции и длины гребёнок
        supplyPipeStartX: preBoilerSupplyPipeStartX,
        returnPipeStartX: preBoilerReturnPipeStartX,
        actualSupplyPipeLength: actualPreBoilerSupplyPipeLength,
        actualReturnPipeLength: actualPreBoilerReturnPipeLength,
        // Информация о левых торцевых подключениях
        supplyLeftConnectionInfo: preBoilerSupplyLeftConnectionInfo,
        returnLeftConnectionInfo: preBoilerReturnLeftConnectionInfo,
        // Центральные фитинги (для размерных линий)
        supplyCenterFittingObjects: preBoilerSupplyCenterFittingObjects,
        returnCenterFittingObjects: preBoilerReturnCenterFittingObjects,
        // Дополнительные выходы (для размерных линий)
        supplyAdditionalOutletObjects: preBoilerSupplyAdditionalOutletObjects,
        returnAdditionalOutletObjects: preBoilerReturnAdditionalOutletObjects,
        supplyAdditionalOutlets: preBoilerSupplyAdditionalOutlets,
        returnAdditionalOutlets: preBoilerReturnAdditionalOutlets,
        // Индивидуальные начальные позиции для разных длин
        supplyStartX: preBoilerSupplyStartX,
        returnStartX: preBoilerReturnStartX,
        // Форма и профили (для квадратных коллекторов)
        shape: params.shape || 'round',
        supplyPipeProfile: params.preBoilerSupplyPipeProfile,
        returnPipeProfile: params.preBoilerReturnPipeProfile,
        supplyPipeSpec: preBoilerSupplyPipeSpec,
        returnPipeSpec: preBoilerReturnPipeSpec,
        pipeSpacing: preBoilerPipeSpacing,
      };
      
      const preBoilerDimensions = generateDistributionCollectorDimensions(dimParams);
      
      // Фильтруем межосевую линию (аналогично обычному коллектору)
      const filteredPreBoilerDimensions = preBoilerDimensions.filter(dim => {
        // Фильтруем невалидные координаты
        const hasValidCoords = 
          dim.x1 !== undefined && !isNaN(dim.x1) &&
          dim.x2 !== undefined && !isNaN(dim.x2) &&
          dim.y1 !== undefined && !isNaN(dim.y1) &&
          dim.y2 !== undefined && !isNaN(dim.y2);
        
        if (!hasValidCoords) {
          // Пропускаем невалидные размерные линии (обычно это размерные линии вылетов контуров с некорректными данными)
          return false;
        }
        
        // Убираем межосевую линию (вертикальная линия между центрами коллекторов)
        if (!dim.isHorizontal && dim.angle === 90 && 
            dim.x1 === preBoilerMinStartX && dim.x2 === preBoilerMinStartX &&
            dim.elements && dim.elements.length === 2) {
          const connectsCenters = 
            Math.abs(dim.y1 - preBoilerSupplyCenterY) < 1 && 
            Math.abs(dim.y2 - preBoilerReturnCenterY) < 1 &&
            dim.elements.every(el => el.objectType === 'cylinder');
          
          if (connectsCenters) {
            return false; // Убираем межосевую линию
          }
        }
        
        return true;
      });
      
      // Находим минимальное Y среди всех размерных линий предкотлового коллектора
      let minPreBoilerDimY = preBoilerSupplyY - preBoilerSupplyPipeDiameter / 2 - 80;
      
      filteredPreBoilerDimensions.forEach(dim => {
        // Пропускаем общую размерную линию длины коллектора (проверяем обе трубы)
        const isSupplyTotalLine = dim.isHorizontal && 
            Math.abs(dim.x1 - preBoilerSupplyStartX) < 1 && 
            Math.abs(dim.x2 - (preBoilerSupplyStartX + actualPreBoilerSupplyCollectorLength)) < 1;
        const isReturnTotalLine = dim.isHorizontal && 
            Math.abs(dim.x1 - preBoilerReturnStartX) < 1 && 
            Math.abs(dim.x2 - (preBoilerReturnStartX + actualPreBoilerReturnCollectorLength)) < 1;
        const isTotalLengthLine = isSupplyTotalLine || isReturnTotalLine;
        
        if (!isTotalLengthLine) {
          if (dim.isHorizontal) {
            minPreBoilerDimY = Math.min(minPreBoilerDimY, dim.y1, dim.y2);
            if (dim.position && typeof dim.position.y === 'number') {
              minPreBoilerDimY = Math.min(minPreBoilerDimY, dim.position.y);
            }
          }
        }
      });
      
      // Устанавливаем общую размерную линию выше всех на 40мм
      const preBoilerTotalLengthLineY = minPreBoilerDimY - 40;
      // Сохраняем для использования в размерной линии диаметра гидроразделителя
      totalLengthLineY = preBoilerTotalLengthLineY;
      
      // Корректируем размерные линии предкотлового коллектора
      const adjustedPreBoilerDimensions = filteredPreBoilerDimensions.map(dim => {
        // Перемещаем размерную линию диаметра обратного коллектора влево (на левый край)
        if (!dim.isHorizontal && dim.angle === 90 &&
            Math.abs(dim.x1 - preBoilerReturnStartX) < 1 && Math.abs(dim.x2 - preBoilerReturnStartX) < 1 &&
            Math.abs(dim.y1 - preBoilerReturnY) < 1 &&
            Math.abs(dim.y2 - (preBoilerReturnY + preBoilerReturnPipeDiameter)) < 1) {
          const leftX = preBoilerReturnStartX;
          return {
            ...dim,
            x1: leftX,
            x2: leftX,
            position: {
              ...dim.position,
              x: leftX - 100, // Отступ слева
            },
            elements: dim.elements.map(el => ({
              ...el,
              x: leftX,
              type: el.type === 'right' ? 'left' : el.type === 'left' ? 'right' : el.type,
            })),
          };
        }
        
        // Перемещаем общую размерную линию длины подающего коллектора выше всех
        const isSupplyTotalLine = dim.isHorizontal && 
            Math.abs(dim.x1 - preBoilerSupplyStartX) < 1 && 
            Math.abs(dim.x2 - (preBoilerSupplyStartX + actualPreBoilerSupplyCollectorLength)) < 1;
        // Перемещаем общую размерную линию длины обратного коллектора выше всех
        const isReturnTotalLine = dim.isHorizontal && 
            Math.abs(dim.x1 - preBoilerReturnStartX) < 1 && 
            Math.abs(dim.x2 - (preBoilerReturnStartX + actualPreBoilerReturnCollectorLength)) < 1;
            
        if (isSupplyTotalLine || isReturnTotalLine) {
          return {
            ...dim,
            y1: preBoilerTotalLengthLineY,
            y2: preBoilerTotalLengthLineY,
            position: {
              ...dim.position,
              y: preBoilerTotalLengthLineY - 15,
            },
            elements: dim.elements.map(el => ({
              ...el,
              y: preBoilerTotalLengthLineY,
              // Привязываем к подающему коллектору
              objectId: el.objectId === preBoilerReturnPipeId ? preBoilerSupplyPipeId : el.objectId,
            })),
          };
        }
        
        return dim;
      });
      
      // Корректируем позицию размерной линии межосевого расстояния (200 мм) для предкотлового коллектора
      // Это вертикальная линия между центрами труб, расположенная на preBoilerMinStartX
      const finalPreBoilerDimensions = adjustedPreBoilerDimensions.map(dim => {
        // Находим вертикальную размерную линию межосевого расстояния
        if (!dim.isHorizontal && dim.angle === 90 &&
            Math.abs(dim.x1 - preBoilerMinStartX) < 1 && Math.abs(dim.x2 - preBoilerMinStartX) < 1 &&
            Math.abs(dim.y1 - preBoilerSupplyCenterY) < 1 &&
            Math.abs(dim.y2 - preBoilerReturnCenterY) < 1 &&
            dim.elements && dim.elements.length === 2 &&
            dim.elements.every(el => el.objectType === 'cylinder')) {
          // Устанавливаем фиксированную позицию для текста размерной линии
          return {
            ...dim,
            position: {
              ...dim.position,
              x: preBoilerMinStartX - 40, // Фиксированное смещение влево
            },
          };
        }
        return dim;
      });
      
      dimensionLines.push(...finalPreBoilerDimensions);
      
      // ========================================
      // 1.5h. МЕЖОСЕВОЕ РАССТОЯНИЕ ДЛЯ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
      // ========================================
      
      // Межосевое расстояние между трубами подачи и обратки предкотлового коллектора
      // Размерная линия справа от предкотлового коллектора (так как слева - гидроразделитель)
      // Фиксированное смещение вправо от конца предкотлового коллектора
      const preBoilerInterAxisDimX = preBoilerEndX + 40;
      
      // Фиксированная позиция для текста (слева от предкотлового коллектора)
      const preBoilerFixedTextPosition = preBoilerMinStartX - 150;
      
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0,
        x1: preBoilerInterAxisDimX,
        y1: preBoilerSupplyCenterY,
        x2: preBoilerInterAxisDimX,
        y2: preBoilerReturnCenterY,
        position: { x: preBoilerFixedTextPosition, y: (preBoilerSupplyCenterY + preBoilerReturnCenterY) / 2 }, // Фиксированная позиция текста
        value: String(Math.round(actualPreBoilerPipeSpacing)),
        rawValue: actualPreBoilerPipeSpacing,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: preBoilerSupplyPipeId, objectType: 'cylinder', x: preBoilerInterAxisDimX, y: preBoilerSupplyCenterY, type: 'left' },
          { objectId: preBoilerReturnPipeId, objectType: 'cylinder', x: preBoilerInterAxisDimX, y: preBoilerReturnCenterY, type: 'left' },
        ],
      });
    }
    
    // ========================================
    // 1.5h-1. РАЗМЕРНЫЕ ЛИНИИ ДЛЯ ЦЕНТРАЛЬНЫХ ФИТИНГОВ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
    // ========================================
    
    // Размерные линии для центральных фитингов подачи предкотлового коллектора
    if (preBoilerSupplyCenterFittingObjects && preBoilerSupplyCenterFittingObjects.length > 0) {
      const preBoilerSupplyCenterDims = generateCenterFittingDimensions(
        preBoilerSupplyCenterFittingObjects,
        preBoilerSupplyLeftCapId,
        null, // Правой заглушки нет у предкотлового
        preBoilerSupplyStartX,
        preBoilerSupplyCenterY,
        preBoilerSupplyPipeDiameter,
        actualPreBoilerSupplyPipeLength,
        preBoilerSupplyLeftCapInfo.straightLength,
        0, // rightStraightCapLength
        preBoilerSupplyPipeId,
        actualPreBoilerSupplyPipeLength,
        preBoilerSupplyStartX,
        preBoilerSupplyLeftConnectionInfo,
        null, // rightConnectionInfo - нет для предкотлового
        preBoilerSupplyLeftBortshaibaLength,
        0 // rightBortshaibaLength - нет для предкотлового
      );
      
      // Размерные линии уже в правильном формате, просто добавляем их
      dimensionLines.push(...preBoilerSupplyCenterDims);
    }
    
    // Размерные линии для центральных фитингов обратки предкотлового коллектора
    if (preBoilerReturnCenterFittingObjects && preBoilerReturnCenterFittingObjects.length > 0) {
      const preBoilerReturnCenterDims = generateCenterFittingDimensions(
        preBoilerReturnCenterFittingObjects,
        preBoilerReturnLeftCapId,
        null, // Правой заглушки нет у предкотлового
        preBoilerReturnStartX,
        preBoilerReturnCenterY,
        preBoilerReturnPipeDiameter,
        actualPreBoilerReturnPipeLength,
        preBoilerReturnLeftCapInfo.straightLength,
        0, // rightStraightCapLength
        preBoilerReturnPipeId,
        actualPreBoilerReturnPipeLength,
        preBoilerReturnStartX,
        preBoilerReturnLeftConnectionInfo,
        null, // rightConnectionInfo - нет для предкотлового
        preBoilerReturnLeftBortshaibaLength,
        0 // rightBortshaibaLength - нет для предкотлового
      );
      
      // Размерные линии уже в правильном формате, просто добавляем их
      dimensionLines.push(...preBoilerReturnCenterDims);
    }
    
    // ========================================
    // 1.5i. BALLOONS ДЛЯ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
    // ========================================
    
    // Balloons для центральных фитингов подачи предкотлового коллектора
    if (preBoilerSupplyCenterFittingObjects && preBoilerSupplyCenterFittingObjects.length > 0) {
      const preBoilerSupplyCenterBalloons = generateCenterFittingBalloons(
        preBoilerSupplyCenterFittingObjects,
        getFittingLabel,
        preBoilerSupplyCenterY,
        0,
        'preboiler-supply-center-'
      );
      balloons.push(...preBoilerSupplyCenterBalloons);
    }
    
    // Balloons для центральных фитингов обратки предкотлового коллектора
    if (preBoilerReturnCenterFittingObjects && preBoilerReturnCenterFittingObjects.length > 0) {
      const preBoilerReturnCenterBalloons = generateCenterFittingBalloons(
        preBoilerReturnCenterFittingObjects,
        getFittingLabel,
        preBoilerReturnCenterY,
        0,
        'preboiler-return-center-'
      );
      balloons.push(...preBoilerReturnCenterBalloons);
    }
    
    // Balloons для дополнительных выходов подачи предкотлового коллектора
    if (preBoilerSupplyAdditionalOutletObjects && preBoilerSupplyAdditionalOutletObjects.length > 0) {
      const processedPreBoilerSupplyOutletIndices = new Set();
      
      preBoilerSupplyAdditionalOutletObjects.forEach((outlet, index) => {
        if (processedPreBoilerSupplyOutletIndices.has(index)) return;
        
        const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
        if (!isFitting) return;
        
        const outletData = preBoilerSupplyAdditionalOutlets[index];
        const fittingKey = outletData?.fittingId;
        const label = getFittingLabel(fittingKey);
        const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
        
        const direction = outletData?.direction || 'up';
        const outletType = outletData?.outletType || 'short';
        const isThrough = outletType === 'through';
        
        // Получаем базовую длину фитинга для расчета anchorYOffset
        let baseLength = 80;
        if (isThrough) {
          const isFlange = fittingKey?.startsWith('Flanec_');
          const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
          const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
          const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
          
          if (isSpecialFitting) {
            baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
          } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
            baseLength = Number(outletData.fittingLength);
          } else if (fittingKey) {
            const fittingInfo = fittingsData[fittingKey];
            if (fittingInfo) {
              baseLength = fittingInfo.length;
            }
          } else if (outletData.length) {
            baseLength = Number(outletData.length);
          }
        }
        
        let balloonY;
        if (direction === 'up') {
          balloonY = outlet.y - 60;
        } else {
          balloonY = outlet.y + outlet.height * 0.7;
        }
        
        // Каждый дополнительный выход имеет свой баллон
        const posX = outlet.centerX + 40;
        const targets = [{ type: 'fitting', id: targetId }];
        
        if (isThrough) {
          const anchorYOffset = baseLength / 2 / outlet.height;
          targets[0].anchorNormalized = { x: 0.5, y: anchorYOffset };
          targets[0].anchorMode = 'local';
        }
        
        balloons.push({
          id: `balloon-preboiler-supply-additional-${index}-${Date.now()}`,
          x: posX,
          y: balloonY,
          text: label,
          targets: targets,
        });
        
        processedPreBoilerSupplyOutletIndices.add(index);
      });
    }
    
    // Balloons для дополнительных выходов обратки предкотлового коллектора
    if (preBoilerReturnAdditionalOutletObjects && preBoilerReturnAdditionalOutletObjects.length > 0) {
      const processedPreBoilerReturnOutletIndices = new Set();
      
      preBoilerReturnAdditionalOutletObjects.forEach((outlet, index) => {
        if (processedPreBoilerReturnOutletIndices.has(index)) return;
        
        const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
        if (!isFitting) return;
        
        const outletData = preBoilerReturnAdditionalOutlets[index];
        const fittingKey = outletData?.fittingId;
        const label = getFittingLabel(fittingKey);
        const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
        
        const direction = outletData?.direction || 'down';
        const outletType = outletData?.outletType || 'short';
        const isThrough = outletType === 'through';
        
        // Получаем базовую длину фитинга для расчета anchorYOffset
        let baseLength = 80;
        if (isThrough) {
          const isFlange = fittingKey?.startsWith('Flanec_');
          const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
          const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
          const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
          
          if (isSpecialFitting) {
            baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
          } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
            baseLength = Number(outletData.fittingLength);
          } else if (fittingKey) {
            const fittingInfo = fittingsData[fittingKey];
            if (fittingInfo) {
              baseLength = fittingInfo.length;
            }
          } else if (outletData.length) {
            baseLength = Number(outletData.length);
          }
        }
        
        let balloonY;
        if (direction === 'up') {
          balloonY = outlet.y - 60;
        } else {
          balloonY = outlet.y + outlet.height * 0.7;
        }
        
        // Каждый дополнительный выход имеет свой баллон
        const posX = outlet.centerX + 40;
        const targets = [{ type: 'fitting', id: targetId }];
        
        if (isThrough) {
          const anchorYOffset = baseLength / 2 / outlet.height;
          targets[0].anchorNormalized = { x: 0.5, y: anchorYOffset };
          targets[0].anchorMode = 'local';
        }
        
        balloons.push({
          id: `balloon-preboiler-return-additional-${index}-${Date.now()}`,
          x: posX,
          y: balloonY,
          text: label,
          targets: targets,
        });
        
        processedPreBoilerReturnOutletIndices.add(index);
      });
    }
    
    // ========================================
    // 1.5j. BALLOONS ДЛЯ КОНТУРОВ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
    // ========================================
    
    // Вычисляем уровни для позиций (аналогично обычному коллектору)
    const allPreBoilerSupplyObjects = [...preBoilerSupplyCircuitObjects, ...preBoilerSupplyAdditionalOutletObjects].filter(obj => obj && obj.y != null && obj.height != null);
    const allPreBoilerReturnObjects = [...preBoilerReturnCircuitObjects, ...preBoilerReturnAdditionalOutletObjects].filter(obj => obj && obj.y != null && obj.height != null);
    
    const minPreBoilerSupplyTopY = allPreBoilerSupplyObjects.length > 0
      ? Math.min(...allPreBoilerSupplyObjects.map(c => c.y))
      : preBoilerSupplyY;
    const maxPreBoilerReturnBottomY = allPreBoilerReturnObjects.length > 0
      ? Math.max(...allPreBoilerReturnObjects.map(c => c.y + c.height))
      : preBoilerReturnY + preBoilerReturnPipeDiameter;
    
    const preBoilerDimOffset = 40;
    const preBoilerTopDimY = minPreBoilerSupplyTopY - preBoilerDimOffset * 2;
    const preBoilerBottomDimY = maxPreBoilerReturnBottomY + preBoilerDimOffset * 3;
    
    // Balloons для подающего предкотлового коллектора
    const processedPreBoilerSupplyIndices = new Set();
    
    preBoilerSupplyCircuitObjects.forEach((circuit, index) => {
      if (processedPreBoilerSupplyIndices.has(index)) return;
      
      const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
      if (!isFitting) return;
      
      // Определяем индекс контура и тип патрубка (короткий/длинный)
      const circuitIndex = Math.floor(index / 2);
      const isShortCircuit = circuit.isShort ?? (index % 2 === 0);
      const fittingKey = preBoilerSupplyCircuits[circuitIndex]?.fitting;
      const label = getFittingLabel(fittingKey);
      const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
      
      // Проверяем следующий фитинг
      const nextIndex = index + 1;
      const nextCircuit = preBoilerSupplyCircuitObjects[nextIndex];
      const nextCircuitIndex = Math.floor(nextIndex / 2);
      const nextFittingKey = preBoilerSupplyCircuits[nextCircuitIndex]?.fitting;
      const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
      
      // Для длинных патрубков используем Y координату короткого патрубка того же контура
      let balloonYBase;
      if (isShortCircuit) {
        balloonYBase = circuit.y; // Короткий - используем свой Y
      } else {
        // Длинный - используем Y короткого патрубка того же контура
        const shortCircuit = preBoilerSupplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        balloonYBase = shortCircuit ? shortCircuit.y : circuit.y;
      }
      const balloonY = (preBoilerTopDimY + balloonYBase) / 2;
      
      if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedPreBoilerSupplyIndices.has(nextIndex)) {
        // Объединяем два соседних одинаковых фитинга
        const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
        const midX = (circuit.centerX + nextCircuit.centerX) / 2;
        
        // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
        const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 0);
        
        // Получаем короткий патрубок для вычисления вылета
        const shortCircuit = preBoilerSupplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        const shortLength = shortCircuit ? shortCircuit.height : 40;
        const anchorYOffset = shortLength / 2 / circuit.height; // смещение от верха
        
        const nextShortCircuit = preBoilerSupplyCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
        const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
        const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
        
        balloons.push({
          id: `balloon-preboiler-supply-${index}-${Date.now()}`,
          x: midX,
          y: balloonY,
          text: label,
          targets: [
            { 
              type: 'fitting', 
              id: targetId,
              // Для длинного: привязка со смещением (вылет/2) от верха
              ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
            },
            { 
              type: 'fitting', 
              id: nextTargetId,
              ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
            },
          ],
        });
        
        processedPreBoilerSupplyIndices.add(index);
        processedPreBoilerSupplyIndices.add(nextIndex);
      } else {
        // Одиночный фитинг
        let posX;
        const prevIndex = index - 1;
        const prevCircuit = preBoilerSupplyCircuitObjects[prevIndex];
        
        if (prevCircuit) {
          posX = (prevCircuit.centerX + circuit.centerX) / 2;
        } else {
          const internalSpacing = Number(preBoilerSupplyCircuits[circuitIndex]?.circuitSpacing) || 125;
          posX = circuit.centerX - internalSpacing / 2;
        }
        
        // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
        const shortCircuit = preBoilerSupplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        const shortLength = shortCircuit ? shortCircuit.height : 40;
        const anchorYOffset = shortLength / 2 / circuit.height;
        
        balloons.push({
          id: `balloon-preboiler-supply-${index}-${Date.now()}`,
          x: posX,
          y: balloonY,
          text: label,
          targets: [{ 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от верха
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          }],
        });
        
        processedPreBoilerSupplyIndices.add(index);
      }
    });
    
    // Balloons для обратного предкотлового коллектора
    const processedPreBoilerReturnIndices = new Set();
    
    preBoilerReturnCircuitObjects.forEach((circuit, index) => {
      if (processedPreBoilerReturnIndices.has(index)) return;
      
      const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
      if (!isFitting) return;
      
      // Определяем индекс контура и тип патрубка (короткий/длинный)
      const circuitIndex = Math.floor(index / 2);
      const isShortCircuit = circuit.isShort ?? (index % 2 === 1);
      const fittingKey = preBoilerReturnCircuits[circuitIndex]?.fitting;
      const label = getFittingLabel(fittingKey);
      const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
      
      // Проверяем следующий фитинг
      const nextIndex = index + 1;
      const nextCircuit = preBoilerReturnCircuitObjects[nextIndex];
      const nextCircuitIndex = Math.floor(nextIndex / 2);
      const nextFittingKey = preBoilerReturnCircuits[nextCircuitIndex]?.fitting;
      const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
      
      // Для длинных патрубков используем Y координату короткого патрубка того же контура
      let balloonYBase;
      if (isShortCircuit) {
        balloonYBase = circuit.y + circuit.height; // Короткий - используем свой низ
      } else {
        // Длинный - используем низ короткого патрубка того же контура
        const shortCircuit = preBoilerReturnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        balloonYBase = shortCircuit ? shortCircuit.y + shortCircuit.height : circuit.y + circuit.height;
      }
      const balloonY = (preBoilerBottomDimY + balloonYBase) / 2;
      
      if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedPreBoilerReturnIndices.has(nextIndex)) {
        // Объединяем два соседних одинаковых фитинга
        const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
        const midX = (circuit.centerX + nextCircuit.centerX) / 2;
        
        // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
        const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 1);
        
        // Получаем короткий патрубок для вычисления вылета
        const shortCircuit = preBoilerReturnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        const shortLength = shortCircuit ? shortCircuit.height : 40;
        const anchorYOffset = shortLength / 2 / circuit.height; // смещение от низа (для обратки)
        
        const nextShortCircuit = preBoilerReturnCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
        const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
        const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
        
        balloons.push({
          id: `balloon-preboiler-return-${index}-${Date.now()}`,
          x: midX,
          y: balloonY,
          text: label,
          targets: [
            { 
              type: 'fitting', 
              id: targetId,
              // Для длинного: привязка со смещением (вылет/2) от низа
              ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
            },
            { 
              type: 'fitting', 
              id: nextTargetId,
              ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
            },
          ],
        });
        
        processedPreBoilerReturnIndices.add(index);
        processedPreBoilerReturnIndices.add(nextIndex);
      } else {
        // Одиночный фитинг
        let posX;
        const prevIndex = index - 1;
        const prevCircuit = preBoilerReturnCircuitObjects[prevIndex];
        
        if (prevCircuit) {
          posX = (prevCircuit.centerX + circuit.centerX) / 2;
        } else {
          const internalSpacing = Number(preBoilerReturnCircuits[circuitIndex]?.circuitSpacing) || 125;
          posX = circuit.centerX - internalSpacing / 2;
        }
        
        // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
        const shortCircuit = preBoilerReturnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
        const shortLength = shortCircuit ? shortCircuit.height : 40;
        const anchorYOffset = 1 - (shortLength / 2 / circuit.height);
        
        balloons.push({
          id: `balloon-preboiler-return-${index}-${Date.now()}`,
          x: posX,
          y: balloonY,
          text: label,
          targets: [{ 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от низа
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          }],
        });
        
        processedPreBoilerReturnIndices.add(index);
      }
    });
  
  // ========================================
  // 1.5k. BALLOONS ДЛЯ ЛЕВЫХ ЗАГЛУШЕК/ПЕРЕХОДОВ И ПОДКЛЮЧЕНИЙ ПРЕДКОТЛОВОГО КОЛЛЕКТОРА
  // ========================================
  
  // Вычисляем Y позиции для balloons (аналогично распределительному коллектору)
  const preBoilerSupplyBalloonY = preBoilerSupplyCenterY;
  const preBoilerReturnBalloonY = preBoilerReturnCenterY;
  const preBoilerBalloonYOffset = 30; // Смещение для позиций, если есть фитинг рядом
  
  // Левый переход/заглушка подающего предкотлового коллектора
  if (hasPreBoilerSupplyLeftCap && preBoilerSupplyLeftCapElementType === 'reducer' && preBoilerSupplyLeftCapId && preBoilerSupplyLeftReducer) {
    const label = getFittingLabel(preBoilerSupplyLeftReducer);
    const reducerStartX = preBoilerSupplyStartX - preBoilerSupplyLeftCapInfo.actualLength;
    const reducerY = preBoilerSupplyLeftConnectionInfo 
      ? preBoilerSupplyBalloonY - preBoilerBalloonYOffset 
      : preBoilerSupplyBalloonY;
    balloons.push({
      id: `balloon-preboiler-supply-left-reducer-${Date.now()}`,
      x: reducerStartX - 25,
      y: reducerY,
      text: label,
      targets: [{ type: 'fitting', id: preBoilerSupplyLeftCapId }],
    });
  }
  
  // Левый фитинг подающего предкотлового коллектора
  if (preBoilerSupplyLeftConnectionInfo && preBoilerSupplyLeftConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(preBoilerSupplyLeftConnectionInfo.fittingKey);
    const fittingStartX = preBoilerSupplyLeftConnectionInfo.startX - preBoilerSupplyLeftConnectionInfo.length;
    balloons.push({
      id: `balloon-preboiler-supply-left-${Date.now()}`,
      x: fittingStartX - 100,
      y: preBoilerSupplyBalloonY,
      text: label,
      targets: [{ type: 'fitting', id: preBoilerSupplyLeftConnectionInfo.id }],
    });
  }
  
  // Левый переход/заглушка обратного предкотлового коллектора
  if (hasPreBoilerReturnLeftCap && preBoilerReturnLeftCapElementType === 'reducer' && preBoilerReturnLeftCapId && preBoilerReturnLeftReducer) {
    const label = getFittingLabel(preBoilerReturnLeftReducer);
    const reducerStartX = preBoilerReturnStartX - preBoilerReturnLeftCapInfo.actualLength;
    const reducerY = preBoilerReturnLeftConnectionInfo 
      ? preBoilerReturnBalloonY + preBoilerBalloonYOffset 
      : preBoilerReturnBalloonY;
    balloons.push({
      id: `balloon-preboiler-return-left-reducer-${Date.now()}`,
      x: reducerStartX - 25,
      y: reducerY,
      text: label,
      targets: [{ type: 'fitting', id: preBoilerReturnLeftCapId }],
    });
  }
  
  // Левый фитинг обратного предкотлового коллектора
  if (preBoilerReturnLeftConnectionInfo && preBoilerReturnLeftConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(preBoilerReturnLeftConnectionInfo.fittingKey);
    const fittingStartX = preBoilerReturnLeftConnectionInfo.startX - preBoilerReturnLeftConnectionInfo.length;
    balloons.push({
      id: `balloon-preboiler-return-left-${Date.now()}`,
      x: fittingStartX - 100,
      y: preBoilerReturnBalloonY,
      text: label,
      targets: [{ type: 'fitting', id: preBoilerReturnLeftConnectionInfo.id }],
    });
  }
  
  } // Конец блока hasPreBoilerCollector
  
  // ========================================
  // 2. РАСПРЕДЕЛИТЕЛЬНЫЙ КОЛЛЕКТОР (горизонтально)
  // ========================================
  
  // Объявляем переменные заранее, чтобы они были доступны в размерных линиях
  let collectorStartX;
  let supplyY, returnY;
  let actualPipeSpacing;
  let supplyPipeId, returnPipeId;
  let supplyCenterY, returnCenterY;
  let supplyCircuitObjects = [];
  let returnCircuitObjects = [];
  let supplyCircuitPositions = []; // IGES: позиции контуров подающего коллектора
  let returnCircuitPositions = []; // IGES: позиции контуров обратного коллектора
  let supplyRightConnectionInfo, returnRightConnectionInfo;
  let supplyRightCapId, returnRightCapId;
  let supplyRightCapInfo = { straightLength: 0, ellipticalLength: 0 };
  let returnRightCapInfo = { straightLength: 0, ellipticalLength: 0 };
  let supplyRightBortshaibaLength = 0;
  let returnRightBortshaibaLength = 0;
  let actualSupplyPipeLength, actualReturnPipeLength;
  let supplyCenterFittingObjects = [];
  let returnCenterFittingObjects = [];
  let supplyAdditionalOutletObjects = [];
  let returnAdditionalOutletObjects = [];
  
  // Объявляем pipeSpec для использования в размерных линиях
  let supplyPipeSpec;
  let returnPipeSpec;
  
  // При отражении системы БЕЗ предкотлового коллектора обычный коллектор НЕ генерируется
  // (он уже сгенерирован как предкотловой слева)
  if (!(isSystemFlipped && !params.hasPreBoilerCollector)) {
  
  // Вычисляем размеры труб коллекторов (для квадратных - размер профиля, для круглых - диаметр)
  let supplyPipeSize = supplyPipeDiameter;
  let returnPipeSize = returnPipeDiameter;
  if (params.shape === 'square') {
    const supplyProfile = params.supplyPipeProfile || '80x80';
    const returnProfile = params.returnPipeProfile || '80x80';
    supplyPipeSize = parseFloat(supplyProfile.split('x')[0]);
    returnPipeSize = parseFloat(returnProfile.split('x')[0]);
  }
  
  // Коллекторы начинаются ВПРИТЫК к разделителю (справа)
  collectorStartX = startX + mainPipeSize / 2;
  
  // Позиции коллекторов по вертикали
  const collectorCenterY = mainPipeTopY + actualMainPipeHeight / 2;
  
  if (pipeSpacingMode === 'offset') {
    // Режим отступов: отступ от верха (с учетом заглушек/бортшайб) до ЦЕНТРА трубы
    // Отступ считается от верхней бортшайбы/заглушки (если диаметры одинаковые)
    const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
    const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    
    supplyY = topEdge + supplyPipeOffset - supplyPipeSize / 2;
    returnY = bottomEdge - returnPipeOffset - returnPipeSize / 2;
    // Вычисляем фактическое межосевое расстояние (от центра до центра)
    actualPipeSpacing = (returnY + returnPipeSize / 2) - (supplyY + supplyPipeSize / 2);
  } else {
    // Режим межосевого расстояния (по умолчанию)
    supplyY = collectorCenterY - pipeSpacing / 2 - supplyPipeSize / 2;
    returnY = collectorCenterY + pipeSpacing / 2 - returnPipeSize / 2;
    actualPipeSpacing = pipeSpacing;
  }
  
  // ========================================
  // 2.0a. ВЫЧИСЛЕНИЕ ДЛИН БОРТШАЙБ/ПРЯМЫХ ЗАГЛУШЕК ДЛЯ ПРАВЫХ ПОДКЛЮЧЕНИЙ КОЛЛЕКТОРА
  // ========================================
  
  // Прямые заглушки для правых подключений коллектора (если есть)
  supplyRightCapInfo = getCapLength({
    hasCap: hasSupplyRightCap,
    capElementType: effectiveSupplyRightCapElementType,
    capType: effectiveSupplyRightCapType,
    reducerKey: supplyRightReducer,
    diameter: supplyPipeDiameter,
    shape: params.shape || 'round',
    profile: params.supplyPipeProfile,
  });
  
  returnRightCapInfo = getCapLength({
    hasCap: hasReturnRightCap,
    capElementType: effectiveReturnRightCapElementType,
    capType: effectiveReturnRightCapType,
    reducerKey: returnRightReducer,
    diameter: returnPipeDiameter,
    shape: params.shape || 'round',
    profile: params.returnPipeProfile,
  });
  
  // Бортшайбы для правых подключений коллектора
  supplyRightBortshaibaLength = 0;
  returnRightBortshaibaLength = 0;
  
  // Подающий коллектор - правая борт-шайба
  if (hasSupplyRightConnection && supplyRightConnectionType === 'fitting' && supplyRightConnectionFitting) {
    const isFlange = supplyRightConnectionFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !supplyRightConnectionFitting.endsWith('_welded');
    const isNakGaika = supplyRightConnectionFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = supplyRightConnectionFitting.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasSupplyRightCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(supplyRightConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          supplyRightBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasSupplyRightCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[supplyRightConnectionFitting];
      if (shaibaInfo) {
        supplyRightBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Обратный коллектор - правая борт-шайба
  if (hasReturnRightConnection && returnRightConnectionType === 'fitting' && returnRightConnectionFitting) {
    const isFlange = returnRightConnectionFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !returnRightConnectionFitting.endsWith('_welded');
    const isNakGaika = returnRightConnectionFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = returnRightConnectionFitting.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasReturnRightCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(returnRightConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          returnRightBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasReturnRightCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[returnRightConnectionFitting];
      if (shaibaInfo) {
        returnRightBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Реальная длина коллектора = длина коллектора - прямые заглушки - бортшайбы
  actualSupplyPipeLength = actualSupplyCollectorLength - supplyRightCapInfo.straightLength - supplyRightBortshaibaLength;
  actualReturnPipeLength = actualReturnCollectorLength - returnRightCapInfo.straightLength - returnRightBortshaibaLength;
  
  // Подающий коллектор
  supplyPipeId = `cyl-supply-main-${Date.now()}`;
  const supplyThickness = determineThickness(mainPipeDiameter, supplyPipeDiameter, coating);
  
  // Получаем профиль для квадратного коллектора
  const supplyPipeProfileValue = supplyPipeProfile || (params.shape === 'square' ? '80x80' : null);
  
  // Получаем глубину вставки для круглых труб (для IGES экспорта)
  const supplyInsertionDepth = params.shape === 'round' ? getInsertionDepth(mainPipeDiameter, supplyPipeDiameter) : 0;
  
  // Создаем pipeSpec для подающего коллектора с учетом формы
  if (params.shape === 'square') {
    const profile = supplyPipeProfileValue || '80x80';
    const comboKey = `SQUARE_PROFILE|${profile}|`;
    const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
    let thickness = '2';
    let squareCoating = 'Матовый';
    if (matchingKey) {
      const parts = matchingKey.split('|');
      thickness = parts[2] || '2';
      squareCoating = parts[3] || 'Матовый';
    }
    supplyPipeSpec = {
      shape: 'square',
      profile: profile,
      thickness: thickness,
      coating: squareCoating,
      squareType: 'profile',
    };
  } else {
    supplyPipeSpec = createPipeSpec(supplyPipeDiameter, supplyThickness, coating);
  }
  
  cylinders.push({
    id: supplyPipeId,
    x: collectorStartX,
    y: supplyY,
    width: actualSupplyPipeLength,
    height: supplyPipeSize,
    pipeSpec: supplyPipeSpec,
    insertionDepth: supplyInsertionDepth, // Для IGES экспорта
  });
  
  // Обратный коллектор
  returnPipeId = `cyl-return-main-${Date.now()}`;
  const returnThickness = determineThickness(mainPipeDiameter, returnPipeDiameter, coating);
  
  // Получаем профиль для квадратного коллектора
  const returnPipeProfileValue = returnPipeProfile || (params.shape === 'square' ? '80x80' : null);
  
  // Получаем глубину вставки для круглых труб (для IGES экспорта)
  const returnInsertionDepth = params.shape === 'round' ? getInsertionDepth(mainPipeDiameter, returnPipeDiameter) : 0;
  
  // Создаем pipeSpec для обратного коллектора с учетом формы
  if (params.shape === 'square') {
    const profile = returnPipeProfileValue || '80x80';
    const comboKey = `SQUARE_PROFILE|${profile}|`;
    const matchingKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
    let thickness = '2';
    let squareCoating = 'Матовый';
    if (matchingKey) {
      const parts = matchingKey.split('|');
      thickness = parts[2] || '2';
      squareCoating = parts[3] || 'Матовый';
    }
    returnPipeSpec = {
      shape: 'square',
      profile: profile,
      thickness: thickness,
      coating: squareCoating,
      squareType: 'profile',
    };
  } else {
    returnPipeSpec = createPipeSpec(returnPipeDiameter, returnThickness, coating);
  }
  
  cylinders.push({
    id: returnPipeId,
    x: collectorStartX,
    y: returnY,
    width: actualReturnPipeLength,
    height: returnPipeSize,
    pipeSpec: returnPipeSpec,
    insertionDepth: returnInsertionDepth, // Для IGES экспорта
  });
  
  // ========================================
  // IGES: ФИКТИВНЫЕ ФИТИНГИ ДЛЯ ОТВЕРСТИЙ В ГИДРОРАЗДЕЛИТЕЛЕ
  // ========================================
  // Создаём невидимые фитинги для отверстий в гидроразделителе, куда привариваются трубы коллектора
  
  // Фиктивный фитинг для подающей трубы коллектора
  const supplyJunctionY = supplyY + supplyPipeSize / 2; // Центр подающей трубы
  const supplyHoleDiameter = getHoleDiameterForPipe(supplyPipeDiameter, { squareProfile: params.shape === 'square' ? params.supplyPipeProfile : null });
  
  fittings.push({
    id: `fitting-junction-supply-${Date.now()}`,
    key: 'junction-supply',
    itemKey: 'junction-supply',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    groupX: collectorStartX, // Начало коллектора = край гидроразделителя
    groupY: supplyJunctionY,
    rotation: 0,
    offsetX: 0,
    offsetY: 0,
    isHidden: true, // Не отображается на чертеже
    igesParentCylinderId: mainPipeId, // IGES: отверстие в гидроразделителе
    igesCreateHole: true, // IGES: создаёт отверстие
    holeDiameter: supplyHoleDiameter, // IGES: диаметр отверстия (для квадратных -10мм, для круглых -уменьшен для сварки)
    igesHoleDirection: 'right', // IGES: направление отверстия (коллектор справа)
  });
  
  // Фиктивный фитинг для обратной трубы коллектора
  const returnJunctionY = returnY + returnPipeSize / 2; // Центр обратной трубы
  const returnHoleDiameter = getHoleDiameterForPipe(returnPipeDiameter, { squareProfile: params.shape === 'square' ? params.returnPipeProfile : null });
  
  fittings.push({
    id: `fitting-junction-return-${Date.now()}`,
    key: 'junction-return',
    itemKey: 'junction-return',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    groupX: collectorStartX, // Начало коллектора = край гидроразделителя
    groupY: returnJunctionY,
    rotation: 0,
    offsetX: 0,
    offsetY: 0,
    isHidden: true, // Не отображается на чертеже
    igesParentCylinderId: mainPipeId, // IGES: отверстие в гидроразделителе
    igesCreateHole: true, // IGES: создаёт отверстие
    holeDiameter: returnHoleDiameter, // IGES: диаметр отверстия (для квадратных -10мм, для круглых -уменьшен для сварки)
    igesHoleDirection: 'right', // IGES: направление отверстия (коллектор справа)
  });
  
  // ========================================
  // 2a. ПРАВЫЕ ЗАГЛУШКИ И ПЕРЕХОДЫ (генерируем ДО подключений)
  // ========================================
  
  // ID для правых заглушек/переходов (для позиций)
  supplyRightCapId = null;
  returnRightCapId = null;
  
  // Подающий коллектор - правая заглушка/переход
  if (hasSupplyRightCap) {
    const capX = collectorStartX + actualSupplyPipeLength; // На правом краю гребёнки (реальная длина)
    const capY = supplyY + supplyPipeSize / 2; // Центр трубы
    
    if (supplyRightCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: supplyRightReducer,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'supply-right',
      });
      if (reducer) {
        fittings.push(reducer);
        supplyRightCapId = reducer.id; // Сохраняем ID
      }
    } else {
      const cap = generateCap({
        capType: supplyRightCapType,
        diameter: supplyPipeDiameter,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'supply-right',
        shape: params.shape || 'round',
        profile: params.supplyPipeProfile,
      });
      if (cap) {
        fittings.push(cap);
        supplyRightCapId = cap.id; // Сохраняем ID
      }
    }
  }
  
  // Обратный коллектор - правая заглушка/переход
  if (hasReturnRightCap) {
    const capX = collectorStartX + actualReturnPipeLength; // На правом краю гребёнки (реальная длина)
    const capY = returnY + returnPipeSize / 2; // Центр трубы
    
    if (returnRightCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: returnRightReducer,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'return-right',
      });
      if (reducer) {
        fittings.push(reducer);
        returnRightCapId = reducer.id; // Сохраняем ID
      }
    } else {
      const cap = generateCap({
        capType: returnRightCapType,
        diameter: returnPipeDiameter,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'return-right',
        shape: params.shape || 'round',
        profile: params.returnPipeProfile,
      });
      if (cap) {
        fittings.push(cap);
        returnRightCapId = cap.id; // Сохраняем ID
      }
    }
  }
  
  // ========================================
  // 2b. ПРАВЫЕ ТОРЦЕВЫЕ ПОДКЛЮЧЕНИЯ
  // ========================================
  
  // Переменные для хранения информации о правых торцевых подключениях (для позиций)
  supplyRightConnectionInfo = null;
  returnRightConnectionInfo = null;
  
  // Правые торцевые подключения подающего коллектора
  if (hasSupplyRightConnection && supplyRightConnectionFitting) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    let connectionStartX = collectorStartX + actualSupplyPipeLength;
    if (hasSupplyRightCap) {
      if (effectiveSupplyRightCapElementType === 'reducer' || effectiveSupplyRightCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = collectorStartX + collectorLength + supplyRightCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем какую длину использовать: для фланцев/гаек/шайб с заглушками используем connectionLength
    const isFlange = supplyRightConnectionFitting.startsWith('Flanec_');
    const isNakGaika = supplyRightConnectionFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = supplyRightConnectionFitting.startsWith('Shaib_Nar_Rezba_');
    const isNippel = supplyRightConnectionFitting.startsWith('Nippel_');
    const useConnectionLength = (isFlange || isNakGaika || isShaibaNarRezba) && hasSupplyRightCap;
    
    // Применяем дефолты для квадратных профилей (торцевые соединения)
    let defaultConnectionLength;
    if (isNakGaika || isShaibaNarRezba) {
      // Для накидных гаек и шайб с резьбой (торцевые): 58мм (квадратные) / 40мм (круглые)
      defaultConnectionLength = params.shape === 'square' ? 58 : 40;
    } else if (isNippel && params.shape === 'square') {
      defaultConnectionLength = null;
    } else {
      defaultConnectionLength = null;
    }
    
    let effectiveLength;
    if (useConnectionLength) {
      effectiveLength = supplyRightConnectionLength || 
        (defaultConnectionLength !== null ? defaultConnectionLength : 100);
    } else {
      effectiveLength = supplyRightConnectionFittingLength || defaultConnectionLength;
    }
    
    const rightSupplyResult = generateConnectionWithCaps({
      connectionType: supplyRightConnectionType,
      fittingId: supplyRightConnectionFitting,
      fittingLength: effectiveLength,
      startX: connectionStartX,
      y: supplyY + supplyPipeSize / 2,
      direction: 'right',
      mainPipeDiameter: supplyPipeDiameter,
      mainPipeId: supplyPipeId,
      prefix: 'supply-right',
      balloonYOffset: 0,
      // Параметры заглушек/переходов
      hasCap: hasSupplyRightCap,
      capElementType: effectiveSupplyRightCapElementType,
      capType: effectiveSupplyRightCapType,
      reducerKey: supplyRightReducer,
      capLength: supplyRightCapInfo.actualLength,
      shape: params.shape || 'round',
      profile: params.supplyPipeProfile,
    });
    
    if (rightSupplyResult) {
      if (rightSupplyResult.cylinders) cylinders.push(...rightSupplyResult.cylinders);
      if (rightSupplyResult.fittings) fittings.push(...rightSupplyResult.fittings);
      // Не добавляем balloons из generateConnectionWithCaps - добавим вручную позже
      
      // Сохраняем информацию о подключении
      supplyRightConnectionInfo = {
        id: rightSupplyResult.mainElementId,
        type: supplyRightConnectionType === 'pipe' ? 'cylinder' : 'fitting',
        fittingKey: supplyRightConnectionFitting,
        length: effectiveLength || rightSupplyResult.totalLength,
        startX: collectorStartX + actualSupplyPipeLength, // Край гребенки (правый край)
        intermediatePipeId: rightSupplyResult.intermediatePipeId,
        bortshaibaId: rightSupplyResult.bortshaibaId,
        capId: supplyRightCapId,
        capType: supplyRightCapType, // 'straight' или 'elliptical'
        capLength: supplyRightCapInfo.straightLength, // Длина прямой заглушки
        needsCapOrReducer: hasSupplyRightCap,
      };
    }
  }
  
  // Правые торцевые подключения обратного коллектора
  if (hasReturnRightConnection && returnRightConnectionFitting) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    let connectionStartX = collectorStartX + actualReturnPipeLength;
    if (hasReturnRightCap) {
      if (effectiveReturnRightCapElementType === 'reducer' || effectiveReturnRightCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = collectorStartX + actualReturnPipeLength + returnRightCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем какую длину использовать: для фланцев/гаек/шайб с заглушками используем connectionLength
    const isReturnFlange = returnRightConnectionFitting.startsWith('Flanec_');
    const isReturnNakGaika = returnRightConnectionFitting.startsWith('Nak_Gaika_');
    const isReturnShaibaNarRezba = returnRightConnectionFitting.startsWith('Shaib_Nar_Rezba_');
    const isReturnNippel = returnRightConnectionFitting.startsWith('Nippel_');
    const useReturnConnectionLength = (isReturnFlange || isReturnNakGaika || isReturnShaibaNarRezba) && hasReturnRightCap;
    
    // Применяем дефолты для квадратных профилей (торцевые соединения)
    let defaultReturnConnectionLength;
    if (isReturnNakGaika || isReturnShaibaNarRezba) {
      // Для накидных гаек и шайб с резьбой (торцевые): 58мм (квадратные) / 40мм (круглые)
      defaultReturnConnectionLength = params.shape === 'square' ? 58 : 40;
    } else if (isReturnNippel && params.shape === 'square') {
      defaultReturnConnectionLength = null;
    } else {
      defaultReturnConnectionLength = null;
    }
    
    let effectiveReturnLength;
    if (useReturnConnectionLength) {
      effectiveReturnLength = returnRightConnectionLength || 
        (defaultReturnConnectionLength !== null ? defaultReturnConnectionLength : 100);
    } else {
      effectiveReturnLength = returnRightConnectionFittingLength || defaultReturnConnectionLength;
    }
    
    const rightReturnResult = generateConnectionWithCaps({
      connectionType: returnRightConnectionType,
      fittingId: returnRightConnectionFitting,
      fittingLength: effectiveReturnLength,
      startX: connectionStartX,
      y: returnY + returnPipeSize / 2,
      direction: 'right',
      mainPipeDiameter: returnPipeDiameter,
      mainPipeId: returnPipeId,
      prefix: 'return-right',
      balloonYOffset: 0,
      // Параметры заглушек/переходов
      hasCap: hasReturnRightCap,
      capElementType: effectiveReturnRightCapElementType,
      capType: effectiveReturnRightCapType,
      reducerKey: returnRightReducer,
      capLength: returnRightCapInfo.actualLength,
      shape: params.shape || 'round',
      profile: params.returnPipeProfile,
    });
    
    if (rightReturnResult) {
      if (rightReturnResult.cylinders) cylinders.push(...rightReturnResult.cylinders);
      if (rightReturnResult.fittings) fittings.push(...rightReturnResult.fittings);
      // Не добавляем balloons из generateConnectionWithCaps - добавим вручную позже
      
      // Сохраняем информацию о подключении
      returnRightConnectionInfo = {
        id: rightReturnResult.mainElementId,
        type: returnRightConnectionType === 'pipe' ? 'cylinder' : 'fitting',
        fittingKey: returnRightConnectionFitting,
        length: effectiveReturnLength || rightReturnResult.totalLength,
        startX: collectorStartX + actualReturnPipeLength, // Край гребенки (правый край)
        intermediatePipeId: rightReturnResult.intermediatePipeId,
        bortshaibaId: rightReturnResult.bortshaibaId,
        capId: returnRightCapId,
        capType: returnRightCapType, // 'straight' или 'elliptical'
        capLength: returnRightCapInfo.straightLength, // Длина прямой заглушки
        needsCapOrReducer: hasReturnRightCap,
      };
    }
  }
  
  // ========================================
  // 3. КОНТУРЫ
  // ========================================
  
  // Генерируем контуры коллектора используя функцию из circuitGenerationLogic
  const circuitsResult = generatePairedCircuitsFull({
    supplyCircuits,
    returnCircuits,
    supplyStartX: collectorStartX, // Начало от внешнего края левой заглушки/разделителя
    returnStartX: collectorStartX, // Начало от внешнего края левой заглушки/разделителя
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter: supplyPipeSize,
    returnPipeDiameter: returnPipeSize,
    supplyPipeThickness: supplyThickness, // Реальная толщина подающего коллектора
    returnPipeThickness: returnThickness, // Реальная толщина обратного коллектора
    supplyFirstCircuitOffset: actualSupplyFirstCircuitOffset,
    returnFirstCircuitOffset: actualReturnFirstCircuitOffset,
    supplySpacingBetweenCircuits: params.supplySpacingBetweenCircuits || 125,
    returnSpacingBetweenCircuits: params.returnSpacingBetweenCircuits || 125,
    pipeSpacing: actualPipeSpacing,
    shape: params.shape || 'round',
  });
  
  if (circuitsResult) {
    if (circuitsResult.cylinders) cylinders.push(...circuitsResult.cylinders);
    if (circuitsResult.fittings) fittings.push(...circuitsResult.fittings);
    if (circuitsResult.welds) welds.push(...circuitsResult.welds);
  }
  
  // Сохраняем информацию о контурах для размерных линий
  supplyCircuitObjects = circuitsResult?.supplyCircuitObjects || [];
  returnCircuitObjects = circuitsResult?.returnCircuitObjects || [];
  // IGES: сохраняем позиции контуров для метаданных
  supplyCircuitPositions = circuitsResult?.supplyCircuitPositions || [];
  returnCircuitPositions = circuitsResult?.returnCircuitPositions || [];
  
  // ========================================
  // 4. ЦЕНТРАЛЬНЫЕ ФИТИНГИ (если есть)
  // ========================================
  
  supplyCenterY = supplyY + supplyPipeSize / 2;
  returnCenterY = returnY + returnPipeSize / 2;
  
  // Подающий коллектор - центральные фитинги
  supplyCenterFittingObjects = [];
  if (supplyCenterFittings && supplyCenterFittings.length > 0) {
    const supplyCenterResult = generateCenterFittings({
      centerFittings: supplyCenterFittings,
      mainPipeId: supplyPipeId,
      mainCenterY: supplyCenterY,
      startX: collectorStartX,
      mainPipeStartX: collectorStartX,
      mainPipeLength: actualSupplyCollectorLength,
      circuits: supplyCircuits,  // Передаем исходные данные контуров подачи
      bottomCircuits: returnCircuits,  // Передаем контуры обратки для возможности переключения
      circuitSpacing: params.supplySpacingBetweenCircuits || 125,
      firstCircuitOffset: actualSupplyFirstCircuitOffset,
      bottomFirstCircuitOffset: actualReturnFirstCircuitOffset,
      bottomCircuitSpacing: params.returnSpacingBetweenCircuits || 125,
      collectorType: 'supply',  // Тип коллектора
      shape: params.shape, // Передаем форму коллектора (round/square)
    });
    
    if (supplyCenterResult) {
      if (supplyCenterResult.fittings) fittings.push(...supplyCenterResult.fittings);
      if (supplyCenterResult.welds) welds.push(...supplyCenterResult.welds);
      if (supplyCenterResult.centerFittingObjects) supplyCenterFittingObjects.push(...supplyCenterResult.centerFittingObjects);
    }
  }
  
  // Обратный коллектор - центральные фитинги
  returnCenterFittingObjects = [];
  if (returnCenterFittings && returnCenterFittings.length > 0) {
    const returnCenterResult = generateCenterFittings({
      centerFittings: returnCenterFittings,
      mainPipeId: returnPipeId,
      mainCenterY: returnCenterY,
      startX: collectorStartX,
      mainPipeStartX: collectorStartX,
      mainPipeLength: actualReturnCollectorLength,
      circuits: returnCircuits,  // Передаем исходные данные контуров обратки
      bottomCircuits: supplyCircuits,  // Передаем контуры подачи для возможности переключения
      circuitSpacing: params.returnSpacingBetweenCircuits || 125,
      firstCircuitOffset: actualReturnFirstCircuitOffset,
      bottomFirstCircuitOffset: actualSupplyFirstCircuitOffset,
      bottomCircuitSpacing: params.supplySpacingBetweenCircuits || 125,
      collectorType: 'return',  // Тип коллектора
      shape: params.shape, // Передаем форму коллектора (round/square)
    });
    
    if (returnCenterResult) {
      if (returnCenterResult.fittings) fittings.push(...returnCenterResult.fittings);
      if (returnCenterResult.welds) welds.push(...returnCenterResult.welds);
      if (returnCenterResult.centerFittingObjects) returnCenterFittingObjects.push(...returnCenterResult.centerFittingObjects);
    }
  }

  // ========================================
  // 5. ДОПОЛНИТЕЛЬНЫЕ ВЫХОДЫ (если есть)
  // ========================================
  
  // Подающий коллектор - дополнительные выходы
  supplyAdditionalOutletObjects = [];
  if (supplyAdditionalOutlets && supplyAdditionalOutlets.length > 0) {
    // Каждый выход генерируется отдельно с учетом своего направления
    supplyAdditionalOutlets.forEach((outlet) => {
      const outletDirection = outlet.direction || 'up';
      const outletType = outlet.outletType || 'short'; // 'short' или 'through'
      
      // Вычисляем X позицию выхода на основе типа размещения
      const outletX = calculateOutletX(
        outlet, 
        collectorStartX, 
        actualSupplyCollectorLength, 
        supplyCircuits, 
        params.supplySpacingBetweenCircuits || 125, 
        actualSupplyFirstCircuitOffset,
        returnCircuits,
        params.returnSpacingBetweenCircuits || 125,
        actualReturnFirstCircuitOffset,
        'supply'
      );
      
      const result = generateCircuits({
        circuits: [outlet],
        startX: outletX,
        startY: supplyY,
        mainPipeId: supplyPipeId,
        mainPipeDiameter: supplyPipeDiameter,
        mainPipeProfile: supplyPipeProfileValue,
        pipeSpec: supplyPipeSpec,
        firstCircuitOffset: 0, // Мы уже вычислили позицию
        circuitSpacing: 0,
        direction: outletDirection,
        shape,
        isAdditionalOutlet: true, // Флаг что это дополнительный выход
        outletType: outletType, // Тип выхода
        pipeSpacing: actualPipeSpacing, // Фактическое межосевое расстояние между коллекторами
      });
      
      if (result) {
        if (result.cylinders) cylinders.push(...result.cylinders);
        if (result.fittings) fittings.push(...result.fittings);
        if (result.welds) welds.push(...result.welds);
        if (result.dimensionLines) dimensionLines.push(...result.dimensionLines);
        if (result.circuitObjects) supplyAdditionalOutletObjects.push(...result.circuitObjects);
      }
    });
  }
  
  // Обратный коллектор - дополнительные выходы
  returnAdditionalOutletObjects = [];
  if (returnAdditionalOutlets && returnAdditionalOutlets.length > 0) {
    // Каждый выход генерируется отдельно с учетом своего направления
    returnAdditionalOutlets.forEach((outlet) => {
      const outletDirection = outlet.direction || 'down';
      const outletType = outlet.outletType || 'short'; // 'short' или 'through'
      
      // Вычисляем X позицию выхода на основе типа размещения
      const outletX = calculateOutletX(
        outlet, 
        collectorStartX, 
        actualReturnCollectorLength, 
        returnCircuits, 
        params.returnSpacingBetweenCircuits || 125, 
        actualReturnFirstCircuitOffset,
        supplyCircuits,
        params.supplySpacingBetweenCircuits || 125,
        actualSupplyFirstCircuitOffset,
        'return'
      );
      
      const result = generateCircuits({
        circuits: [outlet],
        startX: outletX,
        startY: returnY,
        mainPipeId: returnPipeId,
        mainPipeDiameter: returnPipeDiameter,
        mainPipeProfile: returnPipeProfileValue,
        pipeSpec: returnPipeSpec,
        firstCircuitOffset: 0, // Мы уже вычислили позицию
        circuitSpacing: 0,
        direction: outletDirection,
        shape,
        isAdditionalOutlet: true, // Флаг что это дополнительный выход
        outletType: outletType, // Тип выхода
        pipeSpacing: actualPipeSpacing, // Фактическое межосевое расстояние между коллекторами
      });
      
      if (result) {
        if (result.cylinders) cylinders.push(...result.cylinders);
        if (result.fittings) fittings.push(...result.fittings);
        if (result.welds) welds.push(...result.welds);
        if (result.dimensionLines) dimensionLines.push(...result.dimensionLines);
        if (result.circuitObjects) returnAdditionalOutletObjects.push(...result.circuitObjects);
      }
    });
  }
  
  } // Конец условия: генерация обычного коллектора (если не отражение без предкотлового)

  // ========================================
  // 6. РАЗМЕРНЫЕ ЛИНИИ ГИДРОРАЗДЕЛИТЕЛЯ (из HydraulicSeparatorEditor)
  // ========================================
  
  // Размерные линии для левых/правых подключений генерируются только если НЕТ реального предкотлового коллектора
  // (при отражении без предкотлового подключения справа, но размерные линии тоже нужны)
  if (!params.hasPreBoilerCollector) {
    // Проверяем, совпадают ли все фитинги и вылеты у левых подключений
    const leftConnectionsAreSame = leftConnections.length > 1 && leftConnections.every((conn, idx) => {
      if (idx === 0) return true;
      const firstConn = leftConnections[0];
      const firstLength = getFittingLength(firstConn.fittingId, firstConn.fittingLength, params.shape);
      const currentLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
      return conn.fittingId === firstConn.fittingId && Math.abs(firstLength - currentLength) < 0.01;
    });

    // 5.1 Горизонтальные размерные линии для левых подключений (вылет фитингов)
    if (params.leftConnectionsPlacementMode === 'spacing') {
      // Режим межосевого: показываем вылет для каждого уникального типа фитинга
      const processedFittingTypes = new Set();
      
      leftConnections.forEach((conn, index) => {
        const fittingData = fittingsData[conn.fittingId];
        if (!fittingData) return;
        
        // Создаём ключ для идентификации уникального типа фитинга
        const fittingKey = `${conn.fittingId}_${conn.fittingLength || ''}`;
        
        // Пропускаем, если уже обработали такой тип фитинга
        if (processedFittingTypes.has(fittingKey)) return;
        processedFittingTypes.add(fittingKey);
        
        // Вычисляем позицию текущего подключения
        let offset = params.leftConnectionsFirstOffset || 50;
        for (let i = 0; i < index; i++) {
          const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
          offset += spacing;
        }
        
        const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + offset;
        const connectionX = isSystemFlipped 
          ? startX + mainPipeDiameter / 2
          : startX - mainPipeDiameter / 2;
        
        const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
        const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
          fittingId: conn.fittingId,
          connFittingLength: conn.fittingLength,
          pipeDiameter: mainPipeDiameter,
          catalogLength: fittingData.length,
          shape: params.shape,
          fallbackLength,
        });
        const outerEdgeX = isSystemFlipped 
          ? connectionX + geometricLength
          : connectionX - geometricLength;
        
        const dimY = connectionY - 30;
        const leftFittingInfo = leftFittingIds[index];
        const outerElementId = leftFittingInfo?.outerElementId;
        const outerElementType = leftFittingInfo?.outerElementType || 'fitting';
        
        dimensionLines.push({
          isHorizontal: true,
          lockOrientation: true,
          angle: 0,
          x1: isSystemFlipped ? outerEdgeX : connectionX,
          y1: dimY,
          x2: isSystemFlipped ? connectionX : outerEdgeX,
          y2: dimY,
          position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
          value: String(Math.round(geometricLength)),
          rawValue: geometricLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: isSystemFlipped ? [
            { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'right' },
            { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'right' },
          ] : [
            { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'left' },
            { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'left' },
          ],
          ...dimensionOverride,
        });
      });
    } else {
      // Режим отступов: создаём размерные линии для вылета каждого фитинга (старая логика)
      leftConnections.forEach((conn, index) => {
        const fittingData = fittingsData[conn.fittingId];
        if (!fittingData) return;
        
        // Если все подключения одинаковые, показываем размер только для первого (самого верхнего)
        if (leftConnectionsAreSame && index !== 0) return;
        
        // Вычисляем реальную позицию - только режим отступов
        const actualOffset = getConnectionPosition(
          conn.offsetFromTop,
          conn.offsetDirection,
          actualMainPipeHeight,
          topBortshaibaLengthForHeight,
          topStraightCapLength,
          bottomBortshaibaLengthForHeight,
          bottomStraightCapLength
        );
        
        const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
        const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
          fittingId: conn.fittingId,
          connFittingLength: conn.fittingLength,
          pipeDiameter: mainPipeDiameter,
          catalogLength: fittingData.length,
          shape: params.shape,
          fallbackLength,
        });
      // Позиция подключения (с учётом верхней бортшайбы и прямой заглушки)
      const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
      const connectionX = isSystemFlipped 
        ? startX + mainPipeDiameter / 2  // Правый край основной трубы
        : startX - mainPipeDiameter / 2; // Левый край основной трубы
    
    // Внешний край фитинга/подключения (левый для обычного режима, правый для отраженного)
    const outerEdgeX = isSystemFlipped 
      ? connectionX + geometricLength  // Правый конец для отраженной системы
      : connectionX - geometricLength; // Левый конец для обычной системы
    
    // Получаем информацию о крайнем элементе для привязки
    const leftFittingInfo = leftFittingIds[index];
    const outerElementId = leftFittingInfo?.outerElementId;
    const outerElementType = leftFittingInfo?.outerElementType || 'fitting';
    
    const dimY = connectionY - 30;
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: isSystemFlipped ? outerEdgeX : connectionX,
      y1: dimY,
      x2: isSystemFlipped ? connectionX : outerEdgeX,
      y2: dimY,
      position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
      value: String(Math.round(geometricLength)),
      rawValue: geometricLength,
      extensionLines: [],
      precision: 0,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: isSystemFlipped ? [
        // Для отраженной системы: сначала фитинг, потом труба
        { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'right' },
        { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'right' },
      ] : [
        // Для обычной системы: сначала труба, потом фитинг
        { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'left' },
        { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'left' },
      ],
      ...dimensionOverride,
    });
      });
    }

  // 5.1.1 Вертикальные размерные линии для левых подключений
  // Сортируем левые подключения по позиции
  const sortedLeftFittings = leftConnections.map((conn, index) => {
    let actualOffset;
    if (params.leftConnectionsPlacementMode === 'spacing') {
      // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
      actualOffset = params.leftConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов: используем offsetFromTop и offsetDirection
      actualOffset = getConnectionPosition(
        conn.offsetFromTop,
        conn.offsetDirection,
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }
    return {
      ...conn,
      actualOffset,
      index,
      id: leftFittingIds[index]?.id,
    };
  }).sort((a, b) => a.actualOffset - b.actualOffset);

  // При отражении размерные линии справа, иначе слева
  const dimXLeft = isSystemFlipped
    ? startX + mainPipeDiameter / 2 + 200  // Справа (зеркально)
    : startX - mainPipeDiameter / 2 - 200; // Слева

  // 1. От верха (с учётом бортшайбы и прямой заглушки) до первого подключения
  if (sortedLeftFittings.length > 0) {
    const firstFitting = sortedLeftFittings[0];
    const y1 = totalHeightTopY; // С учётом верхней бортшайбы и заглушки
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + firstFitting.actualOffset;
    const distance = firstFitting.actualOffset;

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0,
        x1: dimXLeft,
        y1: y1,
        x2: dimXLeft,
        y2: y2,
        position: { x: dimXLeft + (isSystemFlipped ? 30 : -30), y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          topDimElement, // Используем элемент для общей высоты
          { objectId: firstFitting.id, objectType: 'fitting', x: dimXLeft, y: y2, type: 'top' },
        ],
      });
    }
  }

  // 2. Между подключениями
  for (let i = 0; i < sortedLeftFittings.length - 1; i++) {
    const fitting1 = sortedLeftFittings[i];
    const fitting2 = sortedLeftFittings[i + 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting1.actualOffset;
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting2.actualOffset;
    const distance = fitting2.actualOffset - fitting1.actualOffset;

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0,
        x1: dimXLeft,
        y1: y1,
        x2: dimXLeft,
        y2: y2,
        position: { x: dimXLeft + (isSystemFlipped ? 30 : -30), y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: fitting1.id, objectType: 'fitting', x: dimXLeft, y: y1, type: 'top' },
          { objectId: fitting2.id, objectType: 'fitting', x: dimXLeft, y: y2, type: 'top' },
        ],
      });
    }
  }

  // 3. От последнего подключения до низа (с учётом бортшайбы и прямой заглушки)
  if (sortedLeftFittings.length > 0) {
    const lastFitting = sortedLeftFittings[sortedLeftFittings.length - 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + lastFitting.actualOffset;
    const y2 = totalHeightBottomY; // С учётом нижней бортшайбы и заглушки
    const totalHeightFromTop = actualMainPipeHeight + topBortshaibaLengthForHeight + topStraightCapLength + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    const distance = totalHeightFromTop - lastFitting.actualOffset;

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0,
        x1: dimXLeft,
        y1: y1,
        x2: dimXLeft,
        y2: y2,
        position: { x: dimXLeft + (isSystemFlipped ? 30 : -30), y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: lastFitting.id, objectType: 'fitting', x: dimXLeft, y: y1, type: 'top' },
          bottomDimElement, // Используем элемент для общей высоты
        ],
      });
    }
  }
  } // Конец блока: размерные линии для левых подключений (не генерируются если включен предкотловой)

  // 5.2 Размерные линии для правых торцевых подключений коллектора
  // Эти размерные линии уже генерируются в generateConnectionWithCaps
  // при создании правых торцевых подключений подачи/обратки

  // 5.3 Размерные линии для верхнего торцевого подключения - вертикальные
  // Показываем только если включено торцевое подключение и есть вылет (диаметры не совпадают)
  if (hasTopConnection && topCapFitting && topFittingData2) {
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    
    // Показываем размерную линию только если нужен переход/заглушка (есть вылет)
    if (capReq.needsCap) {
      const fittingLength = topCapFittingLength ?? (
        topCapFitting.startsWith('Flanec_') ? 100 :
        (topCapFitting.startsWith('Nak_Gaika_') || topCapFitting.startsWith('Shaib_Nar_Rezba_')) ? 40 :
        topFittingData2.length
      );
      const capTopY = mainPipeTopY;
      
      // Размерная линия на уровне общей размерной линии высоты
      // Если предкотловой включен кнопкой - по центру
      // Если отражение БЕЗ предкотлового - справа
      // Иначе - слева
      const isPreBoilerEnabledByButton = params.hasPreBoilerCollector;
      const isFlippedWithoutPreBoiler = isSystemFlipped && !params.hasPreBoilerCollector;
      const dimX = isPreBoilerEnabledByButton
        ? startX  // По центру (как было раньше)
        : isFlippedWithoutPreBoiler
          ? startX + mainPipeDiameter / 2 + 270  // Справа при отражении
          : startX - mainPipeDiameter / 2 - 270; // Слева
      
      // Для ниппелей/муфт: размерная линия показывает только длину фитинга (от начала до конца фитинга)
      const isNippel = topCapFitting.startsWith('Nippel_');
      const isMufta = topCapFitting.startsWith('Mufta_');
      
      let elements;
      if (isNippel || isMufta) {
        // Размерная линия от начала фитинга до конца фитинга
        // Вычислим начальную точку фитинга
        let connectionStartY = mainPipeTopY;
        if (hasTopCap) {
          if (topCapType === 'elliptical') {
            connectionStartY = mainPipeTopY - topEllipticalCapLength;
          } else if (topCapType === 'straight') {
            connectionStartY = mainPipeTopY - topStraightCapLength;
          }
        }
        
        const fittingActualLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
        
        elements = [
          // Верхняя точка - к началу фитинга (не к трубе!)
          { objectId: topFittingId, objectType: 'fitting', x: dimX, y: connectionStartY, type: 'bottom' },
          // Нижняя точка - к концу фитинга
          { objectId: topFittingId, objectType: 'fitting', x: dimX, y: connectionStartY - fittingActualLength, type: 'top' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimX,
          y1: connectionStartY,
          x2: dimX,
          y2: connectionStartY - fittingActualLength,
          position: { x: dimX, y: (connectionStartY + connectionStartY - fittingActualLength) / 2 },
          value: String(Math.round(fittingActualLength)),
          rawValue: fittingActualLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      } else {
        // Для других фитингов (фланцев/гаек) - размерная линия от заглушки до конца подключения
        const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
        const isFlange = topCapFitting.startsWith('Flanec_');
        
        const hasStraightCap = hasTopCap && topCapType === 'straight';
        
        // Для фланцев с прямой заглушкой: размерная линия от заглушки
        // Для накидных гаек и шайб с резьбой: размерная линия от заглушки (прямой или эллиптической)
        const useCapAsReference = (isFlange && hasStraightCap && topStraightCapIdForDimLine) ||
                                   ((isNakGaika || isShaibaNarRezba) && (topStraightCapIdForDimLine || topEllipticalCapIdForDimLine));
        const capId = topStraightCapIdForDimLine || topEllipticalCapIdForDimLine;
        
        // Для прямой заглушки: начальная точка - от края заглушки
        let referenceY = capTopY;
        if (hasStraightCap) {
          referenceY = capTopY - topStraightCapLength;
        }
        
        elements = [
          // Верхняя точка - к заглушке (для фланцев/гаек/шайб с прямой заглушкой) или к основной трубе
          useCapAsReference
            ? { objectId: capId, objectType: 'fitting', x: dimX, y: referenceY, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capTopY, type: 'top' },
          // Нижняя точка - к конечному объекту (фитинг или промежуточная труба)
          topFittingEndObjectId 
            ? { objectId: topFittingEndObjectId, objectType: topFittingEndObjectType, x: dimX, y: referenceY - fittingLength, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capTopY - fittingLength, type: 'top' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0, // Вертикальная линия
          x1: dimX,
          y1: referenceY,
          x2: dimX,
          y2: referenceY - fittingLength,
          position: { x: dimX, y: (referenceY + referenceY - fittingLength) / 2 },
          value: String(Math.round(fittingLength)),
          rawValue: fittingLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      }
    }
    // Для фланцев/гаек/шайб впритык - не показываем размерную линию
  }

  // 5.4 Размерные линии для нижнего торцевого подключения - вертикальные
  // Показываем только если включено торцевое подключение и есть вылет (диаметры не совпадают)
  if (hasBottomConnection && bottomCapFitting && bottomFittingData) {
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    
    // Показываем размерную линию только если нужен переход/заглушка (есть вылет)
    if (capReq.needsCap) {
      const fittingLength = bottomCapFittingLength ?? (
        bottomCapFitting.startsWith('Flanec_') ? 100 :
        (bottomCapFitting.startsWith('Nak_Gaika_') || bottomCapFitting.startsWith('Shaib_Nar_Rezba_')) ? 40 :
        bottomFittingData.length
      );
      const bottomEdgeY = mainPipeTopY + actualMainPipeHeight;
      const capBottomY = bottomEdgeY;
      
      // Размерная линия на уровне общей размерной линии высоты
      // Если предкотловой включен кнопкой - по центру
      // Если отражение БЕЗ предкотлового - справа
      // Иначе - слева
      const isPreBoilerEnabledByButton = params.hasPreBoilerCollector;
      const isFlippedWithoutPreBoiler = isSystemFlipped && !params.hasPreBoilerCollector;
      const dimX = isPreBoilerEnabledByButton
        ? startX  // По центру (как было раньше)
        : isFlippedWithoutPreBoiler
          ? startX + mainPipeDiameter / 2 + 270  // Справа при отражении
          : startX - mainPipeDiameter / 2 - 270; // Слева
      
      // Для ниппелей/муфт: размерная линия показывает только длину фитинга (от начала до конца фитинга)
      const isNippel = bottomCapFitting.startsWith('Nippel_');
      const isMufta = bottomCapFitting.startsWith('Mufta_');
      
      let elements;
      if (isNippel || isMufta) {
        // Размерная линия от начала фитинга до конца фитинга
        let connectionStartY = bottomEdgeY;
        if (hasBottomCap) {
          if (bottomCapType === 'elliptical') {
            connectionStartY = bottomEdgeY + bottomEllipticalCapLength;
          } else if (bottomCapType === 'straight') {
            connectionStartY = bottomEdgeY + bottomStraightCapLength;
          }
        }
        
        const fittingActualLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
        
        elements = [
          // Верхняя точка - к началу фитинга (не к трубе!)
          { objectId: bottomFittingId, objectType: 'fitting', x: dimX, y: connectionStartY, type: 'bottom' },
          // Нижняя точка - к концу фитинга
          { objectId: bottomFittingId, objectType: 'fitting', x: dimX, y: connectionStartY + fittingActualLength, type: 'top' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimX,
          y1: connectionStartY,
          x2: dimX,
          y2: connectionStartY + fittingActualLength,
          position: { x: dimX, y: (connectionStartY + connectionStartY + fittingActualLength) / 2 },
          value: String(Math.round(fittingActualLength)),
          rawValue: fittingActualLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      } else {
        // Для других фитингов (фланцев/гаек) - размерная линия от заглушки до конца подключения
        const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
        const isFlange = bottomCapFitting.startsWith('Flanec_');
        
        const hasStraightCap = hasBottomCap && bottomCapType === 'straight';
        
        // Для фланцев с прямой заглушкой: размерная линия от заглушки
        // Для накидных гаек и шайб с резьбой: размерная линия от заглушки (прямой или эллиптической)
        const useCapAsReference = (isFlange && hasStraightCap && bottomStraightCapIdForDimLine) ||
                                   ((isNakGaika || isShaibaNarRezba) && (bottomStraightCapIdForDimLine || bottomEllipticalCapIdForDimLine));
        const capId = bottomStraightCapIdForDimLine || bottomEllipticalCapIdForDimLine;
        
        // Для прямой заглушки: начальная точка - от края заглушки
        let referenceY = capBottomY;
        if (hasStraightCap) {
          referenceY = capBottomY + bottomStraightCapLength;
        }
        
        elements = [
          // Верхняя точка - к заглушке (для фланцев/гаек/шайб с прямой заглушкой) или к основной трубе
          useCapAsReference
            ? { objectId: capId, objectType: 'fitting', x: dimX, y: referenceY, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capBottomY, type: 'bottom' },
          // Нижняя точка - к конечному объекту (бортшайба или промежуточная труба)
          // Для rotation: 180 (идет вниз) привязываемся к 'top' стороне объекта
          bottomFittingEndObjectId 
            ? { objectId: bottomFittingEndObjectId, objectType: bottomFittingEndObjectType, x: dimX, y: referenceY + fittingLength, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capBottomY + fittingLength, type: 'bottom' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0, // Вертикальная линия
          x1: dimX,
          y1: referenceY,
          x2: dimX,
          y2: referenceY + fittingLength,
          position: { x: dimX, y: (referenceY + referenceY + fittingLength) / 2 },
          value: String(Math.round(fittingLength)),
          rawValue: fittingLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      }
    }
    // Для фланцев/гаек/шайб впритык - не показываем размерную линию
  }

  // ========================================
  // 6. ПОЗИЦИИ (BALLOONS) ДЛЯ ФИТИНГОВ
  // ========================================

  // Balloons для левых подключений (размещаем СЛЕВА или СПРАВА при отражении)
  // Генерируются только если НЕ включен предкотловой коллектор
  if (!params.hasPreBoilerCollector) {
    leftConnections.forEach((conn, index) => {
      const fittingDataInfo = fittingsData[conn.fittingId];
      if (!fittingDataInfo) return;

      // Вычисляем реальную позицию
      let actualOffset;
      if (params.leftConnectionsPlacementMode === 'spacing') {
        // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
        actualOffset = params.leftConnectionsFirstOffset || 50;
        for (let i = 0; i < index; i++) {
          const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
          actualOffset += spacing;
        }
      } else {
        // Режим отступов: используем offsetFromTop и offsetDirection
        actualOffset = getConnectionPosition(
          conn.offsetFromTop,
          conn.offsetDirection,
          actualMainPipeHeight,
          topBortshaibaLengthForHeight,
          topStraightCapLength,
          bottomBortshaibaLengthForHeight,
          bottomStraightCapLength
        );
      }

      const label = getFittingLabel(conn.fittingId);
      const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength, params.shape);
      // Позиция подключения (с учётом верхней бортшайбы и прямой заглушки)
      const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
      const connectionX = isSystemFlipped 
        ? startX + mainPipeDiameter / 2  // При отражении - справа
        : startX - mainPipeDiameter / 2; // Обычно - слева
      
      // Внешний край фитинга
      const fittingEndX = isSystemFlipped
        ? connectionX + fittingLength  // При отражении - правый конец
        : connectionX - fittingLength; // Обычно - левый конец
      
      // Смещаем Y от середины разделителя
      const balloonY = connectionY < midSeparatorY 
        ? connectionY - 60  // Если выше середины - сдвигаем вверх
        : connectionY + 60; // Если ниже середины - сдвигаем вниз
      
      balloons.push({
        id: `balloon-left-${index}-${Date.now()}`,
        x: isSystemFlipped ? fittingEndX + 60 : fittingEndX - 60, // При отражении справа, иначе слева
        y: balloonY,
        text: label,
        targets: [{ type: 'fitting', id: leftFittingIds[index]?.id }],
      });
    });
  }
  
  // Позиция для верхнего фитинга (размещаем СВЕРХУ)
  if (hasTopConnection && topCapFitting && topFittingData2 && topFittingId) {
    const label = getFittingLabel(topCapFitting);
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    
    let fittingTopY;
    if (capReq.fitsFlush) {
      // Впритык - фитинг от верха гребенки
      if (topCapFitting.startsWith('Nippel_') || topCapFitting.startsWith('Mufta_')) {
        const fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
        fittingTopY = mainPipeTopY - fLength;
      } else {
        // Фланец/гайка/шайба впритык - от верха гребенки
        fittingTopY = mainPipeTopY - topFittingData2.length;
      }
    } else if (capReq.needsCap) {
      // С переходом/заглушкой - вылет от заглушки
      const fittingLength = endFittingAutoLength(topCapFitting, topCapFittingLength, getFittingLength(topCapFitting, null));
      const capTopY = topCapType === 'straight' 
        ? mainPipeTopY
        : mainPipeTopY - topEllipticalCapLength;
      fittingTopY = capTopY - fittingLength;
    } else {
      // Обычный фитинг с заглушкой
      const fLength = endFittingAutoLength(topCapFitting, topCapFittingLength, topFittingData2.length);
      const capTopY = topCapType === 'straight' 
        ? mainPipeTopY
        : mainPipeTopY - topEllipticalCapLength;
      fittingTopY = capTopY - fLength;
    }
    
    balloons.push({
      id: `balloon-top-${Date.now()}`,
      x: isSystemFlipped ? startX + 80 : startX, // При отражении смещаем вправо
      y: fittingTopY - 30,
      text: label,
      targets: [{ type: 'fitting', id: topFittingId }],
    });
  }

  // Позиция для нижнего фитинга (размещаем СНИЗУ)
  if (hasBottomConnection && bottomCapFitting && bottomFittingData && bottomFittingId) {
    const label = getFittingLabel(bottomCapFitting);
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const bottomMainPipeEdge = mainPipeTopY + actualMainPipeHeight;
    
    let fittingBottomY;
    if (capReq.fitsFlush) {
      // Впритык - фитинг от низа гребенки
      if (bottomCapFitting.startsWith('Nippel_') || bottomCapFitting.startsWith('Mufta_')) {
        const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
        fittingBottomY = bottomMainPipeEdge + fLength;
      } else {
        // Фланец/гайка/шайба впритык - от низа гребенки
        fittingBottomY = bottomMainPipeEdge + bottomFittingData.length;
      }
    } else if (capReq.needsCap) {
      // С переходом/заглушкой - вылет от заглушки
      const fittingLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, getFittingLength(bottomCapFitting, null));
      const capBottomY = bottomCapType === 'straight'
        ? bottomMainPipeEdge
        : bottomMainPipeEdge + bottomEllipticalCapLength;
      fittingBottomY = capBottomY + fittingLength;
    } else {
      // Обычный фитинг с заглушкой
      const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, bottomFittingData.length);
      const capBottomY = bottomCapType === 'straight'
        ? bottomMainPipeEdge
        : bottomMainPipeEdge + bottomEllipticalCapLength;
      fittingBottomY = capBottomY + fLength;
    }
    
    balloons.push({
      id: `balloon-bottom-${Date.now()}`,
      x: isSystemFlipped ? startX + 80 : startX, // При отражении смещаем вправо
      y: fittingBottomY + 30,
      text: label,
      targets: [{ type: 'fitting', id: bottomFittingId }],
    });
  }

  // Позиция для верхнего перехода (размещаем СВЕРХУ, выше фитинга если есть)
  if (topReducerId && topReducer && fittingsData[topReducer]) {
    const label = getFittingLabel(topReducer);
    const reducerData = fittingsData[topReducer];
    if (reducerData) {
      const reducerTopY = mainPipeTopY - reducerData.length;
      // Если есть фитинг сверху - переход чуть выше него
      const balloonY = topCapFitting && topFittingData2 ? reducerTopY - 120 : reducerTopY - 60;
      balloons.push({
        id: `balloon-top-reducer-${Date.now()}`,
        x: isSystemFlipped ? startX + 160 : startX - 80, // При отражении справа, иначе слева
        y: balloonY,
        text: label,
        targets: [{ type: 'fitting', id: topReducerId }],
      });
    }
  }

  // Позиция для нижнего перехода (размещаем СНИЗУ, ниже фитинга если есть)
  if (bottomReducerId && bottomReducer && fittingsData[bottomReducer]) {
    const label = getFittingLabel(bottomReducer);
    const reducerData = fittingsData[bottomReducer];
    if (reducerData) {
      const bottomMainPipeEdge = mainPipeTopY + actualMainPipeHeight;
      const reducerBottomY = bottomMainPipeEdge + reducerData.length;
      // Если есть фитинг снизу - переход чуть ниже него
      const balloonY = bottomCapFitting && bottomFittingData ? reducerBottomY + 120 : reducerBottomY + 60;
      balloons.push({
        id: `balloon-bottom-reducer-${Date.now()}`,
        x: isSystemFlipped ? startX + 160 : startX - 80, // При отражении справа, иначе слева
        y: balloonY,
        text: label,
        targets: [{ type: 'fitting', id: bottomReducerId }],
      });
    }
  }

  // ========================================
  // 7. ОБЩАЯ РАЗМЕРНАЯ ЛИНИЯ ВЫСОТЫ И МЕЖОСЕВОЕ
  // ========================================
  
  // Размерная линия для общей высоты
  // Учитываем бортшайбы и прямые заглушки, которые входят в общую высоту
  // totalHeightTopY, totalHeightBottomY, topDimElement и bottomDimElement
  // уже определены выше (после генерации левых подключений)

  // Позиция размерной линии общей высоты
  // Если предкотловой включен кнопкой - по центру (справа от трубы на 25)
  // Если отражение БЕЗ предкотлового - справа (на 240)
  // Иначе - слева
  const isPreBoilerEnabledByButton = params.hasPreBoilerCollector;
  const isFlippedWithoutPreBoiler = isSystemFlipped && !params.hasPreBoilerCollector;
  const heightDimX = isPreBoilerEnabledByButton
    ? startX - mainPipeDiameter / 2 + 25  // По центру (как было раньше)
    : isFlippedWithoutPreBoiler
      ? startX + mainPipeDiameter / 2 + 240  // Справа при отражении
      : startX - mainPipeDiameter / 2 - 240; // Слева

  dimensionLines.push({
    isHorizontal: false,
    lockOrientation: true,
    angle: 0, // Вертикальная линия
    x1: heightDimX,
    y1: totalHeightTopY,
    x2: heightDimX,
    y2: totalHeightBottomY,
    position: { x: heightDimX + (isPreBoilerEnabledByButton ? 30 : isFlippedWithoutPreBoiler ? 30 : -30), y: (totalHeightTopY + totalHeightBottomY) / 2 },
    value: String(Math.round(separatorHeight)),
    rawValue: separatorHeight,
    extensionLines: [],
    precision: 0,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [topDimElement, bottomDimElement],
  });
  
  // Межосевое расстояние между трубами подачи и обратки (слева, так как справа - гидроразделитель)
  // Фиксированное смещение влево от начала коллектора
  const interAxisDimX = collectorStartX - 40;
  
  // Вычисляем правый край всей конструкции коллектора для позиции текста
  // Используем фиксированную позицию для текста межосевой размерной линии
  const fixedTextPosition = collectorStartX + collectorLength + 150;

  dimensionLines.push({
    isHorizontal: false,
    lockOrientation: true,
    angle: 0,
    x1: interAxisDimX,
    y1: supplyCenterY,
    x2: interAxisDimX,
    y2: returnCenterY,
    position: { x: fixedTextPosition, y: (supplyCenterY + returnCenterY) / 2 }, // Фиксированная позиция текста
    value: String(Math.round(actualPipeSpacing)),
    rawValue: actualPipeSpacing,
    extensionLines: [],
    precision: 0,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: supplyPipeId, objectType: 'cylinder', x: interAxisDimX, y: supplyCenterY, type: 'right' },
      { objectId: returnPipeId, objectType: 'cylinder', x: interAxisDimX, y: returnCenterY, type: 'right' },
    ],
  });
  
  // ========================================
  // РАЗМЕРНЫЕ ЛИНИИ И ПОЗИЦИИ ДЛЯ КОЛЛЕКТОРА
  // ========================================
  
  // Размерные линии коллектора генерируются только если коллектор был сгенерирован
  if (collectorStartX !== undefined && supplyPipeId !== undefined) {
  
  // Используем стандартную функцию для генерации размерных линий коллектора
  const collectorDimensions = generateDistributionCollectorDimensions({
    startX: collectorStartX,
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeLength: actualSupplyCollectorLength,
    returnPipeLength: actualReturnCollectorLength,
    supplyCircuitObjects,
    returnCircuitObjects,
    supplyFirstCircuitOffset: actualSupplyFirstCircuitOffset,
    supplyLastCircuitOffset,
    returnFirstCircuitOffset: actualReturnFirstCircuitOffset,
    returnLastCircuitOffset,
    supplyCircuits,
    returnCircuits,
    dimOffset: 40,
    // Информация о правых торцевых подключениях (для размерных линий)
    supplyRightConnectionInfo,
    returnRightConnectionInfo,
    // ID заглушек/переходов (для размерных линий контуров)
    supplyRightCapId,
    returnRightCapId,
    // Длины прямых заглушек
    supplyRightStraightCapLength: supplyRightCapInfo.straightLength,
    returnRightStraightCapLength: returnRightCapInfo.straightLength,
    // Длины бортшайб (для размерных линий и отступов)
    supplyRightBortshaibaLength,
    returnRightBortshaibaLength,
    // Фактические позиции и длины гребёнок
    supplyPipeStartX: collectorStartX,
    returnPipeStartX: collectorStartX,
    actualSupplyPipeLength,
    actualReturnPipeLength,
    // Центральные фитинги (для размерных линий)
    supplyCenterFittingObjects,
    returnCenterFittingObjects,
    // Дополнительные выходы (для размерных линий)
    supplyAdditionalOutletObjects,
    returnAdditionalOutletObjects,
    supplyAdditionalOutlets,
    returnAdditionalOutlets,
    // Форма и профили (для квадратных коллекторов)
    shape: params.shape || 'round',
    supplyPipeProfile: params.supplyPipeProfile,
    returnPipeProfile: params.returnPipeProfile,
    supplyPipeSpec,
    returnPipeSpec,
    pipeSpacing,
  });
  
  // Фильтруем размерную линию межосевого расстояния (она слева, у нас уже есть справа)
  // Межосевая линия соединяет центры двух коллекторов и имеет 2 элемента - обе трубы
  // Используем уже вычисленные значения supplyCenterY и returnCenterY (строки 1717-1718)
  
  
  const filteredCollectorDimensions = collectorDimensions.filter(dim => {
    // Убираем межосевую линию между коллекторами — она уже нарисована слева отдельно
    // Определяем её по привязке к обеим трубам (supplyPipeId и returnPipeId)
    if (!dim.isHorizontal && dim.elements && dim.elements.length === 2 &&
        dim.elements.every(el => el.objectType === 'cylinder') &&
        dim.elements.some(el => el.objectId === supplyPipeId) &&
        dim.elements.some(el => el.objectId === returnPipeId)) {
      return false; // Убираем межосевую линию
    }
    return true; // Оставляем все остальные
  });
  
  // Сначала найдём минимальное Y среди всех размерных линий коллектора (самая верхняя точка)
  // чтобы установить общую размерную линию выше всех
  // Если коллектор не генерируется (при отражении без предкотлового), используем верх гидроразделителя
  let minCollectorDimY = supplyY !== undefined 
    ? supplyY - supplyPipeDiameter / 2 - 80 
    : mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength - 80; // Базовое значение
  
  filteredCollectorDimensions.forEach(dim => {
    // Пропускаем общую размерную линию длины коллектора (её мы будем перемещать)
    const isTotalLengthLine = dim.isHorizontal && 
        Math.abs(dim.x1 - collectorStartX) < 1 && 
        Math.abs(dim.x2 - (collectorStartX + collectorLength)) < 1;
    
    if (!isTotalLengthLine) {
      // Проверяем горизонтальные размерные линии (отступы, межосевые)
      if (dim.isHorizontal) {
        minCollectorDimY = Math.min(minCollectorDimY, dim.y1, dim.y2);
        if (dim.position && typeof dim.position.y === 'number') {
          minCollectorDimY = Math.min(minCollectorDimY, dim.position.y);
        }
      }
    }
  });
  
  // Устанавливаем общую размерную линию выше всех на 40мм
  totalLengthLineY = minCollectorDimY - 40;
  
  // Переносим размерную линию диаметра обратного коллектора вправо
  // И перемещаем общую размерную линию длины коллектора выше
  const adjustedCollectorDimensions = filteredCollectorDimensions.map(dim => {
    // Размерная линия диаметра: вертикальная, angle: 90, y1 = returnY, y2 = returnY + returnPipeDiameter
    if (!dim.isHorizontal && dim.angle === 90 &&
        dim.x1 === collectorStartX && dim.x2 === collectorStartX &&
        Math.abs(dim.y1 - returnY) < 1 &&
        Math.abs(dim.y2 - (returnY + returnPipeDiameter)) < 1) {
      // Перемещаем эту линию вправо (на правый край коллектора)
      const rightX = collectorStartX + collectorLength;
      return {
        ...dim,
        x1: rightX,
        x2: rightX,
        position: {
          ...dim.position,
          x: rightX + 100, // Увеличенный отступ справа
        },
        elements: dim.elements.map(el => ({
          ...el,
          x: rightX,
          type: el.type === 'left' ? 'right' : el.type === 'right' ? 'left' : el.type,
        })),
      };
    }
    
    // Общая размерная линия длины коллектора: горизонтальная, от начала до конца коллектора
    if (dim.isHorizontal && 
        Math.abs(dim.x1 - collectorStartX) < 1 && 
        Math.abs(dim.x2 - (collectorStartX + collectorLength)) < 1) {
      // Перемещаем эту линию выше всех остальных размерных линий коллектора
      return {
        ...dim,
        y1: totalLengthLineY,
        y2: totalLengthLineY,
        position: {
          ...dim.position,
          y: totalLengthLineY - 15,
        },
        elements: dim.elements.map(el => ({
          ...el,
          y: totalLengthLineY,
          // Привязываем к подающему коллектору
          objectId: el.objectId === returnPipeId ? supplyPipeId : el.objectId,
        })),
      };
    }
    
    return dim;
  });
  
  // Корректируем позицию размерной линии межосевого расстояния (200 мм)
  // Это вертикальная линия между центрами труб, расположенная на collectorStartX
  const finalCollectorDimensions = adjustedCollectorDimensions.map(dim => {
    // Находим вертикальную размерную линию межосевого расстояния
    if (!dim.isHorizontal && dim.angle === 90 &&
        dim.x1 === collectorStartX && dim.x2 === collectorStartX &&
        Math.abs(dim.y1 - supplyCenterY) < 1 &&
        Math.abs(dim.y2 - returnCenterY) < 1 &&
        dim.elements && dim.elements.length === 2 &&
        dim.elements.every(el => el.objectType === 'cylinder')) {
      // Устанавливаем фиксированную позицию для текста размерной линии
      return {
        ...dim,
        position: {
          ...dim.position,
          x: collectorStartX - 40, // Фиксированное смещение влево
        },
      };
    }
    return dim;
  });
  
  dimensionLines.push(...finalCollectorDimensions);

  // ========================================
  // ПОЗИЦИИ (BALLOONS) ДЛЯ КОНТУРОВ КОЛЛЕКТОРА
  // ========================================
  
  // Уровни для позиций
  const allSupplyObjects = supplyCircuitObjects.filter(obj => obj && obj.y != null && obj.height != null);
  const allReturnObjects = returnCircuitObjects.filter(obj => obj && obj.y != null && obj.height != null);
  
  const minSupplyTopY = allSupplyObjects.length > 0
    ? Math.min(...allSupplyObjects.map(c => c.y))
    : supplyY;
  const maxReturnBottomY = allReturnObjects.length > 0
    ? Math.max(...allReturnObjects.map(c => c.y + c.height))
    : returnY + returnPipeDiameter;
  
  const dimOffset = 40;
  const topDimY = minSupplyTopY - dimOffset * 2;
  const bottomDimY = maxReturnBottomY + dimOffset * 3;
  
  // Базовые Y позиции для позиций торцевых подключений (как в оригинале)
  const supplyBalloonY = supplyY - 30;
  const returnBalloonY = returnY + returnPipeDiameter + 30;
  
  // Balloons для подающего коллектора
  const processedSupplyIndices = new Set();
  
  supplyCircuitObjects.forEach((circuit, index) => {
    if (processedSupplyIndices.has(index)) return;
    
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting) return;
    
    // Определяем индекс контура и тип патрубка (короткий/длинный)
    const circuitIndex = Math.floor(index / 2);
    const isShortCircuit = circuit.isShort ?? (index % 2 === 0);
    const fittingKey = supplyCircuits[circuitIndex]?.fitting;
    const label = getFittingLabel(fittingKey);
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий фитинг
    const nextIndex = index + 1;
    const nextCircuit = supplyCircuitObjects[nextIndex];
    const nextCircuitIndex = Math.floor(nextIndex / 2);
    const nextFittingKey = supplyCircuits[nextCircuitIndex]?.fitting;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Для длинных патрубков используем Y координату короткого патрубка того же контура
    let balloonYBase;
    if (isShortCircuit) {
      balloonYBase = circuit.y; // Короткий - используем свой Y
    } else {
      // Длинный - используем Y короткого патрубка того же контура
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      balloonYBase = shortCircuit ? shortCircuit.y : circuit.y;
    }
    const balloonY = (topDimY + balloonYBase) / 2;
    
    if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedSupplyIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 0);
      
      // Получаем короткий патрубок для вычисления вылета
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height; // смещение от верха
      
      const nextShortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
      const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
      const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
      
      balloons.push({
        id: `balloon-supply-${index}-${Date.now()}`,
        x: midX,
        y: balloonY,
        text: label,
        targets: [
          { 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от верха
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          },
          { 
            type: 'fitting', 
            id: nextTargetId,
            ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
          },
        ],
      });
      
      processedSupplyIndices.add(index);
      processedSupplyIndices.add(nextIndex);
    } else {
      // Одиночный фитинг
      let posX;
      const prevIndex = index - 1;
      const prevCircuit = supplyCircuitObjects[prevIndex];
      
      if (prevCircuit) {
        posX = (prevCircuit.centerX + circuit.centerX) / 2;
      } else {
        const internalSpacing = Number(supplyCircuits[circuitIndex]?.circuitSpacing) || 125;
        posX = circuit.centerX - internalSpacing / 2;
      }
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height;
      
      balloons.push({
        id: `balloon-supply-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        targets: [{ 
          type: 'fitting', 
          id: targetId,
          // Для длинного: привязка со смещением (вылет/2) от верха
          ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
        }],
      });
      
      processedSupplyIndices.add(index);
    }
  });
  
  // Balloons для обратного коллектора
  const processedReturnIndices = new Set();
  
  returnCircuitObjects.forEach((circuit, index) => {
    if (processedReturnIndices.has(index)) return;
    
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting) return;
    
    // Определяем индекс контура и тип патрубка (короткий/длинный)
    const circuitIndex = Math.floor(index / 2);
    const isShortCircuit = circuit.isShort ?? (index % 2 === 1);
    const fittingKey = returnCircuits[circuitIndex]?.fitting;
    const label = getFittingLabel(fittingKey);
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий фитинг
    const nextIndex = index + 1;
    const nextCircuit = returnCircuitObjects[nextIndex];
    const nextCircuitIndex = Math.floor(nextIndex / 2);
    const nextFittingKey = returnCircuits[nextCircuitIndex]?.fitting;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Для длинных патрубков используем Y координату короткого патрубка того же контура
    let balloonYBase;
    if (isShortCircuit) {
      balloonYBase = circuit.y + circuit.height; // Короткий - используем свой низ
    } else {
      // Длинный - используем низ короткого патрубка того же контура
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      balloonYBase = shortCircuit ? (shortCircuit.y + shortCircuit.height) : (circuit.y + circuit.height);
    }
    const balloonY = (bottomDimY + balloonYBase) / 2;
    
    if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedReturnIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 1);
      
      // Получаем короткий патрубок для вычисления вылета
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height; // смещение от верха (визуально низа)
      
      const nextShortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
      const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
      const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
      
      balloons.push({
        id: `balloon-return-${index}-${Date.now()}`,
        x: midX,
        y: balloonY,
        text: label,
        targets: [
          { 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от верха (визуально внизу)
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          },
          { 
            type: 'fitting', 
            id: nextTargetId,
            ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
          },
        ],
      });
      
      processedReturnIndices.add(index);
      processedReturnIndices.add(nextIndex);
    } else {
      // Одиночный фитинг
      let posX;
      const prevIndex = index - 1;
      const prevCircuit = returnCircuitObjects[prevIndex];
      
      if (prevCircuit) {
        posX = (prevCircuit.centerX + circuit.centerX) / 2;
      } else {
        const internalSpacing = Number(returnCircuits[circuitIndex]?.circuitSpacing) || 125;
        posX = circuit.centerX - internalSpacing / 2;
      }
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height;
      
      balloons.push({
        id: `balloon-return-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        targets: [{ 
          type: 'fitting', 
          id: targetId,
          // Для длинного: привязка со смещением (вылет/2) от верха (визуально внизу)
          ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
        }],
      });
      
      processedReturnIndices.add(index);
    }
  });

  // ========================================
  // ПОЗИЦИИ ДЛЯ ПРАВЫХ ЗАГЛУШЕК/ПЕРЕХОДОВ КОЛЛЕКТОРОВ
  // ========================================
  
  const balloonYOffset = 30; // Смещение для позиций, если есть фитинг рядом
  
  // Правый переход/заглушка подающего коллектора
  if (hasSupplyRightCap && supplyRightCapElementType === 'reducer' && supplyRightCapId && supplyRightReducer) {
    const label = getFittingLabel(supplyRightReducer);
    const reducerEndX = collectorStartX + collectorLength + supplyRightCapInfo.actualLength;
    const reducerY = supplyRightConnectionInfo 
      ? supplyBalloonY - balloonYOffset 
      : supplyBalloonY;
    balloons.push({
      id: `balloon-supply-right-reducer-${Date.now()}`,
      x: reducerEndX + 25,
      y: reducerY,
      text: label,
      targets: [{ type: 'fitting', id: supplyRightCapId }],
    });
  }
  
  // Правый фитинг подающего коллектора
  if (supplyRightConnectionInfo && supplyRightConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(supplyRightConnectionInfo.fittingKey);
    const fittingEndX = supplyRightConnectionInfo.startX + supplyRightConnectionInfo.length;
    balloons.push({
      id: `balloon-supply-right-${Date.now()}`,
      x: fittingEndX + 100, // Увеличено с 60 до 100
      y: supplyBalloonY,
      text: label,
      targets: [{ type: 'fitting', id: supplyRightConnectionInfo.id }],
    });
  }
  
  // Правый переход/заглушка обратного коллектора
  if (hasReturnRightCap && returnRightCapElementType === 'reducer' && returnRightCapId && returnRightReducer) {
    const label = getFittingLabel(returnRightReducer);
    const reducerEndX = collectorStartX + collectorLength + returnRightCapInfo.actualLength;
    const reducerY = returnRightConnectionInfo 
      ? returnBalloonY + balloonYOffset 
      : returnBalloonY;
    balloons.push({
      id: `balloon-return-right-reducer-${Date.now()}`,
      x: reducerEndX + 25,
      y: reducerY,
      text: label,
      targets: [{ type: 'fitting', id: returnRightCapId }],
    });
  }
  
  // Правый фитинг обратного коллектора
  if (returnRightConnectionInfo && returnRightConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(returnRightConnectionInfo.fittingKey);
    const fittingEndX = returnRightConnectionInfo.startX + returnRightConnectionInfo.length;
    balloons.push({
      id: `balloon-return-right-${Date.now()}`,
      x: fittingEndX + 100, // Увеличено с 60 до 100
      y: returnBalloonY,
      text: label,
      targets: [{ type: 'fitting', id: returnRightConnectionInfo.id }],
    });
  }
  
  // ========================================
  // РАЗМЕРНЫЕ ЛИНИИ ДЛЯ ЦЕНТРАЛЬНЫХ ФИТИНГОВ
  // ========================================
  
  // Размерные линии для центральных фитингов подачи
  if (supplyCenterFittingObjects && supplyCenterFittingObjects.length > 0) {
    const supplyCenterDims = generateCenterFittingDimensions(
      supplyCenterFittingObjects,
      null, // Левой заглушки нет
      supplyRightCapId,
      collectorStartX,
      supplyCenterY,
      supplyPipeDiameter,
      actualSupplyCollectorLength,
      0, // leftStraightCapLength
      supplyRightCapInfo.straightLength,
      supplyPipeId,
      actualSupplyPipeLength,
      collectorStartX,
      null, // leftConnectionInfo - нет для основного коллектора
      supplyRightConnectionInfo,
      0, // leftBortshaibaLength - нет для основного коллектора
      supplyRightBortshaibaLength
    );
    
    // Размерные линии уже в правильном формате, просто добавляем их
    dimensionLines.push(...supplyCenterDims);
  }
  
  // Размерные линии для центральных фитингов обратки
  if (returnCenterFittingObjects && returnCenterFittingObjects.length > 0) {
    const returnCenterDims = generateCenterFittingDimensions(
      returnCenterFittingObjects,
      null, // Левой заглушки нет
      returnRightCapId,
      collectorStartX,
      returnCenterY,
      returnPipeDiameter,
      actualReturnCollectorLength,
      0, // leftStraightCapLength
      returnRightCapInfo.straightLength,
      returnPipeId,
      actualReturnPipeLength,
      collectorStartX,
      null, // leftConnectionInfo - нет для основного коллектора
      returnRightConnectionInfo,
      0, // leftBortshaibaLength - нет для основного коллектора
      returnRightBortshaibaLength
    );
    
    // Размерные линии уже в правильном формате, просто добавляем их
    dimensionLines.push(...returnCenterDims);
  }
  
  // ========================================
  // BALLOONS ДЛЯ ЦЕНТРАЛЬНЫХ ФИТИНГОВ И ДОПОЛНИТЕЛЬНЫХ ВЫХОДОВ
  // ========================================
  
  // Balloons для центральных фитингов подачи
  if (supplyCenterFittingObjects && supplyCenterFittingObjects.length > 0) {
    const supplyCenterBalloons = generateCenterFittingBalloons(
      supplyCenterFittingObjects,
      getFittingLabel,
      supplyCenterY,
      0,
      'supply-'
    );
    balloons.push(...supplyCenterBalloons);
  }
  
  // Balloons для центральных фитингов обратки
  if (returnCenterFittingObjects && returnCenterFittingObjects.length > 0) {
    const returnCenterBalloons = generateCenterFittingBalloons(
      returnCenterFittingObjects,
      getFittingLabel,
      returnCenterY,
      0,
      'return-'
    );
    balloons.push(...returnCenterBalloons);
  }
  
  // Balloons для дополнительных выходов подачи
  if (supplyAdditionalOutletObjects && supplyAdditionalOutletObjects.length > 0) {
    const processedSupplyOutletIndices = new Set();
    
    supplyAdditionalOutletObjects.forEach((outlet, index) => {
      if (processedSupplyOutletIndices.has(index)) return;
      
      const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
      if (!isFitting) return;
      
      const outletData = supplyAdditionalOutlets[index];
      const fittingKey = outletData?.fittingId;
      const label = getFittingLabel(fittingKey);
      const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
      
      const direction = outletData?.direction || 'up';
      const outletType = outletData?.outletType || 'short';
      const isThrough = outletType === 'through';
      
      // Получаем базовую длину фитинга для расчета anchorYOffset
      let baseLength = 80; // Значение по умолчанию
      if (isThrough) {
        const isFlange = fittingKey?.startsWith('Flanec_');
        const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
        const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
        
        if (isSpecialFitting) {
          baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
        } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
          baseLength = Number(outletData.fittingLength);
        } else if (fittingKey) {
          const fittingInfo = fittingsData[fittingKey];
          if (fittingInfo) {
            baseLength = fittingInfo.length;
          }
        } else if (outletData.length) {
          baseLength = Number(outletData.length);
        }
      }
      
      let balloonY;
      if (direction === 'up') {
        balloonY = outlet.y - 60;
      } else {
        balloonY = outlet.y + outlet.height * 0.7;
      }
      
      // Каждый дополнительный выход имеет свой баллон
      const posX = outlet.centerX + 40;
      const targets = [{ type: 'fitting', id: targetId }];
      
      if (isThrough) {
        const anchorYOffset = baseLength / 2 / outlet.height;
        targets[0].anchorNormalized = { x: 0.5, y: anchorYOffset };
        targets[0].anchorMode = 'local';
      }
      
      balloons.push({
        id: `balloon-supply-additional-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        targets: targets,
      });
      
      processedSupplyOutletIndices.add(index);
    });
  }
  
  // Balloons для дополнительных выходов обратки
  if (returnAdditionalOutletObjects && returnAdditionalOutletObjects.length > 0) {
    const processedReturnOutletIndices = new Set();
    
    returnAdditionalOutletObjects.forEach((outlet, index) => {
      if (processedReturnOutletIndices.has(index)) return;
      
      const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
      if (!isFitting) return;
      
      const outletData = returnAdditionalOutlets[index];
      const fittingKey = outletData?.fittingId;
      const label = getFittingLabel(fittingKey);
      const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
      
      const direction = outletData?.direction || 'down';
      const outletType = outletData?.outletType || 'short';
      const isThrough = outletType === 'through';
      
      // Получаем базовую длину фитинга для расчета anchorYOffset
      let baseLength = 80;
      if (isThrough) {
        const isFlange = fittingKey?.startsWith('Flanec_');
        const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
        const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
        
        if (isSpecialFitting) {
          baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
        } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
          baseLength = Number(outletData.fittingLength);
        } else if (fittingKey) {
          const fittingInfo = fittingsData[fittingKey];
          if (fittingInfo) {
            baseLength = fittingInfo.length;
          }
        } else if (outletData.length) {
          baseLength = Number(outletData.length);
        }
      }
      
      let balloonY;
      if (direction === 'down') {
        balloonY = outlet.y + outlet.height + 60;
      } else {
        balloonY = outlet.y + outlet.height * 0.3;
      }
      
      // Каждый дополнительный выход имеет свой баллон
      const posX = outlet.centerX + 40;
      const targets = [{ type: 'fitting', id: targetId }];
      
      if (isThrough) {
        const anchorYOffset = baseLength / 2 / outlet.height;
        targets[0].anchorNormalized = { x: 0.5, y: anchorYOffset };
        targets[0].anchorMode = 'local';
      }
      
      balloons.push({
        id: `balloon-return-additional-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        targets: targets,
      });
      
      processedReturnOutletIndices.add(index);
    });
  }
  
  } // Конец условия: размерные линии коллектора

  // ========================================
  // РАЗМЕРНАЯ ЛИНИЯ ДИАМЕТРА ГИДРОРАЗДЕЛИТЕЛЯ
  // ========================================
  
  // Добавляем горизонтальную размерную линию диаметра гидроразделителя на уровне общей линии длины коллектора
  // Если totalLengthLineY определена (есть коллектор), используем её, иначе размещаем выше гидроразделителя
  const separatorDiameterY = (typeof totalLengthLineY !== 'undefined')
    ? totalLengthLineY  // На том же уровне что и общая линия длины коллектора
    : mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength - 80; // Выше гидроразделителя
  
  const separatorCenterX = startX;
  const separatorLeftX = startX - mainPipeDiameter / 2;
  const separatorRightX = startX + mainPipeDiameter / 2;
  
  const separatorDiameterLine = {
    isHorizontal: true, // Горизонтальная линия
    lockOrientation: true,
    angle: 0, // Без поворота
    x1: separatorLeftX,
    y1: separatorDiameterY,
    x2: separatorRightX,
    y2: separatorDiameterY,
    position: { x: separatorCenterX, y: separatorDiameterY - 15 },
    value: String(Math.round(mainPipeDiameter)),
    rawValue: mainPipeDiameter,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: mainPipeId, objectType: 'cylinder', x: separatorLeftX, y: separatorDiameterY, type: 'left' },
      { objectId: mainPipeId, objectType: 'cylinder', x: separatorRightX, y: separatorDiameterY, type: 'right' },
    ],
  };
  
  // Для квадратных труб добавляем измененное значение (профиль вместо размера)
  if (params.shape === 'square' && params.mainPipeProfile) {
    const profileThickness = mainPipeSpec?.thickness || '2';
    separatorDiameterLine.overrideEnabled = true;
    separatorDiameterLine.overrideText = `${params.mainPipeProfile.replace('x', '×')} ×${profileThickness}`; // "80×80 ×2"
  } else if (mainPipeSpec) {
    // Для круглых труб добавляем толщину к диаметру
    const pipeThickness = (mainPipeSpec.thickness || 'x2').replace('x', ''); // "x2" → "2"
    separatorDiameterLine.overrideEnabled = true;
    separatorDiameterLine.overrideText = `${mainPipeDiameter} ×${pipeThickness}`; // "60.3 ×2"
  }
  
  dimensionLines.push(separatorDiameterLine);

  // ========================================
  // ПРИМЕНЕНИЕ ИЗМЕНЕННЫХ ЗНАЧЕНИЙ К РАЗМЕРНЫМ ЛИНИЯМ
  // ========================================
  const fittingsWithOverride = fittings.filter(f => f.overrideEnabled && f.overrideText);
  
  fittingsWithOverride.forEach((fitting) => {
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что линия относится к этому фитингу
      const hasFitting = dimLine.elements.some(el => el.objectId === fitting.id);
      const hasBortshaiba = dimLine.elements.some(el => el.objectId === fitting.bortshaibaId);
      
      if (!hasFitting && !hasBortshaiba) return;
      
      // Определяем тип фитинга по rotation и ID:
      // rotation = 0 -> торцевое подключение сверху (вертикальный) или контур коллектора вверх
      // rotation = 180 -> торцевое подключение снизу (вертикальный) или контур коллектора вниз
      // rotation = -90 или 90 -> левое/правое подключение (горизонтальный)
      const isTopBottomConnection = (fitting.rotation === 0 || fitting.rotation === 180) && 
                                     (fitting.id.includes('fitting-top-') || fitting.id.includes('fitting-bottom-'));
      const isLeftRightConnection = fitting.rotation === -90 || fitting.rotation === 90;
      const isCircuitFitting = fitting.id.includes('fitting-circuit-');
      
      // Для вертикальных линий (торцевые подключения сверху/снизу гидроразделителя)
      if (!dimLine.isHorizontal && isTopBottomConnection) {
        const isNakGaika = fitting.key?.startsWith('Nak_Gaika_');
        const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
        const isMufta = fitting.key?.startsWith('Mufta_');
        
        let shouldApplyOverride = false;
        
        if (isNakGaika || isShaiba) {
          // Для накидных гаек и шайб с резьбой проверяем значение 40 (дефолтный вылет)
          // Также проверяем если это размерная линия от заглушки до фитинга (hasBortshaiba)
          if (dimLine.rawValue === 40 || dimLine.value === '40' || hasBortshaiba) {
            shouldApplyOverride = true;
          }
        } else if (isMufta) {
          // Для муфт проверяем точное соответствие высоте фитинга
          if (dimLine.rawValue === fitting.height || dimLine.value === String(fitting.height)) {
            shouldApplyOverride = true;
          }
        }
        
        if (shouldApplyOverride) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
      // Для горизонтальных линий (левые/правые подключения гидроразделителя)
      else if (dimLine.isHorizontal && isLeftRightConnection) {
        const isNakGaika = fitting.key?.startsWith('Nak_Gaika_');
        const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
        const isMufta = fitting.key?.startsWith('Mufta_');
        const isNippel = fitting.key?.startsWith('Nippel_');
        
        let shouldApplyOverride = false;
        if (isNakGaika && hasBortshaiba) {
          shouldApplyOverride = true;
        } else if (isShaiba && hasFitting) {
          shouldApplyOverride = true;
        } else if ((isMufta || isNippel) && hasFitting) {
          shouldApplyOverride = dimLine.rawValue === fitting.height;
        }
        
        if (shouldApplyOverride) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
      // Для вертикальных линий контуров коллекторов
      else if (!dimLine.isHorizontal && isCircuitFitting) {
        const isNakGaika = fitting.key?.startsWith('Nak_Gaika_') || fitting.key?.startsWith('Shaib_Gaika_');
        const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
        const isMufta = fitting.key?.startsWith('Mufta_');
        const isNippel = fitting.key?.startsWith('Nippel_');
        
        let shouldApplyOverride = false;
        
        if (isNakGaika && (hasBortshaiba || hasFitting)) {
          // Для накидных гаек с бортшайбой или самой гайкой
          shouldApplyOverride = true;
        } else if (isShaiba && hasFitting) {
          // Для шайб с резьбой
          shouldApplyOverride = true;
        } else if ((isMufta || isNippel) && hasFitting) {
          // Для муфт и ниппелей проверяем точное соответствие высоте фитинга
          shouldApplyOverride = dimLine.rawValue === fitting.height;
        }
        
        if (shouldApplyOverride) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
    });
  });

  // Фильтруем валидные объекты
  const validCylinders = cylinders.filter(cyl => {
    if (isNaN(cyl.y) || isNaN(cyl.x)) {
      console.error('Cylinder with NaN coordinates filtered out:', cyl);
      return false;
    }
    return true;
  });
  
  const validFittings = fittings.filter(fit => {
    if (isNaN(fit.groupY) || isNaN(fit.groupX)) {
      console.error('Fitting with NaN coordinates filtered out:', fit);
      return false;
    }
    return true;
  });
  
  // IGES: Создаём "снимок" координат сразу после генерации
  // Этот снимок будет использоваться для экспорта вне зависимости от перемещений на чертеже
  const igesSnapshot = {
    cylinders: validCylinders.map(cyl => ({
      id: cyl.id,
      x: cyl.x,  // Текущие координаты при генерации
      y: cyl.y,
      width: cyl.width,
      height: cyl.height,
      rotation: cyl.rotation,
    })),
    fittings: validFittings.map(fit => ({
      id: fit.id,
      groupX: fit.groupX,  // Текущие координаты при генерации
      groupY: fit.groupY,
      diameter: fit.diameter,
      igesParentCylinderId: fit.igesParentCylinderId,
      igesCreateHole: fit.igesCreateHole,
      holeDiameter: fit.holeDiameter,
      igesHoleDirection: fit.igesHoleDirection,
      isLongCircuit: fit.isLongCircuit,
      pairedCylinderId: fit.pairedCylinderId,
    })),
  };

  const result = {
    cylinders: validCylinders,
    fittings: validFittings,
    dimensionLines,
    welds,
    balloons: balloons.filter(bal => {
      if (isNaN(bal.y) || isNaN(bal.x)) {
        console.error('Balloon with NaN coordinates filtered out:', bal);
        return false;
      }
      return true;
    }),
    metadata: {
      coating: coating, // Добавляем покрытие для аннотации
      // IGES: Снимок координат для экспорта (не меняется при перемещении чертежа)
      igesSnapshot: igesSnapshot,
      // Параметры контуров для IGES экспорта (распределительный коллектор)
      supplyCircuitParams: {
        pipeId: supplyPipeId,
        pairedCylinderId: returnPipeId, // IGES: ID парной трубы для длинных патрубков
        circuitPositions: supplyCircuitPositions,
      },
      returnCircuitParams: {
        pipeId: returnPipeId,
        pairedCylinderId: supplyPipeId, // IGES: ID парной трубы для длинных патрубков
        circuitPositions: returnCircuitPositions,
      },
      // Параметры контуров для IGES экспорта (предкотловой коллектор)
      preBoilerSupplyCircuitParams: hasPreBoilerCollector ? {
        pipeId: preBoilerSupplyPipeId,
        pairedCylinderId: preBoilerReturnPipeId,
        circuitPositions: preBoilerSupplyCircuitPositions,
      } : undefined,
      preBoilerReturnCircuitParams: hasPreBoilerCollector ? {
        pipeId: preBoilerReturnPipeId,
        pairedCylinderId: preBoilerSupplyPipeId,
        circuitPositions: preBoilerReturnCircuitPositions,
      } : undefined,
    },
  };
  
  return result;
}
